import React, { useMemo, useEffect, useState, useRef } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import "./AdminDashboard.css";
import * as XLSX from "xlsx";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  limit,
  updateDoc,
  writeBatch,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, listAll, getDownloadURL, deleteObject, getMetadata } from "firebase/storage";
import { db, storage } from "../../firebase";
import DOMPurify from "dompurify"; //html enter for blog posts
import { IoChevronDown, IoDesktopOutline, IoPhonePortraitOutline, IoTabletPortraitOutline } from "react-icons/io5";
import PlumaAnalyticsPanel from "./PlumaAnalyticsPanel";
import AnalyticsInsightsChat from "./AnalyticsInsightsChat";
import AnalyticsHeatmap from "./AnalyticsHeatmap";

import { exportAnalyticsPdf, exportAnalyticsSlide } from "./AnalyticsExportReport";

const getProductViewDocId = (productSlug, dateKey) => `${productSlug}_${dateKey}`;

const COUNTRY_DISPLAY_NAMES =
  typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["en"], { type: "region" }) : null;

const getCountryFlag = (value = "") => {
  const code = String(value || "")
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) return "🌐";

  return String.fromCodePoint(...[...code].map((letter) => 127397 + letter.charCodeAt()));
};

const getCountryName = (code = "", fallback = "") => {
  const normalizedCode = String(code || "")
    .trim()
    .toUpperCase();

  const fallbackName = String(fallback || "").trim();

  if (fallbackName && fallbackName.toUpperCase() !== normalizedCode) {
    return fallbackName;
  }

  if (!/^[A-Z]{2}$/.test(normalizedCode)) {
    return fallbackName || normalizedCode;
  }

  try {
    return COUNTRY_DISPLAY_NAMES?.of(normalizedCode) || normalizedCode;
  } catch {
    return normalizedCode;
  }
};

const formatSeconds = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);

  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

const formatKnowledgeCenterName = (fileName = "") =>
  fileName
    .replace(/\.[^/.]+$/, "")
    .replace(/-/g, " ")
    .replace(/_/g, " ");

const getKnowledgeCenterTypeFromFolder = (folder = "") => {
  if (folder === "brochures") return "brochure";
  if (folder === "catalogs") return "catalog";
  if (folder === "whitepapers") return "whitepaper";
  return "document";
};

const getAdminDisplayName = (email = "") => {
  if (!email) return "Admin";

  const firstPart = email.split("@")[0] || "";

  return firstPart
    .replace(/[._-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const splitMulti = (val) =>
  typeof val === "string"
    ? val
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

const splitMultiSemiColon = (val) =>
  typeof val === "string"
    ? val
        .split(";")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");

const getDocumentMetadataId = (storagePath) => encodeURIComponent(storagePath).replace(/\./g, "%2E");

const buildFallbackTitle = (fileName = "") =>
  fileName
    .replace(/\.[^/.]+$/, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const makeBlogSlug = (title = "") =>
  title
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const sanitizeBlogHtml = (html = "") =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "h2",
      "h3",
      "h4",
      "ul",
      "ol",
      "li",
      "blockquote",
      "a",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "colspan", "rowspan"],
  });

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncateText = (text = "", max = 100) => (text.length > max ? `${text.slice(0, max).trim()}...` : text);

const makeKnowledgeCenterDocSlug = (fileNameOrPath = "") => {
  const fileName = fileNameOrPath.split("/").pop() || fileNameOrPath;

  return fileName
    .replace(/\.[^/.]+$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const getProductImage = (p) => {
  const candidates = [p.image, p.cardImage, p.featuredImage, p.image1, p.imageUrl, p.image2, p.image3].filter(Boolean);

  return candidates[0] || "";
};

const ANALYTICS_RANGE_OPTIONS = [
  { key: "today", label: "Today", days: 1 },
  { key: "last7", label: "Last 7 Days", days: 7 },
  { key: "last30", label: "Last 30 Days", days: 30 },
  { key: "last90", label: "Last 90 Days", days: 90 },
];

const ANALYTICS_TAB_OPTIONS = [
  {
    key: "overview",
    label: "Overview",
    slideTitle: "Analytics Overview",
  },
  {
    key: "pages",
    label: "Pages",
    slideTitle: "Page Analytics",
  },
  {
    key: "products",
    label: "Products",
    slideTitle: "Product Analytics",
  },
  {
    key: "acquisition",
    label: "Acquisition",
    slideTitle: "Acquisition Analytics",
  },
  {
    key: "engagement",
    label: "Engagement",
    slideTitle: "Engagement Analytics",
  },
  {
    key: "exits",
    label: "Exits",
    slideTitle: "Exit Analytics",
  },
  {
    key: "funnel",
    label: "Funnel",
    slideTitle: "Conversion Funnel Analytics",
  },
  {
    key: "journeys",
    label: "Customer Journeys",
    slideTitle: "Customer Journey Analytics",
  },
  {
    key: "tools",
    label: "Tools",
    slideTitle: "Website Tools Analytics",
  },
  {
    key: "search",
    label: "Search & Pluma",
    slideTitle: "Search & Pluma Analytics",
  },
  {
    key: "insights",
    label: "Insights",
    slideTitle: "Insights",
  },
  {
    key: "alerts",
    label: "Email Alerts",
    slideTitle: "Email Alerts",
  },
  {
    key: "exclusions",
    label: "Exclusions",
    slideTitle: "Exclusions",
  },
];

const ANALYTICS_SLIDE_TAB_KEYS = new Set([
  "overview",
  "pages",
  "products",
  "acquisition",
  "engagement",
  "exits",
  "funnel",
  "journeys",
  "tools",
  "search",
]);

const ANALYTICS_EXPORT_EVENT = "gofoton:analytics-export";

const ANALYTICS_EXCLUSION_EVENT = "gofoton:analytics-exclusion-changed";

const PRODUCT_HEATMAP_KEY = "__all_product_pages__";

const JOURNEY_HIDDEN_EVENT_TYPES = new Set(["active_time", "scroll_depth", "heatmap_click", "hotspot_click"]);

const ANALYTICS_VISITOR_KEY = "gf_analytics_visitor_id";

const LEGACY_ANALYTICS_EXCLUDED_KEY = "gf_analytics_excluded";

const BROWSER_EXCLUSION_MIGRATION_KEY = "gf_browser_exclusion_migrated_v2";

const makePersistentBrowserId = () => {
  if (typeof window === "undefined") {
    return "";
  }

  let browserId = localStorage.getItem(ANALYTICS_VISITOR_KEY);

  if (!browserId) {
    const random = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    browserId = `visitor_${random}`;

    localStorage.setItem(ANALYTICS_VISITOR_KEY, browserId);
  }

  return browserId;
};

const getBrowserName = () => {
  if (typeof navigator === "undefined") {
    return "Browser";
  }

  const ua = navigator.userAgent || "";

  const platform = navigator.userAgentData?.platform || navigator.platform || "Device";

  let browser = "Browser";

  if (/Edg\//i.test(ua)) {
    browser = "Microsoft Edge";
  } else if (/Chrome\//i.test(ua)) {
    browser = "Google Chrome";
  } else if (/Firefox\//i.test(ua)) {
    browser = "Firefox";
  } else if (/Safari\//i.test(ua)) {
    browser = "Safari";
  }

  return `${browser} on ${platform}`;
};

const formatBrowserId = (browserId = "") =>
  String(browserId || "")
    .replace(/^visitor_/, "")
    .slice(0, 10)
    .toUpperCase() || "UNKNOWN";

function AdminDashboard() {
  const tabs = useMemo(
    () => [
      { key: "productUpdates", label: "Product Updates" },
      { key: "operations", label: "Operations" },
      { key: "documents", label: "Documents" },
      { key: "analytics", label: "Analytics" },
      { key: "tradeshows", label: "Tradeshows" },
      { key: "blogs", label: "Blogs & Articles" },
      { key: "settings", label: "Settings" },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState("productUpdates");

  const [loggingOut, setLoggingOut] = useState(false);

  const [adminDisplayName, setAdminDisplayName] = useState("Admin");

  const [analyticsRange, setAnalyticsRange] = useState("last90");

  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState("overview");

  const [showAnalyticsExportMenu, setShowAnalyticsExportMenu] = useState(false);

  const analyticsExportMenuRef = useRef(null);

  const selectedAnalyticsRange =
    ANALYTICS_RANGE_OPTIONS.find((item) => item.key === analyticsRange) || ANALYTICS_RANGE_OPTIONS[3];

  const activeAnalyticsMeta =
    ANALYTICS_TAB_OPTIONS.find((item) => item.key === activeAnalyticsTab) || ANALYTICS_TAB_OPTIONS[0];

  const slideExportAvailable = ANALYTICS_SLIDE_TAB_KEYS.has(activeAnalyticsTab);

  const handleAnalyticsExport = (format) => {
    if (format === "slide" && !slideExportAvailable) {
      return;
    }

    setShowAnalyticsExportMenu(false);

    window.dispatchEvent(
      new CustomEvent(ANALYTICS_EXPORT_EVENT, {
        detail: {
          format,

          activeTab: activeAnalyticsTab,

          rangeKey: analyticsRange,

          rangeLabel: selectedAnalyticsRange.label,
        },
      })
    );
  };

  useEffect(() => {
    if (!showAnalyticsExportMenu) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (analyticsExportMenuRef.current && !analyticsExportMenuRef.current.contains(event.target)) {
        setShowAnalyticsExportMenu(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setShowAnalyticsExportMenu(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);

    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);

      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showAnalyticsExportMenu]);

  useEffect(() => {
    setShowAnalyticsExportMenu(false);
  }, [activeAnalyticsTab, analyticsRange, activeTab]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAdminDisplayName(getAdminDisplayName(user?.email || ""));
    });

    return () => unsub();
  }, []);

  const activeLabel = tabs.find((t) => t.key === activeTab)?.label || "Dashboard";

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await signOut(auth);
      // RequireAuth will redirect away
    } catch (e) {
      console.error("Logout failed", e);
      alert("Logout failed. Try again.");
      setLoggingOut(false);
    }
  };

  // ✅ Central render switch: edit each tab in one place
  const renderTab = () => {
    switch (activeTab) {
      case "productUpdates":
        return <ProductUpdatesTab />;
      case "operations":
        return <OperationsTab />;
      case "documents":
        return <DocumentsTab />;
      case "analytics":
        return (
          <AnalyticsTab
            activeRange={analyticsRange}

            onLoadingChange={setAnalyticsLoading}

            activeAnalyticsTab={activeAnalyticsTab}

            setActiveAnalyticsTab={setActiveAnalyticsTab}
          />
        );
      case "tradeshows":
        return <TradeshowsTab />;
      case "blogs":
        return <BlogsTab />;
      case "settings":
        return <SettingsTab onLogout={handleLogout} loggingOut={loggingOut} />;
      default:
        return (
          <div className="admin-card">
            <h2 className="admin-h2">Unknown tab</h2>
          </div>
        );
    }
  };

  return (
    <div className="admin-shell">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <div className="admin-badge">{adminDisplayName.charAt(0) || "A"}</div>

          <div className="admin-brandText">
            <div className="admin-title">{adminDisplayName}</div>
            <div className="admin-subtitle">Dashboard</div>
          </div>
        </div>

        <nav className="admin-nav">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`admin-navItem ${activeTab === t.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="admin-sidebarFooter">
          <button type="button" className="admin-logout" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? "Signing out..." : "Sign out"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        <div className="admin-topbar">
          <div className="admin-heading-with-status">
            <h1 className="admin-h1">{activeLabel}</h1>

            {activeTab === "analytics" && (
              <span className={`analytics-header-loader ${analyticsLoading ? "is-loading" : ""}`} aria-hidden="true">
                <i />
              </span>
            )}
          </div>

          {activeTab === "analytics" && (
            <div className="analytics-topbar-controls">
              <div className="analytics-range-group">
                {ANALYTICS_RANGE_OPTIONS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`analytics-range-button ${analyticsRange === item.key ? "active" : ""}`}
                    onClick={() => setAnalyticsRange(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="analytics-export-wrap" ref={analyticsExportMenuRef}>
                <button
                  type="button"
                  className="analytics-export-button"

                  aria-expanded={showAnalyticsExportMenu}

                  onClick={() => setShowAnalyticsExportMenu((previous) => !previous)}
                >
                  Export Report
                  <IoChevronDown className="analytics-export-chevron" />
                </button>

                {showAnalyticsExportMenu && (
                  <div className="analytics-export-menu">
                    <button
                      type="button"

                      onClick={() => handleAnalyticsExport("pdf")}
                    >
                      <strong>Executive Analytics PDF</strong>

                      <span>Formal portrait report with summary, charts, and top-performing tables</span>
                    </button>

                    <button
                      type="button"

                      disabled={!slideExportAvailable}

                      title={
                        slideExportAvailable
                          ? `Export ${activeAnalyticsMeta.label} as an editable PowerPoint slide`
                          : "This administrative tab does not have a presentation slide."
                      }

                      onClick={() => handleAnalyticsExport("slide")}
                    >
                      <strong>
                        {slideExportAvailable
                          ? `${activeAnalyticsMeta.label} Slide Image`
                          : "Slide unavailable for this tab"}
                      </strong>

                      <span>
                        {slideExportAvailable
                          ? `${activeAnalyticsMeta.slideTitle} · 1920 × 1080 JPG`
                          : "Insights, Email Alerts, and Browser Exclusions do not export slides"}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <section className="admin-content">{renderTab()}</section>
      </main>
    </div>
  );
}

export default AdminDashboard;

/* ===========================
   Tab Components (edit these)
   =========================== */

const cleanExcelValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeOrderRow = ({ row, status, clientId, sourceRow }) => {
  return {
    clientId,
    status,

    poNumber: cleanExcelValue(row["PO Number"]),
    mpn: cleanExcelValue(row["MPN"]),
    customerPartNumber: cleanExcelValue(row["CustomerPartNumber"]),
    lineNumber: cleanExcelValue(row["LineNumber"]),
    quantity: cleanExcelValue(row["Quantity"]),

    shipDate: cleanExcelValue(row["ShipDate"]),
    expectedReceiptDate: cleanExcelValue(row["ExpectedReceiptDate"]),

    carrier: cleanExcelValue(row["Carrier"]),
    trackingNumber: cleanExcelValue(row["TrackingNumber"]),

    salesOrderNumber: cleanExcelValue(row["SalesOrderNumber"]),

    sourceRow,
  };
};

const isValidOrderRow = (row) => {
  return Boolean(row.poNumber || row.salesOrderNumber || row.trackingNumber || row.mpn);
};

const deleteExistingClientOrders = async (clientId) => {
  const existingQuery = query(collection(db, "portalOrders"), where("clientId", "==", clientId));

  const snapshot = await getDocs(existingQuery);

  const docs = snapshot.docs;

  const batchSize = 400;

  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = writeBatch(db);

    docs.slice(i, i + batchSize).forEach((docSnap) => {
      batch.delete(docSnap.ref);
    });

    await batch.commit();
  }

  return docs.length;
};

const writePortalOrders = async (orders) => {
  const batchSize = 400;

  for (let i = 0; i < orders.length; i += batchSize) {
    const batch = writeBatch(db);

    orders.slice(i, i + batchSize).forEach((order, index) => {
      const globalIndex = i + index;

      const id = [order.clientId, order.status, globalIndex].join("_");

      const orderRef = doc(db, "portalOrders", id);

      batch.set(orderRef, {
        ...order,
        uploadedAt: serverTimestamp(),
      });
    });

    await batch.commit();
  }
};

const uploadOrderCSV = async ({ clientId, status, csvText }) => {
  const fileName = status === "open" ? "open-orders.csv" : "shipped-orders.csv";

  const csvRef = ref(storage, `portalOrders/${clientId}/${fileName}`);

  const blob = new Blob([csvText], {
    type: "text/csv;charset=utf-8;",
  });

  await uploadBytes(csvRef, blob, {
    contentType: "text/csv",
  });

  return await getDownloadURL(csvRef);
};

function OperationsTab() {
  /* =====================================================
     MASTER PARTS LIST
     ===================================================== */

  const fileInputRef = useRef(null);

  const [password, setPassword] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const FUNCTION_URL = "https://us-central1-gofoton-55796.cloudfunctions.net/uploadVerizonMasterWorkbook";

  /* =====================================================
     ORDER STATUS WORKBOOK
     ===================================================== */

  const orderFileInputRef = useRef(null);

  const [orderFile, setOrderFile] = useState(null);
  const [uploadingOrders, setUploadingOrders] = useState(false);
  const [orderUploadResult, setOrderUploadResult] = useState(null);
  const [orderError, setOrderError] = useState("");

  const CLIENT_ID = "verizon";

  /* =====================================================
     MASTER PARTS LIST UPLOAD
     ===================================================== */

  const handleSubmit = async () => {
    if (password !== "gfa") {
      setErrorMsg("Incorrect password.");
      return;
    }

    if (!selectedFile) {
      setErrorMsg("Choose an Excel file first.");
      return;
    }

    setUploading(true);
    setErrorMsg("");
    setResult(null);

    try {
      const formData = new FormData();

      formData.append("password", password);

      formData.append("file", selectedFile);

      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Upload failed.");
      }

      setResult(data);
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error(error);

      setErrorMsg(error.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  /* =====================================================
     ORDER STATUS UPLOAD
     ===================================================== */

  const handleOrderWorkbookUpload = async () => {
    if (!orderFile) {
      setOrderError("Choose the Verizon order Excel file first.");
      return;
    }

    setUploadingOrders(true);
    setOrderError("");
    setOrderUploadResult(null);

    try {
      /* ---------------------------------------
         READ EXCEL
         --------------------------------------- */

      const arrayBuffer = await orderFile.arrayBuffer();

      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
      });

      console.log("Order workbook sheets:", workbook.SheetNames);

      const shippedSheetName = workbook.SheetNames.find((name) => name.trim().toLowerCase() === "shipped");

      const openSheetName = workbook.SheetNames.find((name) => name.trim().toLowerCase() === "open");

      if (!shippedSheetName) {
        throw new Error('Workbook does not contain a "Shipped" sheet.');
      }

      if (!openSheetName) {
        throw new Error('Workbook does not contain an "Open" sheet.');
      }

      const shippedSheet = workbook.Sheets[shippedSheetName];

      const openSheet = workbook.Sheets[openSheetName];

      /*
        raw: false is intentional.

        It keeps Excel-formatted dates and
        long tracking numbers closer to what
        the user sees in Excel.
      */

      const shippedRows = XLSX.utils.sheet_to_json(shippedSheet, {
        defval: "",
        raw: false,
      });

      const openRows = XLSX.utils.sheet_to_json(openSheet, {
        defval: "",
        raw: false,
      });

      /* ---------------------------------------
         NORMALIZE DATA
         --------------------------------------- */

      const normalizedShipped = shippedRows
        .map((row, index) =>
          normalizeOrderRow({
            row,
            status: "shipped",
            clientId: CLIENT_ID,
            sourceRow: index + 2,
          })
        )
        .filter(isValidOrderRow);

      const normalizedOpen = openRows
        .map((row, index) =>
          normalizeOrderRow({
            row,
            status: "open",
            clientId: CLIENT_ID,
            sourceRow: index + 2,
          })
        )
        .filter(isValidOrderRow);

      /* ---------------------------------------
         GENERATE CSV FILES
         --------------------------------------- */

      const shippedCSV = XLSX.utils.sheet_to_csv(shippedSheet);

      const openCSV = XLSX.utils.sheet_to_csv(openSheet);

      /* ---------------------------------------
         SAVE ORIGINAL WORKBOOK
         --------------------------------------- */

      const workbookRef = ref(storage, `portalOrders/${CLIENT_ID}/current-orders.xlsx`);

      await uploadBytes(workbookRef, orderFile, {
        contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      /* ---------------------------------------
         WIPE OLD FIRESTORE ORDERS
         --------------------------------------- */

      const deletedCount = await deleteExistingClientOrders(CLIENT_ID);

      /* ---------------------------------------
         WRITE NEW FIRESTORE ORDERS
         --------------------------------------- */

      const allOrders = [...normalizedShipped, ...normalizedOpen];

      await writePortalOrders(allOrders);

      /* ---------------------------------------
         UPLOAD GENERATED CSV FILES
         --------------------------------------- */

      const shippedCSVURL = await uploadOrderCSV({
        clientId: CLIENT_ID,
        status: "shipped",
        csvText: shippedCSV,
      });

      const openCSVURL = await uploadOrderCSV({
        clientId: CLIENT_ID,
        status: "open",
        csvText: openCSV,
      });

      /* ---------------------------------------
         UPDATE CLIENT CONFIG
         --------------------------------------- */

      await setDoc(
        doc(db, "portalClients", CLIENT_ID),
        {
          shippedOrdersCSVURL: shippedCSVURL,
          openOrdersCSVURL: openCSVURL,

          orderDataUpdatedAt: serverTimestamp(),

          orderDataFileName: orderFile.name,

          openOrderCount: normalizedOpen.length,

          shippedOrderCount: normalizedShipped.length,
        },
        {
          merge: true,
        }
      );

      /* ---------------------------------------
         ADMIN UPLOAD METADATA
         --------------------------------------- */

      const currentUser = auth.currentUser;

      await setDoc(
        doc(db, "adminUploads", "verizonOrders"),
        {
          lastUploadedAt: serverTimestamp(),

          lastUploadedByUid: currentUser?.uid || null,

          lastUploadedByEmail: currentUser?.email || null,

          lastFileName: orderFile.name,

          openOrderCount: normalizedOpen.length,

          shippedOrderCount: normalizedShipped.length,

          replacedOrderCount: deletedCount,

          status: "success",
        },
        {
          merge: true,
        }
      );

      setOrderUploadResult({
        open: normalizedOpen.length,

        shipped: normalizedShipped.length,

        deleted: deletedCount,
      });

      setOrderFile(null);

      if (orderFileInputRef.current) {
        orderFileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Order workbook upload error:", error);

      setOrderError(error.message || "Order workbook upload failed.");
    } finally {
      setUploadingOrders(false);
    }
  };

  return (
    <div className="admin-card">
      <h2 className="admin-h2">Operations</h2>

      {/* =====================================
          MASTER PARTS LIST
          ===================================== */}

      <div
        style={{
          border: "1px solid #eee",
          padding: 16,
          borderRadius: 8,
          marginTop: 16,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Verizon Master Parts List Upload</h3>

        <p className="admin-muted">
          Upload the approved Excel master file. This will update the existing Master Parts List Google Sheet and
          generate the separate Flattened Master Parts List Google Sheet.
        </p>

        <div
          style={{
            marginTop: 18,
            marginBottom: 14,
          }}
        >
          <label className="admin-muted">Password</label>

          <input
            className="admin-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            style={{
              width: "100%",
              maxWidth: 360,
            }}
          />
        </div>

        <div
          style={{
            marginBottom: 14,
          }}
        >
          <label className="admin-muted">Excel File</label>

          <input
            ref={fileInputRef}
            className="admin-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            style={{
              width: "100%",
              maxWidth: 520,
            }}
          />
        </div>

        {selectedFile && (
          <p className="admin-muted">
            Selected: <strong>{selectedFile.name}</strong>
          </p>
        )}

        <div className="admin-buttonRow">
          <button type="button" className="admin-primaryButton" onClick={handleSubmit} disabled={uploading}>
            {uploading ? "Uploading and processing..." : "Upload to Google Sheets"}
          </button>
        </div>

        {result && (
          <div
            className="admin-successCheck"
            style={{
              marginTop: 14,
            }}
          >
            ✓ Master Google Sheet updated. Detected {result.sheetsDetected} sheets and generated {result.flattenedRows}{" "}
            flattened rows.
          </div>
        )}

        {errorMsg && (
          <div
            className="admin-errorText"
            style={{
              marginTop: 14,
            }}
          >
            {errorMsg}
          </div>
        )}
      </div>

      {/* =====================================
          ORDER STATUS WORKBOOK
          ===================================== */}

      <div
        style={{
          border: "1px solid #eee",
          padding: 16,
          borderRadius: 8,
          marginTop: 28,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Verizon Order Status Upload</h3>

        <p className="admin-muted">
          Upload the current Verizon order workbook containing the <strong>Open</strong> and <strong>Shipped</strong>{" "}
          sheets. Uploading a new workbook completely replaces all previous Verizon order status data.
        </p>

        <div
          style={{
            marginTop: 18,
            marginBottom: 14,
          }}
        >
          <label className="admin-muted">Order Excel File</label>

          <input
            ref={orderFileInputRef}
            className="admin-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              setOrderFile(e.target.files?.[0] || null);

              setOrderError("");
              setOrderUploadResult(null);
            }}
            style={{
              width: "100%",
              maxWidth: 520,
            }}
          />
        </div>

        {orderFile && (
          <p className="admin-muted">
            Selected: <strong>{orderFile.name}</strong>
          </p>
        )}

        <button
          type="button"
          className="admin-primaryButton"
          onClick={handleOrderWorkbookUpload}
          disabled={uploadingOrders || !orderFile}
        >
          {uploadingOrders ? "Replacing order data..." : "Upload Order Workbook"}
        </button>

        {uploadingOrders && (
          <p
            className="admin-muted"
            style={{
              marginTop: 12,
            }}
          >
            Reading workbook, deleting old order data, importing Open and Shipped orders, and generating CSV files...
          </p>
        )}

        {orderUploadResult && (
          <div
            className="admin-successCheck"
            style={{
              marginTop: 14,
            }}
          >
            ✓ Order status updated. {orderUploadResult.open} open order lines and {orderUploadResult.shipped} shipped
            order lines imported. Previous data removed: {orderUploadResult.deleted} rows.
          </div>
        )}

        {orderError && (
          <div
            className="admin-errorText"
            style={{
              marginTop: 14,
            }}
          >
            {orderError}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductUpdatesTab() {
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false); // ✅ shows checkmark
  const [errorMsg, setErrorMsg] = useState("");

  const [lastMeta, setLastMeta] = useState(null);

  // Live “last uploaded” display
  useEffect(() => {
    const metaRef = doc(db, "adminUploads", "productTaxonomy");
    const unsub = onSnapshot(metaRef, (snap) => {
      if (snap.exists()) setLastMeta(snap.data());
      else setLastMeta(null);
    });
    return () => unsub();
  }, []);

  const handleUploadClick = () => {
    setErrorMsg("");
    setSuccess(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setSuccess(false);
    setErrorMsg("");

    try {
      await uploadProductTaxonomyFromExcel(file);

      // ✅ write “last uploaded” metadata to Firestore
      const u = auth.currentUser;
      await setDoc(
        doc(db, "adminUploads", "productTaxonomy"),
        {
          lastUploadedAt: serverTimestamp(),
          lastUploadedByUid: u?.uid || null,
          lastUploadedByEmail: u?.email || null,
          lastFileName: file.name,
          lastFileSize: file.size,
          lastUploadStatus: "success",
        },
        { merge: true }
      );

      setSuccess(true);
    } catch (err) {
      console.error(err);

      // (optional) log failure too
      const u = auth.currentUser;
      await setDoc(
        doc(db, "adminUploads", "productTaxonomy"),
        {
          lastUploadedAt: serverTimestamp(),
          lastUploadedByUid: u?.uid || null,
          lastUploadedByEmail: u?.email || null,
          lastFileName: file?.name || null,
          lastFileSize: file?.size || null,
          lastUploadStatus: "failed",
          lastError: String(err?.message || err),
        },
        { merge: true }
      );

      setErrorMsg("Upload failed. Check console for details.");
      setSuccess(false);
    } finally {
      setUploading(false);
      e.target.value = ""; // allows same file re-upload
    }
  };

  const lastUploadedText = (() => {
    const ts = lastMeta?.lastUploadedAt;
    if (!ts?.toDate) return "Never uploaded";
    const d = ts.toDate();
    return d.toLocaleString();
  })();

  return (
    <div className="admin-card">
      <h2 className="admin-h2">Reupload Product Taxonomy</h2>
      <p className="admin-muted">
        Upload the Excel file from{" "}
        <a
          style={{ color: "#D54F28" }}
          href="https://gofoton.sharepoint.com/:x:/r/sites/Websitemanagement/Shared%20Documents/2024%20Gofoton%20Website%20Overhaul/Gofoton.com%20Site%20Mapping/Product%20Taxonomy%20%26%20Resources%20EXCELS/Product%20Taxonomy.update%201.7.xlsx?d=w306d8b9746114dd08dbf1bf455ca7b80&csf=1&web=1&e=RilDWi"
          target="_blank"
        >
          SharePoint
        </a>
        .
      </p>

      <div className="admin-metaRow">
        <div className="admin-metaLabel">Last uploaded:</div>
        <div className="admin-metaValue">{lastUploadedText}</div>
      </div>

      {lastMeta?.lastFileName && (
        <div className="admin-metaRow">
          <div className="admin-metaLabel">Last file:</div>
          <div className="admin-metaValue">
            {lastMeta.lastFileName}
            {lastMeta?.lastUploadStatus ? ` (${lastMeta.lastUploadStatus})` : ""}
          </div>
        </div>
      )}

      {/* Hidden input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Button row + checkmark */}
      <div className="admin-buttonRow">
        <button type="button" className="admin-primaryButton" onClick={handleUploadClick} disabled={uploading}>
          {uploading ? "Uploading..." : "Upload Excel File"}
        </button>

        {success && <span className="admin-successCheck">✓ Uploaded</span>}
      </div>

      {errorMsg && <div className="admin-errorText">{errorMsg}</div>}
    </div>
  );
}

function TradeshowsTab() {
  const emptyForm = {
    name: "",
    slug: "",

    startDate: "",
    endDate: "",

    venue: "",
    location: "",
    booth: "",

    eventWebsite: "",

    logoUrl: "",
    backgroundImageUrl: "",

    /* HERO */

    heroHeading: "",
    heroEventLine: "",
    heroDescription: "",

    heroButtonLabel: "",
    heroButtonLink: "",

    /* OVERVIEW */

    overviewBadgeHeading: "",

    overviewHtml: "",

    passText: "",
    passLinkText: "",
    passLink: "",

    /* SPECIAL EVENTS */

    specialEventsHeading: "",

    specialEvents: [],

    /* FEATURED PRODUCTS */

    featuredProductsHeading: "",

    featuredProducts: [],

    /* SUBSCRIBE */

    subscribeEnabled: true,

    subscribeHeading: "",

    subscribeButtonLabel: "Subscribe to Go!Foton news",

    subscribeButtonLink: "/subscribe",

    /* OTHER SHOWS */

    otherShowsEnabled: true,

    otherShowsHeading: "Other upcoming shows",

    /* MOBILE UNIT */

    mobileUnitEnabled: true,

    mobileUnitHeading: "Need us to come to you?",

    mobileUnitButtonLabel: "Request the Go!Foton mobile demo unit",

    mobileUnitButtonLink:
      "mailto:brand@gofoton.com?subject=I%E2%80%99d%20like%20to%20request%20a%20Go!Foton%20mobile%20demo",

    displayOrder: 1,

    active: true,

    showOnEventsPage: true,
  };

  const [tradeshows, setTradeshows] = useState([]);
  const [form, setForm] = useState(emptyForm);

  const [editingId, setEditingId] = useState(null);

  const [logoFile, setLogoFile] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const loadTradeshows = async () => {
    setLoading(true);

    try {
      const snap = await getDocs(collection(db, "tradeshows"));

      const rows = snap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .sort((a, b) => {
          const orderA = Number(a.displayOrder || 999);
          const orderB = Number(b.displayOrder || 999);

          return orderA - orderB;
        });

      setTradeshows(rows);
    } catch (error) {
      console.error("Error loading tradeshows:", error);
      alert("Could not load tradeshows.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTradeshows();
  }, []);

  const makeTradeshowSlug = (value = "") => {
    return value
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addSpecialEvent = () => {
    setForm((prev) => ({
      ...prev,

      specialEvents: [
        ...(prev.specialEvents || []),

        {
          title: "",
          subtitle: "",
          description: "",
          image: "",
          details: [],
          buttonLabel: "",
          calendarLink: "",
          secondaryButtonLabel: "",
          secondaryButtonLink: "",
        },
      ],
    }));
  };

  const updateSpecialEvent = (index, field, value) => {
    setForm((prev) => ({
      ...prev,

      specialEvents: (prev.specialEvents || []).map((event, eventIndex) =>
        eventIndex === index
          ? {
              ...event,
              [field]: value,
            }
          : event
      ),
    }));
  };

  const removeSpecialEvent = (index) => {
    setForm((prev) => ({
      ...prev,

      specialEvents: (prev.specialEvents || []).filter((_, eventIndex) => eventIndex !== index),
    }));
  };

  const addFeaturedProduct = () => {
    setForm((prev) => ({
      ...prev,

      featuredProducts: [
        ...(prev.featuredProducts || []),

        {
          name: "",
          image: "",
          link: "",
        },
      ],
    }));
  };

  const updateFeaturedProduct = (index, field, value) => {
    setForm((prev) => ({
      ...prev,

      featuredProducts: (prev.featuredProducts || []).map((product, productIndex) =>
        productIndex === index
          ? {
              ...product,
              [field]: value,
            }
          : product
      ),
    }));
  };

  const removeFeaturedProduct = (index) => {
    setForm((prev) => ({
      ...prev,

      featuredProducts: (prev.featuredProducts || []).filter((_, productIndex) => productIndex !== index),
    }));
  };

  const handleNameChange = (value) => {
    setForm((prev) => ({
      ...prev,

      name: value,

      // Only automatically generate the slug
      // while adding a new show.
      slug: editingId ? prev.slug : makeTradeshowSlug(value),
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);

    setLogoFile(null);
    setBackgroundFile(null);
  };

  const uploadTradeshowImage = async (file, showSlug, imageType) => {
    if (!file) {
      return imageType === "logo" ? form.logoUrl : form.backgroundImageUrl;
    }

    const safeFileName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, "-");

    const storageRef = ref(storage, `tradeshows/${showSlug}/${imageType}-${Date.now()}-${safeFileName}`);

    await uploadBytes(storageRef, file);

    return await getDownloadURL(storageRef);
  };

  const saveTradeshow = async () => {
    const name = form.name.trim();
    const slug = makeTradeshowSlug(form.slug || name);

    if (!name) {
      alert("Show name is required.");
      return;
    }

    if (!slug) {
      alert("A valid URL slug is required.");
      return;
    }

    if (!form.startDate) {
      alert("Start date is required.");
      return;
    }

    if (!form.endDate) {
      alert("End date is required.");
      return;
    }

    setSaving(true);

    try {
      const logoUrl = await uploadTradeshowImage(logoFile, slug, "logo");

      const backgroundImageUrl = await uploadTradeshowImage(backgroundFile, slug, "background");

      /*
        We use the slug as the Firestore document ID.

        Example:
        tradeshows / ai-infra-summit
      */

      const documentId = editingId || slug;

      await setDoc(
        doc(db, "tradeshows", documentId),
        {
          name,
          slug,

          startDate: form.startDate,
          endDate: form.endDate,

          venue: form.venue.trim(),
          location: form.location.trim(),
          booth: form.booth.trim(),

          eventWebsite: form.eventWebsite.trim(),

          logoUrl,
          backgroundImageUrl,

          heroHeading: form.heroHeading.trim(),

          heroEventLine: form.heroEventLine.trim(),

          heroDescription: form.heroDescription.trim(),

          heroButtonLabel: form.heroButtonLabel.trim(),

          heroButtonLink: form.heroButtonLink.trim(),

          overviewBadgeHeading: form.overviewBadgeHeading.trim(),

          overviewHtml: form.overviewHtml,

          passText: form.passText.trim(),

          passLinkText: form.passLinkText.trim(),

          passLink: form.passLink.trim(),

          specialEventsHeading: form.specialEventsHeading.trim(),

          specialEvents: form.specialEvents || [],

          featuredProductsHeading: form.featuredProductsHeading.trim(),

          featuredProducts: form.featuredProducts || [],

          subscribeEnabled: form.subscribeEnabled !== false,

          subscribeHeading: form.subscribeHeading.trim(),

          subscribeButtonLabel: form.subscribeButtonLabel.trim(),

          subscribeButtonLink: form.subscribeButtonLink.trim(),

          otherShowsEnabled: form.otherShowsEnabled !== false,

          otherShowsHeading: form.otherShowsHeading.trim(),

          mobileUnitEnabled: form.mobileUnitEnabled !== false,

          mobileUnitHeading: form.mobileUnitHeading.trim(),

          mobileUnitButtonLabel: form.mobileUnitButtonLabel.trim(),

          mobileUnitButtonLink: form.mobileUnitButtonLink.trim(),

          displayOrder: Number(form.displayOrder) || 1,

          active: form.active !== false,

          showOnEventsPage: form.showOnEventsPage !== false,

          updatedAt: serverTimestamp(),

          ...(editingId
            ? {}
            : {
                createdAt: serverTimestamp(),
              }),
        },
        {
          merge: true,
        }
      );

      /*
        If somebody changed the slug while editing,
        we intentionally do NOT automatically move
        the Firestore document ID.

        The saved `slug` field controls the website URL.
      */

      resetForm();
      await loadTradeshows();

      alert(editingId ? "Tradeshow updated." : "Tradeshow added.");
    } catch (error) {
      console.error("Error saving tradeshow:", error);

      alert("Could not save tradeshow.");
    } finally {
      setSaving(false);
    }
  };

  const editTradeshow = (show) => {
    setEditingId(show.id);

    setForm({
      name: show.name || "",

      slug: show.slug || show.id || "",

      startDate: show.startDate || "",

      endDate: show.endDate || "",

      venue: show.venue || "",

      location: show.location || "",

      booth: show.booth || "",

      eventWebsite: show.eventWebsite || "",

      logoUrl: show.logoUrl || "",

      backgroundImageUrl: show.backgroundImageUrl || "",

      heroHeading: show.heroHeading || "",

      heroEventLine: show.heroEventLine || "",

      heroDescription: show.heroDescription || "",

      heroButtonLabel: show.heroButtonLabel || "",

      heroButtonLink: show.heroButtonLink || "",

      overviewBadgeHeading: show.overviewBadgeHeading || "",

      overviewHtml: show.overviewHtml || "",

      passText: show.passText || "",

      passLinkText: show.passLinkText || "",

      passLink: show.passLink || "",

      specialEventsHeading: show.specialEventsHeading || "",

      specialEvents: Array.isArray(show.specialEvents) ? show.specialEvents : [],

      featuredProductsHeading: show.featuredProductsHeading || "",

      featuredProducts: Array.isArray(show.featuredProducts) ? show.featuredProducts : [],

      subscribeEnabled: show.subscribeEnabled !== false,

      subscribeHeading: show.subscribeHeading || "",

      subscribeButtonLabel: show.subscribeButtonLabel || "Subscribe to Go!Foton news",

      subscribeButtonLink: show.subscribeButtonLink || "/subscribe",

      otherShowsEnabled: show.otherShowsEnabled !== false,

      otherShowsHeading: show.otherShowsHeading || "Other upcoming shows",

      mobileUnitEnabled: show.mobileUnitEnabled !== false,

      mobileUnitHeading: show.mobileUnitHeading || "Need us to come to you?",

      mobileUnitButtonLabel: show.mobileUnitButtonLabel || "Request the Go!Foton mobile demo unit",

      mobileUnitButtonLink:
        show.mobileUnitButtonLink ||
        "mailto:brand@gofoton.com?subject=I%E2%80%99d%20like%20to%20request%20a%20Go!Foton%20mobile%20demo",

      displayOrder: Number(show.displayOrder || 1),

      active: show.active !== false,

      showOnEventsPage: show.showOnEventsPage !== false,
    });

    setLogoFile(null);
    setBackgroundFile(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const deleteTradeshow = async (show) => {
    const confirmed = window.confirm(`Delete ${show.name}?`);

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "tradeshows", show.id));

      if (editingId === show.id) {
        resetForm();
      }

      await loadTradeshows();
    } catch (error) {
      console.error("Error deleting tradeshow:", error);

      alert("Could not delete tradeshow.");
    }
  };

  const getBoothDisplay = (booth) => {
    if (!booth?.trim()) {
      return "Booth: Coming Soon";
    }

    return `Booth ${booth}`;
  };

  return (
    <div className="admin-card admin-tradeshows-card">
      <div className="admin-tradeshow-header">
        <div>
          <h2 className="admin-h2">Tradeshows</h2>

          <p className="admin-muted">
            Manage event cards, individual tradeshow pages, and homepage tradeshow promotion.
          </p>
        </div>
      </div>

      {/* ===========================
          ADD / EDIT FORM
          =========================== */}

      <div className="admin-tradeshow-editor">
        <h3>{editingId ? "Edit Tradeshow" : "Add Tradeshow"}</h3>

        <div className="admin-tradeshow-grid">
          <div>
            <label className="admin-muted">Show Name</label>

            <input
              className="admin-input"
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="AI Infra Summit"
            />
          </div>

          <div>
            <label className="admin-muted">URL Slug</label>

            <input
              className="admin-input"
              type="text"
              value={form.slug}
              onChange={(e) => handleChange("slug", makeTradeshowSlug(e.target.value))}
              placeholder="ai-infra-summit"
            />

            <div className="admin-tradeshow-urlPreview">/{form.slug || "show-name"}</div>
          </div>

          <div>
            <label className="admin-muted">Start Date</label>

            <input
              className="admin-input"
              type="date"
              value={form.startDate}
              onChange={(e) => handleChange("startDate", e.target.value)}
            />
          </div>

          <div>
            <label className="admin-muted">End Date</label>

            <input
              className="admin-input"
              type="date"
              value={form.endDate}
              onChange={(e) => handleChange("endDate", e.target.value)}
            />
          </div>

          <div>
            <label className="admin-muted">Venue</label>

            <input
              className="admin-input"
              type="text"
              value={form.venue}
              onChange={(e) => handleChange("venue", e.target.value)}
              placeholder="Santa Clara Convention Center"
            />
          </div>

          <div>
            <label className="admin-muted">Location</label>

            <input
              className="admin-input"
              type="text"
              value={form.location}
              onChange={(e) => handleChange("location", e.target.value)}
              placeholder="Santa Clara, CA"
            />
          </div>

          <div>
            <label className="admin-muted">Booth</label>

            <input
              className="admin-input"
              type="text"
              value={form.booth}
              onChange={(e) => handleChange("booth", e.target.value)}
              placeholder="321"
            />

            <div className="admin-tradeshow-urlPreview">Leave blank to show "Booth: Coming Soon"</div>
          </div>

          <div>
            <label className="admin-muted">Display Order</label>

            <input
              className="admin-input"
              type="number"
              min="1"
              value={form.displayOrder}
              onChange={(e) => handleChange("displayOrder", e.target.value)}
            />
          </div>
        </div>

        <div className="admin-tradeshow-fullField">
          <label className="admin-muted">Event Website</label>

          <input
            className="admin-input"
            type="text"
            value={form.eventWebsite}
            onChange={(e) => handleChange("eventWebsite", e.target.value)}
            placeholder="https://..."
          />
        </div>

        {/* ===========================
            IMAGES
            =========================== */}

        <div className="admin-tradeshow-section">
          <h3>Images</h3>

          <div className="admin-tradeshow-grid">
            <div>
              <label className="admin-muted">Tradeshow Logo</label>

              <input
                className="admin-input"
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              />

              {form.logoUrl && !logoFile && (
                <img src={form.logoUrl} alt="" className="admin-tradeshow-imagePreview logo" />
              )}
            </div>

            <div>
              <label className="admin-muted">Background Image</label>

              <input
                className="admin-input"
                type="file"
                accept="image/*"
                onChange={(e) => setBackgroundFile(e.target.files?.[0] || null)}
              />

              {form.backgroundImageUrl && !backgroundFile && (
                <img src={form.backgroundImageUrl} alt="" className="admin-tradeshow-imagePreview" />
              )}
            </div>
          </div>
        </div>

        {/* ===========================
            HERO
            =========================== */}

        <div className="admin-tradeshow-section">
          <h3>Tradeshow Page Hero</h3>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Hero Heading</label>

            <input
              className="admin-input"
              type="text"
              value={form.heroHeading}
              onChange={(e) => handleChange("heroHeading", e.target.value)}
              placeholder="OPTICAL EXCELLENCE FOR SMARTER AI INFRASTRUCTURE"
            />
          </div>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Hero Event Line</label>

            <input
              className="admin-input"
              type="text"
              value={form.heroEventLine}
              onChange={(e) => handleChange("heroEventLine", e.target.value)}
              placeholder="Join us at AI Infra Summit"
            />
          </div>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Hero Description</label>

            <textarea
              className="admin-input"
              value={form.heroDescription}
              onChange={(e) => handleChange("heroDescription", e.target.value)}
              rows={4}
              placeholder="Visit our booth to see..."
            />
          </div>

          <div className="admin-tradeshow-grid">
            <div>
              <label className="admin-muted">Hero Button Label</label>

              <input
                className="admin-input"
                type="text"
                value={form.heroButtonLabel}
                onChange={(e) => handleChange("heroButtonLabel", e.target.value)}
                placeholder="Meet at AI Infra Summit"
              />
            </div>

            <div>
              <label className="admin-muted">Hero Button Link</label>

              <input
                className="admin-input"
                type="text"
                value={form.heroButtonLink}
                onChange={(e) => handleChange("heroButtonLink", e.target.value)}
                placeholder="mailto:brand@gofoton.com..."
              />
            </div>
          </div>
        </div>

        {/* ===========================
    OVERVIEW
    =========================== */}

        <div className="admin-tradeshow-section">
          <h3>Tradeshow Page Overview</h3>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Event Badge Heading</label>

            <input
              className="admin-input"
              type="text"
              value={form.overviewBadgeHeading}
              onChange={(e) => handleChange("overviewBadgeHeading", e.target.value)}
              placeholder="AI Infra Summit Exhibit 321"
            />
          </div>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Full Event Description</label>

            <textarea
              className="admin-input"
              value={form.overviewHtml}
              onChange={(e) => handleChange("overviewHtml", e.target.value)}
              rows={10}
              placeholder="<p>We’d love to see you at...</p>"
            />

            <p
              className="admin-muted"
              style={{
                fontSize: 12,
                marginTop: 6,
              }}
            >
              HTML is allowed here so you can use paragraphs, links, bold text, etc.
            </p>
          </div>

          <div className="admin-tradeshow-grid">
            <div>
              <label className="admin-muted">Registration Intro</label>

              <input
                className="admin-input"
                value={form.passText}
                onChange={(e) => handleChange("passText", e.target.value)}
                placeholder="Need a pass?"
              />
            </div>

            <div>
              <label className="admin-muted">Registration Link Text</label>

              <input
                className="admin-input"
                value={form.passLinkText}
                onChange={(e) => handleChange("passLinkText", e.target.value)}
                placeholder="Register here."
              />
            </div>
          </div>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Registration Link</label>

            <input
              className="admin-input"
              value={form.passLink}
              onChange={(e) => handleChange("passLink", e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>

        {/* ===========================
    SPECIAL EVENTS
    =========================== */}

        <div className="admin-tradeshow-section">
          <h3>Schedule / Special Events</h3>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Section Heading</label>

            <input
              className="admin-input"
              value={form.specialEventsHeading}
              onChange={(e) => handleChange("specialEventsHeading", e.target.value)}
              placeholder="Don’t miss these special opportunities..."
            />
          </div>

          {(form.specialEvents || []).map((event, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ddd",
                borderRadius: 12,
                padding: 16,
                marginTop: 18,
              }}
            >
              <h4>Event {index + 1}</h4>

              <label className="admin-muted">Title</label>

              <input
                className="admin-input"
                value={event.title || ""}
                onChange={(e) => updateSpecialEvent(index, "title", e.target.value)}
                style={{
                  width: "100%",
                  marginBottom: 12,
                }}
              />

              <label className="admin-muted">Subtitle</label>

              <input
                className="admin-input"
                value={event.subtitle || ""}
                onChange={(e) => updateSpecialEvent(index, "subtitle", e.target.value)}
                style={{
                  width: "100%",
                  marginBottom: 12,
                }}
              />

              <label className="admin-muted">Description</label>

              <textarea
                className="admin-input"
                value={event.description || ""}
                onChange={(e) => updateSpecialEvent(index, "description", e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  marginBottom: 12,
                }}
              />

              <label className="admin-muted">Image URL</label>

              <input
                className="admin-input"
                value={event.image || ""}
                onChange={(e) => updateSpecialEvent(index, "image", e.target.value)}
                style={{
                  width: "100%",
                  marginBottom: 12,
                }}
              />

              <label className="admin-muted">Event Details</label>

              <textarea
                className="admin-input"
                value={(event.details || []).join("\n")}
                onChange={(e) =>
                  updateSpecialEvent(
                    index,
                    "details",
                    e.target.value
                      .split("\n")
                      .map((x) => x.trim())
                      .filter(Boolean)
                  )
                }
                rows={4}
                placeholder={"Expo Theater 2\nTuesday, September 15\n12:15 PM - 12:30 PM"}
                style={{
                  width: "100%",
                  marginBottom: 12,
                }}
              />

              <div className="admin-tradeshow-grid">
                <div>
                  <label className="admin-muted">Button Label</label>

                  <input
                    className="admin-input"
                    value={event.buttonLabel || ""}
                    onChange={(e) => updateSpecialEvent(index, "buttonLabel", e.target.value)}
                  />
                </div>

                <div>
                  <label className="admin-muted">Button Link</label>

                  <input
                    className="admin-input"
                    value={event.calendarLink || ""}
                    onChange={(e) => updateSpecialEvent(index, "calendarLink", e.target.value)}
                  />
                </div>

                <div>
                  <label className="admin-muted">Secondary Button</label>

                  <input
                    className="admin-input"
                    value={event.secondaryButtonLabel || ""}
                    onChange={(e) => updateSpecialEvent(index, "secondaryButtonLabel", e.target.value)}
                  />
                </div>

                <div>
                  <label className="admin-muted">Secondary Link</label>

                  <input
                    className="admin-input"
                    value={event.secondaryButtonLink || ""}
                    onChange={(e) => updateSpecialEvent(index, "secondaryButtonLink", e.target.value)}
                  />
                </div>
              </div>

              <button
                type="button"
                className="admin-junkButton"
                onClick={() => removeSpecialEvent(index)}
                style={{
                  marginTop: 16,
                }}
              >
                Remove Event
              </button>
            </div>
          ))}

          <button
            type="button"
            className="admin-navItem"
            onClick={addSpecialEvent}
            style={{
              marginTop: 16,
            }}
          >
            + Add Schedule Event
          </button>
        </div>

        {/* ===========================
    FEATURED PRODUCTS
    =========================== */}

        <div className="admin-tradeshow-section">
          <h3>Featured Products</h3>

          <div className="admin-tradeshow-fullField">
            <label className="admin-muted">Section Heading</label>

            <input
              className="admin-input"
              value={form.featuredProductsHeading}
              onChange={(e) => handleChange("featuredProductsHeading", e.target.value)}
              placeholder="Get a hands-on look at our AI-ready fiber optic connectivity"
            />
          </div>

          {(form.featuredProducts || []).map((product, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ddd",
                borderRadius: 12,
                padding: 16,
                marginTop: 18,
              }}
            >
              <div className="admin-tradeshow-grid">
                <div>
                  <label className="admin-muted">Product Name</label>

                  <input
                    className="admin-input"
                    value={product.name || ""}
                    onChange={(e) => updateFeaturedProduct(index, "name", e.target.value)}
                  />
                </div>

                <div>
                  <label className="admin-muted">Product Link</label>

                  <input
                    className="admin-input"
                    value={product.link || ""}
                    onChange={(e) => updateFeaturedProduct(index, "link", e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-tradeshow-fullField">
                <label className="admin-muted">Product Image URL</label>

                <input
                  className="admin-input"
                  value={product.image || ""}
                  onChange={(e) => updateFeaturedProduct(index, "image", e.target.value)}
                />
              </div>

              <button
                type="button"
                className="admin-junkButton"
                onClick={() => removeFeaturedProduct(index)}
                style={{
                  marginTop: 14,
                }}
              >
                Remove Product
              </button>
            </div>
          ))}

          <button
            type="button"
            className="admin-navItem"
            onClick={addFeaturedProduct}
            style={{
              marginTop: 16,
            }}
          >
            + Add Featured Product
          </button>
        </div>

        {/* ===========================
    FOLLOW-UP SECTIONS
    =========================== */}

        <div className="admin-tradeshow-section">
          <h3>Follow-Up / Other Ways to Find Us</h3>

          <label
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <input
              type="checkbox"
              checked={form.subscribeEnabled}
              onChange={(e) => handleChange("subscribeEnabled", e.target.checked)}
            />
            Show Subscribe Section
          </label>

          <input
            className="admin-input"
            value={form.subscribeHeading}
            onChange={(e) => handleChange("subscribeHeading", e.target.value)}
            placeholder="Can’t make AI Infra Summit? Let’s stay in touch."
            style={{
              width: "100%",
              marginBottom: 12,
            }}
          />

          <div className="admin-tradeshow-grid">
            <input
              className="admin-input"
              value={form.subscribeButtonLabel}
              onChange={(e) => handleChange("subscribeButtonLabel", e.target.value)}
              placeholder="Subscribe to Go!Foton news"
            />

            <input
              className="admin-input"
              value={form.subscribeButtonLink}
              onChange={(e) => handleChange("subscribeButtonLink", e.target.value)}
              placeholder="/subscribe"
            />
          </div>

          <hr
            style={{
              margin: "28px 0",
              border: 0,
              borderTop: "1px solid #eee",
            }}
          />

          <label
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <input
              type="checkbox"
              checked={form.otherShowsEnabled}
              onChange={(e) => handleChange("otherShowsEnabled", e.target.checked)}
            />
            Show Other Upcoming Tradeshows
          </label>

          <input
            className="admin-input"
            value={form.otherShowsHeading}
            onChange={(e) => handleChange("otherShowsHeading", e.target.value)}
            placeholder="Other upcoming shows"
            style={{
              width: "100%",
            }}
          />

          <hr
            style={{
              margin: "28px 0",
              border: 0,
              borderTop: "1px solid #eee",
            }}
          />

          <label
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <input
              type="checkbox"
              checked={form.mobileUnitEnabled}
              onChange={(e) => handleChange("mobileUnitEnabled", e.target.checked)}
            />
            Show Mobile Demo Section
          </label>

          <input
            className="admin-input"
            value={form.mobileUnitHeading}
            onChange={(e) => handleChange("mobileUnitHeading", e.target.value)}
            placeholder="Need us to come to you?"
            style={{
              width: "100%",
              marginBottom: 12,
            }}
          />

          <div className="admin-tradeshow-grid">
            <input
              className="admin-input"
              value={form.mobileUnitButtonLabel}
              onChange={(e) => handleChange("mobileUnitButtonLabel", e.target.value)}
              placeholder="Request the Go!Foton mobile demo unit"
            />

            <input
              className="admin-input"
              value={form.mobileUnitButtonLink}
              onChange={(e) => handleChange("mobileUnitButtonLink", e.target.value)}
              placeholder="mailto:..."
            />
          </div>
        </div>
        {/* ===========================
            DISPLAY OPTIONS
            =========================== */}

        <div className="admin-tradeshow-options">
          <label>
            <input type="checkbox" checked={form.active} onChange={(e) => handleChange("active", e.target.checked)} />
            Active
          </label>

          <label>
            <input
              type="checkbox"
              checked={form.showOnEventsPage}
              onChange={(e) => handleChange("showOnEventsPage", e.target.checked)}
            />
            Show on Events Page
          </label>
        </div>

        <div className="admin-buttonRow">
          <button type="button" className="admin-primaryButton" onClick={saveTradeshow} disabled={saving}>
            {saving ? "Saving..." : editingId ? "Save Tradeshow Changes" : "Add Tradeshow"}
          </button>

          {editingId && (
            <button type="button" className="admin-navItem" onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      {/* ===========================
          EXISTING SHOWS
          =========================== */}

      <div className="admin-tradeshow-list">
        <h3>Existing Tradeshows</h3>

        {loading ? (
          <p className="admin-muted">Loading tradeshows...</p>
        ) : tradeshows.length === 0 ? (
          <p className="admin-muted">No tradeshows have been added yet.</p>
        ) : (
          <div className="admin-tableWrap">
            <table className="admin-tradeshow-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Logo</th>
                  <th>Show</th>
                  <th>Dates</th>
                  <th>Location</th>
                  <th>Booth</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {tradeshows.map((show) => (
                  <tr key={show.id}>
                    <td>{show.displayOrder || "—"}</td>

                    <td>
                      {show.logoUrl ? <img src={show.logoUrl} alt="" className="admin-tradeshow-tableLogo" /> : "—"}
                    </td>

                    <td>
                      <strong>{show.name}</strong>

                      <br />

                      <span className="admin-muted">/{show.slug || show.id}</span>
                    </td>

                    <td>
                      {show.startDate || "—"}

                      <br />

                      <span className="admin-muted">through {show.endDate || "—"}</span>
                    </td>

                    <td>{show.location || "Coming Soon"}</td>

                    <td>{getBoothDisplay(show.booth)}</td>

                    <td>
                      <div className="admin-tradeshow-statuses">
                        <span className={`admin-tradeshow-status ${show.active !== false ? "active" : ""}`}>
                          {show.active !== false ? "Active" : "Inactive"}
                        </span>

                        {show.showOnEventsPage !== false && (
                          <span className="admin-tradeshow-status active">Events Page</span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="admin-tradeshow-actions">
                        <button type="button" onClick={() => editTradeshow(show)}>
                          Edit
                        </button>

                        <a href={`/${show.slug || show.id}`} target="_blank" rel="noopener noreferrer">
                          View
                        </a>

                        <button type="button" className="delete" onClick={() => deleteTradeshow(show)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentsTab() {
  const [currentFolder, setCurrentFolder] = useState("brochures");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [editingFile, setEditingFile] = useState(null);
  const [metadataForm, setMetadataForm] = useState({
    title: "",
    date: "",
    description: "",
  });
  const [savingMetadata, setSavingMetadata] = useState(false);

  const folders = [
    "brochures",
    "catalogs",
    "datasheets",
    "featured",
    "icons",
    "installationGuides",
    "other",
    "pressReleases",
    "productImages",
    "productSheets",
    "stockImages",
    "whitepapers",
  ];

  const formatName = (str) => str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

  const getDocumentTypeFromFolder = (folder) => {
    if (folder === "pressReleases") return "pressRelease";
    if (folder === "brochures") return "brochure";
    if (folder === "catalogs") return "catalog";
    if (folder === "whitepapers") return "whitepaper";
    return folder;
  };

  const fetchPressReleaseMetadata = async () => {
    const snap = await getDocs(collection(db, "documentMetadata"));

    const byPath = {};
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.storagePath) {
        byPath[data.storagePath] = {
          id: docSnap.id,
          ...data,
        };
      }
    });

    return byPath;
  };

  const fetchFiles = async () => {
    setLoading(true);

    try {
      const listRef = ref(storage, currentFolder);
      const res = await listAll(listRef);

      let metadataByPath = {};
      if (["pressReleases", "brochures", "catalogs", "whitepapers"].includes(currentFolder)) {
        metadataByPath = await fetchPressReleaseMetadata();
      }

      const fileData = await Promise.all(
        res.items.map(async (item) => {
          const url = await getDownloadURL(item);

          return {
            name: item.name,
            path: item.fullPath,
            url,
            metadata: metadataByPath[item.fullPath] || null,
          };
        })
      );

      setFiles(fileData);
    } catch (err) {
      console.error("Error fetching files:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [currentFolder]);

  const upsertPressReleaseMetadata = async (file, overrides = {}) => {
    if (!file?.path) return;

    const metadataId = getDocumentMetadataId(file.path);
    await setDoc(
      doc(db, "documentMetadata", metadataId),
      {
        storagePath: file.path,
        folder: file.folder || currentFolder,
        fileName: file.name,
        url: file.url,
        type: getDocumentTypeFromFolder(file.folder || currentFolder),
        title: overrides.title ?? file.metadata?.title ?? buildFallbackTitle(file.name),
        date: overrides.date ?? file.metadata?.date ?? "",
        description: overrides.description ?? file.metadata?.description ?? "",
        downloads: file.metadata?.downloads ?? 0,
        updatedAt: serverTimestamp(),
        createdAt: file.metadata?.createdAt || serverTimestamp(),
      },
      { merge: true }
    );
  };

  const handleUpload = async (e, replaceName = null) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const fileName = replaceName || uploadedFile.name;
    const storageRef = ref(storage, `${currentFolder}/${fileName}`);

    setUploading(true);

    try {
      await uploadBytes(storageRef, uploadedFile);

      const downloadURL = await getDownloadURL(storageRef);

      if (["pressReleases", "brochures", "catalogs", "whitepapers"].includes(currentFolder)) {
        const fileRecord = {
          name: fileName,
          path: `${currentFolder}/${fileName}`,
          url: downloadURL,
          folder: currentFolder,
          metadata: null,
        };

        await upsertPressReleaseMetadata(fileRecord);
      }

      await navigator.clipboard.writeText(downloadURL);

      alert(
        `${replaceName ? "File replaced successfully!" : "File uploaded successfully!"}\n\nURL copied to clipboard:\n${downloadURL}`
      );

      fetchFiles();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openMetadataEditor = (file) => {
    setEditingFile(file);

    setMetadataForm({
      title: file.metadata?.title || buildFallbackTitle(file.name),
      date: file.metadata?.date || "",
      description: file.metadata?.description || "",
    });
  };

  const saveMetadata = async () => {
    if (!editingFile) return;

    if (metadataForm.title.length > 150) {
      alert("Title must be 150 characters or less.");
      return;
    }

    if (metadataForm.description.length > 150) {
      alert("Description must be 150 characters or less.");
      return;
    }

    setSavingMetadata(true);

    try {
      await upsertPressReleaseMetadata(editingFile, {
        title: metadataForm.title.trim(),
        date: metadataForm.date,
        description: metadataForm.description.trim(),
      });

      setEditingFile(null);
      fetchFiles();
    } catch (err) {
      console.error("Error saving metadata:", err);
      alert("Could not save metadata.");
    } finally {
      setSavingMetadata(false);
    }
  };

  return (
    <div className="admin-card">
      <h2 className="admin-h2" style={{ marginBottom: "10px" }}>
        Document Manager
      </h2>

      <div
        className="admin-folder-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        {folders.map((f) => (
          <button
            key={f}
            className={`admin-navItem ${currentFolder === f ? "is-active" : ""}`}
            onClick={() => setCurrentFolder(f)}
            style={{ fontSize: "12px", padding: "8px" }}
          >
            {formatName(f)}
          </button>
        ))}
      </div>

      <div
        className="admin-upload-zone"
        style={{
          border: "2px dashed #ccc",
          padding: "20px",
          textAlign: "center",
          marginBottom: "20px",
        }}
      >
        <input type="file" id="main-upload" onChange={(e) => handleUpload(e)} style={{ display: "none" }} />

        <label htmlFor="main-upload" className="admin-primaryButton" style={{ cursor: "pointer" }}>
          {uploading ? "Processing..." : `Upload to ${formatName(currentFolder)}`}
        </label>

        {currentFolder === "pressReleases" && (
          <p className="admin-muted" style={{ marginTop: 10 }}>
            Press releases uploaded here will also get a Firestore metadata record for the News page.
          </p>
        )}
      </div>

      <div className="admin-file-list">
        {loading ? (
          <p>Loading files...</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                <th style={{ padding: "10px" }}>File Name</th>
                {currentFolder === "pressReleases" && <th style={{ padding: "10px" }}>Metadata</th>}
                <th style={{ padding: "10px" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {files.map((file) => (
                <tr key={file.path} style={{ borderBottom: "1px solid #f9f9f9" }}>
                  <td style={{ padding: "10px", fontSize: "14px" }}>{file.name}</td>

                  {currentFolder === "pressReleases" && (
                    <td style={{ padding: "10px", fontSize: "13px" }}>
                      <strong>{file.metadata?.title || "No title set"}</strong>
                      <br />
                      <span className="admin-muted">
                        {file.metadata?.date || "No date"} — {file.metadata?.description || "No description"}
                      </span>
                    </td>
                  )}

                  <td style={{ padding: "10px" }}>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="admin-muted"
                        style={{ textDecoration: "none", color: "#0072b1" }}
                      >
                        View
                      </a>

                      <label
                        style={{
                          color: "#D54F28",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        Replace
                        <input type="file" style={{ display: "none" }} onChange={(e) => handleUpload(e, file.name)} />
                      </label>

                      {currentFolder === "pressReleases" && (
                        <button
                          type="button"
                          onClick={() => openMetadataEditor(file)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#333",
                            cursor: "pointer",
                            fontWeight: "bold",
                            padding: 0,
                          }}
                        >
                          Edit Metadata
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editingFile && (
        <div className="popup-overlay" onClick={() => setEditingFile(null)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2 className="admin-h2">Edit Press Release Metadata</h2>

            <p className="admin-muted" style={{ marginBottom: 16 }}>
              Title and description are limited to 150 characters. This metadata controls how the item appears on the
              News page.
            </p>

            <label className="admin-muted">Title</label>
            <input
              className="admin-input"
              type="text"
              maxLength={150}
              value={metadataForm.title}
              onChange={(e) => setMetadataForm((prev) => ({ ...prev, title: e.target.value }))}
              style={{ width: "100%", marginBottom: 6 }}
            />
            <div className="admin-muted" style={{ fontSize: 12, marginBottom: 12 }}>
              {metadataForm.title.length}/150
            </div>

            <label className="admin-muted">Date</label>
            <input
              className="admin-input"
              type="date"
              value={metadataForm.date}
              onChange={(e) => setMetadataForm((prev) => ({ ...prev, date: e.target.value }))}
              style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="admin-muted">Description</label>
            <textarea
              className="admin-input"
              maxLength={150}
              value={metadataForm.description}
              onChange={(e) =>
                setMetadataForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={4}
              style={{ width: "100%", marginBottom: 6 }}
            />
            <div className="admin-muted" style={{ fontSize: 12, marginBottom: 18 }}>
              {metadataForm.description.length}/150
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="admin-primaryButton" onClick={saveMetadata} disabled={savingMetadata}>
                {savingMetadata ? "Saving..." : "Save Metadata"}
              </button>

              <button type="button" className="admin-navItem" onClick={() => setEditingFile(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeaturedCardsManager() {
  const emptyCard = {
    sourceType: "",
    sourceId: "",
    title: "",
    description: "",
    image: "",
    link: "",
  };

  const [featuredConfig, setFeaturedConfig] = useState({
    latestNews: emptyCard,
    featuredContent: emptyCard,
    featuredTechnology: emptyCard,
  });

  const [pressReleases, setPressReleases] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [products, setProducts] = useState([]);

  const [searchTerms, setSearchTerms] = useState({
    latestNews: "",
    featuredContent: "",
    featuredTechnology: "",
  });

  const [saving, setSaving] = useState(false);

  const cardLabels = {
    latestNews: "Latest News",
    featuredContent: "Featured Content",
    featuredTechnology: "Featured Technology",
  };

  const loadFeaturedConfig = async () => {
    const snap = await getDoc(doc(db, "siteCustomization", "featuredCards"));

    if (snap.exists()) {
      setFeaturedConfig((prev) => ({
        ...prev,
        ...snap.data(),
      }));
    }
  };

  const loadOptions = async () => {
    const metadataSnap = await getDocs(collection(db, "documentMetadata"));

    const metadataRows = metadataSnap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    const releaseRows = metadataRows
      .filter((item) => item.type === "pressRelease")
      .sort((a, b) => new Date(b.date || "1900-01-01") - new Date(a.date || "1900-01-01"));

    const metadataContentRows = metadataRows.filter((item) => {
      const type = item.type;
      const folder = item.folder;

      return (
        type === "article" ||
        type === "blog" ||
        folder === "brochures" ||
        folder === "catalogs" ||
        folder === "whitepapers" ||
        type === "brochure" ||
        type === "catalog" ||
        type === "whitepaper"
      );
    });

    const fetchStorageKnowledgeCenterFolder = async (folderName) => {
      const folderRef = ref(storage, folderName);
      const folderList = await listAll(folderRef);

      const rows = await Promise.all(
        folderList.items.map(async (itemRef) => {
          const [url, metadata] = await Promise.all([getDownloadURL(itemRef), getMetadata(itemRef)]);

          const fileName = itemRef.name;
          const storagePath = `${folderName}/${fileName}`;
          const docSlug = makeKnowledgeCenterDocSlug(fileName);

          return {
            id: `storage-${folderName}-${fileName}`,
            type: getKnowledgeCenterTypeFromFolder(folderName),
            folder: folderName,
            title: formatKnowledgeCenterName(fileName),
            fileName,
            storagePath,
            url,
            image: "",
            description: "",
            link: `/knowledge-center?doc=${docSlug}`,
            createdAt: metadata?.timeCreated ? new Date(metadata.timeCreated) : new Date(0),
          };
        })
      );

      return rows;
    };

    const storageContentRows = (
      await Promise.all([
        fetchStorageKnowledgeCenterFolder("brochures"),
        fetchStorageKnowledgeCenterFolder("catalogs"),
        fetchStorageKnowledgeCenterFolder("whitepapers"),
      ])
    ).flat();

    const contentRows = [...metadataContentRows, ...storageContentRows]
      .filter((item, index, arr) => {
        const key = item.storagePath || item.url || item.id;
        return arr.findIndex((x) => (x.storagePath || x.url || x.id) === key) === index;
      })
      .sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt?.toDate?.() || a.createdAt || "1900-01-01");
        const dateB = new Date(b.date || b.createdAt?.toDate?.() || b.createdAt || "1900-01-01");
        return dateB - dateA;
      });

    const productSnap = await getDocs(collection(db, "products"));

    const productRows = productSnap.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((p) => (p["Include Y or N"] || "").trim().toUpperCase() !== "N")
      .sort((a, b) => (a.product || a.id).localeCompare(b.product || b.id));

    setPressReleases(releaseRows);
    setContentItems(contentRows);
    setProducts(productRows);
  };

  useEffect(() => {
    loadFeaturedConfig();
    loadOptions();
  }, []);

  const getOptionsForSlot = (slot) => {
    if (slot === "latestNews") return pressReleases;
    if (slot === "featuredTechnology") return products;
    return contentItems;
  };

  const getItemLabel = (item) => item.title || item.product || item.fileName || item.name || item.id;

  const getItemMeta = (item) => {
    if (item.type === "blog") return "Blog";
    if (item.type === "article") return "Article";
    if (item.type === "pressRelease") return "Press Release";
    if (item.folder === "brochures" || item.type === "brochure") return "Brochure";
    if (item.folder === "catalogs" || item.type === "catalog") return "Catalog";
    if (item.folder === "whitepapers" || item.type === "whitepaper") return "Whitepaper";
    if (item.product || item.productFamily) return "Product";
    return item.type || item.folder || "Item";
  };

  const getFilteredOptions = (slot) => {
    const term = searchTerms[slot].toLowerCase().trim();
    const options = getOptionsForSlot(slot);

    if (!term) return options.slice(0, 12);

    return options
      .filter((item) => {
        const text = [
          item.title,
          item.product,
          item.fileName,
          item.name,
          item.description,
          item.folder,
          item.type,
          item.productFamily,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return text.includes(term);
      })
      .slice(0, 20);
  };

  const buildCardFromSelection = (slot, selectedId) => {
    const options = getOptionsForSlot(slot);
    const selected = options.find((item) => item.id === selectedId);

    if (!selected) return;

    let card = emptyCard;

    if (slot === "latestNews") {
      const docSlug = makeKnowledgeCenterDocSlug(selected.fileName || selected.storagePath || selected.id);

      card = {
        sourceType: "pressRelease",
        sourceId: selected.id,
        title: selected.title || selected.fileName || "Press Release",
        description: selected.description || "",
        image: selected.image || "",
        link: `/knowledge-center?doc=${docSlug}`,
      };
    }

    if (slot === "featuredContent") {
      const isBlog = selected.type === "blog";
      const isArticle = selected.type === "article";
      const isKnowledgeCenterDoc =
        selected.folder === "brochures" ||
        selected.folder === "catalogs" ||
        selected.folder === "whitepapers" ||
        selected.type === "brochure" ||
        selected.type === "catalog" ||
        selected.type === "whitepaper";

      const docSlug = makeKnowledgeCenterDocSlug(selected.fileName || selected.storagePath || selected.id);

      card = {
        sourceType: selected.type || selected.folder || "document",
        sourceId: selected.id,
        title: selected.title || selected.fileName || "Featured Content",
        description: selected.description || truncateText(stripHtml(selected.bodyHtml || ""), 100),
        image: selected.image || selected.logo || "",
        link: isBlog
          ? `/blog/${selected.slug}`
          : isArticle
            ? selected.link
            : isKnowledgeCenterDoc
              ? `/knowledge-center?doc=${docSlug}`
              : selected.url || "",
      };
    }

    if (slot === "featuredTechnology") {
      const productSlug = selected.slug || selected.id;

      card = {
        sourceType: "product",
        sourceId: selected.id,
        title: selected.product || selected.name || selected.id,
        description:
          selected.headline ||
          selected.subhead ||
          truncateText(selected.text || selected.section1Description || "", 100),
        image: getProductImage(selected),
        link: `/${productSlug}`,
      };
    }

    setFeaturedConfig((prev) => ({
      ...prev,
      [slot]: card,
    }));
  };

  const updateCardField = (slot, field, value) => {
    setFeaturedConfig((prev) => ({
      ...prev,
      [slot]: {
        ...prev[slot],
        [field]: value,
      },
    }));
  };

  const updateSearchTerm = (slot, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [slot]: value,
    }));
  };

  const saveFeaturedCards = async () => {
    setSaving(true);

    try {
      await setDoc(
        doc(db, "siteCustomization", "featuredCards"),
        {
          ...featuredConfig,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      alert("Featured cards saved.");
    } catch (err) {
      console.error("Error saving featured cards:", err);
      alert("Could not save featured cards.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Featured Homepage / News Cards</h3>

      <p className="admin-muted" style={{ marginBottom: 18 }}>
        Search and select the source item, then override the image, description, or link before publishing.
      </p>

      {["latestNews", "featuredContent", "featuredTechnology"].map((slot) => {
        const card = featuredConfig[slot] || emptyCard;
        const options = getFilteredOptions(slot);

        return (
          <div
            key={slot}
            style={{
              border: "1px solid #eee",
              padding: 16,
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            <h3 style={{ marginTop: 0 }}>{cardLabels[slot]}</h3>

            <label className="admin-muted">Search Items</label>
            <input
              className="admin-input"
              type="text"
              value={searchTerms[slot]}
              onChange={(e) => updateSearchTerm(slot, e.target.value)}
              placeholder={
                slot === "latestNews"
                  ? "Search press releases..."
                  : slot === "featuredContent"
                    ? "Search articles, blogs, brochures, catalogs, or whitepapers..."
                    : "Search products..."
              }
              style={{ width: "100%", marginBottom: 12 }}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: 10,
                marginBottom: 16,
                maxHeight: 360,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {options.map((item) => {
                const selected = card.sourceId === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => buildCardFromSelection(slot, item.id)}
                    style={{
                      textAlign: "left",
                      border: selected ? "2px solid #D54F28" : "1px solid #ddd",
                      background: selected ? "#fff7f3" : "white",
                      borderRadius: 8,
                      padding: 10,
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#D54F28",
                        fontWeight: "bold",
                        marginBottom: 4,
                      }}
                    >
                      {getItemMeta(item)}
                    </div>

                    <div
                      style={{
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: 6,
                      }}
                    >
                      {getItemLabel(item)}
                    </div>

                    {(item.date || item.productFamily || item.folder) && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#777",
                        }}
                      >
                        {item.date || item.productFamily || item.folder}
                      </div>
                    )}
                  </button>
                );
              })}

              {options.length === 0 && <p className="admin-muted">No matching items found.</p>}
            </div>

            <label className="admin-muted">Card Title</label>
            <input
              className="admin-input"
              type="text"
              value={card.title || ""}
              onChange={(e) => updateCardField(slot, "title", e.target.value)}
              style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="admin-muted">Description</label>
            <textarea
              className="admin-input"
              value={card.description || ""}
              onChange={(e) => updateCardField(slot, "description", e.target.value)}
              rows={3}
              style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="admin-muted">Image URL</label>
            <input
              className="admin-input"
              type="text"
              value={card.image || ""}
              onChange={(e) => updateCardField(slot, "image", e.target.value)}
              placeholder="Paste image URL here"
              style={{ width: "100%", marginBottom: 12 }}
            />

            {card.image && (
              <img
                src={card.image}
                alt=""
                style={{
                  width: 160,
                  maxHeight: 100,
                  objectFit: "cover",
                  borderRadius: 6,
                  marginBottom: 12,
                  display: "block",
                }}
              />
            )}

            <label className="admin-muted">Button Link</label>
            <input
              className="admin-input"
              type="text"
              value={card.link || ""}
              onChange={(e) => updateCardField(slot, "link", e.target.value)}
              style={{ width: "100%", marginBottom: 12 }}
            />
          </div>
        );
      })}

      <button type="button" className="admin-primaryButton" onClick={saveFeaturedCards} disabled={saving}>
        {saving ? "Saving..." : "Save Featured Cards"}
      </button>
    </div>
  );
}

function BlogManager() {
  const editorRef = useRef(null);
  const [htmlInsert, setHtmlInsert] = useState("");

  const emptyBlogForm = {
    title: "",
    date: "",
    image: "",

    author: "",
    authorTitle: "",
    excerpt: "",

    relatedLinksText: "",

    seoTitle: "",
    metaDescription: "",
    seoKeywords: "",
    aiKeywords: "",

    canonicalUrl: "",

    socialTitle: "",
    socialDescription: "",
    socialImage: "",

    bodyHtml: "",
    createdAt: null,
  };

  const [blogs, setBlogs] = useState([]);
  const [blogForm, setBlogForm] = useState(emptyBlogForm);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [savingBlog, setSavingBlog] = useState(false);

  const parseRelatedLinks = (value = "") =>
    String(value)
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separatorIndex = line.indexOf("|");

        if (separatorIndex === -1) {
          return {
            label: line,
            url: line,
          };
        }

        return {
          label: line.slice(0, separatorIndex).trim(),
          url: line.slice(separatorIndex + 1).trim(),
        };
      })
      .filter((item) => item.url);

  const currentSlug = makeBlogSlug(blogForm.title);
  const blogUrlPreview = currentSlug ? `/blog/${currentSlug}` : "/blog/blog-title";

  const loadBlogs = async () => {
    setLoadingBlogs(true);

    try {
      const snap = await getDocs(collection(db, "documentMetadata"));

      const rows = snap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((item) => item.type === "blog")
        .sort((a, b) => {
          return new Date(b.date || "1900-01-01") - new Date(a.date || "1900-01-01");
        });

      setBlogs(rows);
    } catch (err) {
      console.error("Error loading blogs:", err);
      alert("Could not load blogs.");
    } finally {
      setLoadingBlogs(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = blogForm.bodyHtml || "";
    }
  }, [editingBlogId]);

  const handleBlogChange = (field, value) => {
    setBlogForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const insertHtmlIntoEditor = () => {
    if (!htmlInsert.trim()) return;

    const cleanHtml = sanitizeBlogHtml(htmlInsert);

    if (editorRef.current) {
      editorRef.current.innerHTML += cleanHtml;
      handleBlogChange("bodyHtml", editorRef.current.innerHTML);
    }

    setHtmlInsert("");
  };

  const resetBlogForm = () => {
    setBlogForm(emptyBlogForm);
    setEditingBlogId(null);
    setSelectedImageFile(null);

    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
  };

  const uploadBlogImageIfNeeded = async () => {
    if (!selectedImageFile) return blogForm.image || "";

    const safeFileName = selectedImageFile.name.replace(/[^a-zA-Z0-9.\-_]/g, "-");
    const imageRef = ref(storage, `blogImages/${Date.now()}-${safeFileName}`);

    await uploadBytes(imageRef, selectedImageFile);
    return await getDownloadURL(imageRef);
  };

  const saveBlog = async () => {
    const title = blogForm.title.trim();
    const date = blogForm.date;
    const rawHtml = editorRef.current?.innerHTML || "";
    const bodyHtml = sanitizeBlogHtml(rawHtml);
    const slug = makeBlogSlug(title);

    const relatedLinks = parseRelatedLinks(blogForm.relatedLinksText);

    const seoTitle = blogForm.seoTitle.trim() || `${title} | Go!Foton`;

    const metaDescription =
      blogForm.metaDescription.trim() || blogForm.excerpt.trim() || stripHtml(bodyHtml).slice(0, 158);

    const canonicalUrl = blogForm.canonicalUrl.trim() || `https://gofoton.com/blog/${slug}`;

    if (!title) {
      alert("Blog title is required.");
      return;
    }

    if (!date) {
      alert("Blog date is required.");
      return;
    }

    if (!bodyHtml || bodyHtml === "<br>") {
      alert("Blog content is required.");
      return;
    }

    if (!slug) {
      alert("Could not generate a valid URL from this title.");
      return;
    }

    setSavingBlog(true);

    try {
      const imageUrl = await uploadBlogImageIfNeeded();
      const blogId = `blog-${slug}`;

      await setDoc(
        doc(db, "documentMetadata", blogId),
        {
          type: "blog",
          folder: "blogs",
          title,
          slug,
          date,
          image: imageUrl,

          author: blogForm.author.trim(),

          authorTitle: blogForm.authorTitle.trim(),

          excerpt: blogForm.excerpt.trim(),

          relatedLinks,

          seoTitle,

          metaDescription,

          seoKeywords: blogForm.seoKeywords.trim(),

          aiKeywords: blogForm.aiKeywords.trim(),

          canonicalUrl,

          socialTitle: blogForm.socialTitle.trim() || seoTitle,

          socialDescription: blogForm.socialDescription.trim() || metaDescription,

          socialImage: blogForm.socialImage.trim() || imageUrl,

          bodyHtml,
          url: `/blog/${slug}`,
          updatedAt: serverTimestamp(),
          createdAt: editingBlogId ? blogForm.createdAt || serverTimestamp() : serverTimestamp(),
        },
        { merge: true }
      );

      resetBlogForm();
      loadBlogs();
      alert("Blog saved.");
    } catch (err) {
      console.error("Error saving blog:", err);
      alert("Could not save blog.");
    } finally {
      setSavingBlog(false);
    }
  };

  const editBlog = (blog) => {
    setEditingBlogId(blog.id);
    setSelectedImageFile(null);
    setBlogForm({
      title: blog.title || "",

      date: blog.date || "",

      image: blog.image || "",

      author: blog.author || "",

      authorTitle: blog.authorTitle || "",

      excerpt: blog.excerpt || "",

      relatedLinksText: Array.isArray(blog.relatedLinks)
        ? blog.relatedLinks.map((item) => `${item.label || item.url || ""} | ${item.url || ""}`).join("\n")
        : "",

      seoTitle: blog.seoTitle || "",

      metaDescription: blog.metaDescription || "",

      seoKeywords: blog.seoKeywords || "",

      aiKeywords: blog.aiKeywords || "",

      canonicalUrl: blog.canonicalUrl || "",

      socialTitle: blog.socialTitle || "",

      socialDescription: blog.socialDescription || "",

      socialImage: blog.socialImage || "",

      bodyHtml: blog.bodyHtml || "",

      createdAt: blog.createdAt || null,
    });
  };

  const deleteBlog = async (blogId) => {
    const confirmed = window.confirm("Delete this blog?");
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "documentMetadata", blogId));

      if (editingBlogId === blogId) {
        resetBlogForm();
      }

      loadBlogs();
    } catch (err) {
      console.error("Error deleting blog:", err);
      alert("Could not delete blog.");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #eee",
        padding: 16,
        borderRadius: 8,
        marginTop: 28,
      }}
    >
      <h3 style={{ marginTop: 0 }}>{editingBlogId ? "Edit Blog" : "Add Blog"}</h3>

      <label className="admin-muted">Blog Title</label>
      <input
        className="admin-input"
        type="text"
        value={blogForm.title}
        onChange={(e) => handleBlogChange("title", e.target.value)}
        placeholder="Example Blog Title"
        style={{ width: "100%", marginBottom: 8 }}
      />

      <div className="admin-muted" style={{ fontSize: 13, marginBottom: 14 }}>
        URL preview: <strong>{blogUrlPreview}</strong>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 14,
          marginBottom: 14,
        }}
      >
        <div>
          <label className="admin-muted">Author</label>

          <input
            className="admin-input"
            type="text"
            value={blogForm.author}
            onChange={(e) => handleBlogChange("author", e.target.value)}
            placeholder="John Smith"
            style={{
              width: "100%",
              boxSizing: "border-box",
              marginTop: 6,
            }}
          />
        </div>

        <div>
          <label className="admin-muted">Author Title / Role</label>

          <input
            className="admin-input"
            type="text"
            value={blogForm.authorTitle}
            onChange={(e) => handleBlogChange("authorTitle", e.target.value)}
            placeholder="Director of Engineering"
            style={{
              width: "100%",
              boxSizing: "border-box",
              marginTop: 6,
            }}
          />
        </div>
      </div>

      <label className="admin-muted">Blog Excerpt</label>

      <textarea
        className="admin-input"
        value={blogForm.excerpt}
        onChange={(e) => handleBlogChange("excerpt", e.target.value)}
        maxLength={300}
        rows={3}
        placeholder="Short summary used for cards, previews, and metadata fallbacks."
        style={{
          width: "100%",
          marginBottom: 6,
          boxSizing: "border-box",
        }}
      />

      <div
        className="admin-muted"
        style={{
          fontSize: 12,
          marginBottom: 16,
        }}
      >
        {blogForm.excerpt.length}/300
      </div>

      <label className="admin-muted">Blog Image</label>
      <input
        className="admin-input"
        type="file"
        accept="image/*"
        onChange={(e) => setSelectedImageFile(e.target.files?.[0] || null)}
        style={{ width: "100%", marginBottom: 10 }}
      />

      {blogForm.image && !selectedImageFile && (
        <div style={{ marginBottom: 14 }}>
          <img
            src={blogForm.image}
            alt=""
            style={{
              width: 160,
              maxHeight: 100,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        </div>
      )}

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 10,
          padding: 16,
          marginBottom: 18,
          background: "#fafafa",
        }}
      >
        <h4 style={{ marginTop: 0 }}>Related Information</h4>

        <p
          className="admin-muted"
          style={{
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          One link per line using: Link Label | URL
        </p>

        <textarea
          className="admin-input"
          value={blogForm.relatedLinksText}
          onChange={(e) => handleBlogChange("relatedLinksText", e.target.value)}
          rows={5}
          placeholder={"Fiber Panels | /fiber-panels\nAI Data Center Solutions | /solutions/ai"}
          style={{
            width: "100%",
            boxSizing: "border-box",
          }}
        />
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 18,
          marginBottom: 20,
          background: "#fff",
        }}
      >
        <h3 style={{ marginTop: 0 }}>SEO, Search & AI Metadata</h3>

        <p
          className="admin-muted"
          style={{
            fontSize: 13,
            marginBottom: 18,
          }}
        >
          These values can be generated externally or written manually, then edited before publishing.
        </p>

        <label className="admin-muted">SEO Page Title</label>

        <input
          className="admin-input"
          type="text"
          maxLength={70}
          value={blogForm.seoTitle}
          onChange={(e) => handleBlogChange("seoTitle", e.target.value)}
          placeholder={`${blogForm.title || "Blog Title"} | Go!Foton`}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 6,
          }}
        />

        <div
          className="admin-muted"
          style={{
            fontSize: 12,
            marginBottom: 14,
          }}
        >
          {blogForm.seoTitle.length}/70
        </div>

        <label className="admin-muted">Meta Description</label>

        <textarea
          className="admin-input"
          maxLength={160}
          value={blogForm.metaDescription}
          onChange={(e) => handleBlogChange("metaDescription", e.target.value)}
          rows={3}
          placeholder="Search-engine description, ideally about 150-160 characters."
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 6,
          }}
        />

        <div
          className="admin-muted"
          style={{
            fontSize: 12,
            marginBottom: 14,
          }}
        >
          {blogForm.metaDescription.length}/160
        </div>

        <label className="admin-muted">SEO Keywords</label>

        <textarea
          className="admin-input"
          value={blogForm.seoKeywords}
          onChange={(e) => handleBlogChange("seoKeywords", e.target.value)}
          rows={3}
          placeholder="fiber optics, AI data center fiber, high density connectivity"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 14,
          }}
        />

        <label className="admin-muted">AI / Semantic Keywords</label>

        <textarea
          className="admin-input"
          value={blogForm.aiKeywords}
          onChange={(e) => handleBlogChange("aiKeywords", e.target.value)}
          rows={4}
          placeholder="Concepts and terms that help AI/search systems understand this content."
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 14,
          }}
        />

        <label className="admin-muted">Canonical URL</label>

        <input
          className="admin-input"
          type="text"
          value={blogForm.canonicalUrl}
          onChange={(e) => handleBlogChange("canonicalUrl", e.target.value)}
          placeholder={`https://gofoton.com${blogUrlPreview}`}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 18,
          }}
        />

        <h4>Social Sharing</h4>

        <label className="admin-muted">Social Share Title</label>

        <input
          className="admin-input"
          type="text"
          value={blogForm.socialTitle}
          onChange={(e) => handleBlogChange("socialTitle", e.target.value)}
          placeholder="Defaults to SEO title"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 14,
          }}
        />

        <label className="admin-muted">Social Share Description</label>

        <textarea
          className="admin-input"
          value={blogForm.socialDescription}
          onChange={(e) => handleBlogChange("socialDescription", e.target.value)}
          rows={3}
          placeholder="Defaults to meta description"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 14,
          }}
        />

        <label className="admin-muted">Social Share Image URL</label>

        <input
          className="admin-input"
          type="text"
          value={blogForm.socialImage}
          onChange={(e) => handleBlogChange("socialImage", e.target.value)}
          placeholder="Defaults to the Blog Image"
          style={{
            width: "100%",
            boxSizing: "border-box",
          }}
        />
      </div>

      <label className="admin-muted">Blog Content</label>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => handleBlogChange("bodyHtml", e.currentTarget.innerHTML)}
        style={{
          minHeight: 260,
          border: "1px solid #ccc",
          borderRadius: 6,
          padding: 12,
          background: "white",
          marginBottom: 10,
          lineHeight: 1.6,
          outline: "none",
        }}
      />

      <p className="admin-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        You can paste from Word. Basic formatting like paragraphs, headings, bold, italics, bullet points, numbered
        lists, links, and tables will be preserved.
      </p>

      <div
        style={{
          border: "1px solid #eee",
          padding: 12,
          borderRadius: 6,
          marginBottom: 16,
          background: "#fafafa",
        }}
      >
        <label className="admin-muted">Insert HTML Block</label>

        <textarea
          className="admin-input"
          value={htmlInsert}
          onChange={(e) => setHtmlInsert(e.target.value)}
          placeholder="Paste table HTML here, then click Insert HTML"
          rows={6}
          style={{
            width: "100%",
            marginTop: 8,
            marginBottom: 10,
            fontFamily: "monospace",
            fontSize: 13,
          }}
        />

        <button type="button" className="admin-navItem" onClick={insertHtmlIntoEditor}>
          Insert HTML
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
        <button type="button" className="admin-primaryButton" onClick={saveBlog} disabled={savingBlog}>
          {savingBlog ? "Saving..." : editingBlogId ? "Save Blog Changes" : "Add Blog"}
        </button>

        {editingBlogId && (
          <button type="button" className="admin-navItem" onClick={resetBlogForm}>
            Cancel Edit
          </button>
        )}
      </div>

      <h3>Existing Blogs</h3>

      {loadingBlogs ? (
        <p className="admin-muted">Loading blogs...</p>
      ) : blogs.length === 0 ? (
        <p className="admin-muted">No blogs have been added yet.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
              <th style={{ padding: 10 }}>Image</th>
              <th style={{ padding: 10 }}>Title</th>
              <th style={{ padding: 10 }}>Date</th>
              <th style={{ padding: 10 }}>URL</th>
              <th style={{ padding: 10 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {blogs.map((blog) => (
              <tr key={blog.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                <td style={{ padding: 10 }}>
                  {blog.image ? (
                    <img
                      src={blog.image}
                      alt=""
                      style={{
                        width: 80,
                        maxHeight: 50,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  ) : (
                    "—"
                  )}
                </td>

                <td style={{ padding: 10 }}>
                  <strong>{blog.title}</strong>
                </td>

                <td style={{ padding: 10 }}>{blog.date || "—"}</td>

                <td style={{ padding: 10 }}>
                  <a href={`/blog/${blog.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: "#0072b1" }}>
                    /blog/{blog.slug}
                  </a>
                </td>

                <td style={{ padding: 10 }}>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => editBlog(blog)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#333",
                        cursor: "pointer",
                        fontWeight: "bold",
                        padding: 0,
                      }}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => deleteBlog(blog.id)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#D54F28",
                        cursor: "pointer",
                        fontWeight: "bold",
                        padding: 0,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AIResponsesTab() {
  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState("recent");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPage, setSelectedPage] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    const chatsRef = collection(db, "aiChats");

    const q =
      filter === "recent"
        ? query(chatsRef, orderBy("createdAt", "desc"), limit(100))
        : query(chatsRef, orderBy("createdAt", "asc"), limit(100));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((chat) => chat.isJunk !== true);

        setChats(rows);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading AI chats:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [filter]);

  const handleMarkJunk = async (chatId) => {
    try {
      await updateDoc(doc(db, "aiChats", chatId), {
        isJunk: true,
        junkedAt: serverTimestamp(),
      });

      setChats((prev) => prev.filter((chat) => chat.id !== chatId));
    } catch (error) {
      console.error("Error marking chat as junk:", error);
      alert("Could not mark as junk. Try again.");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return "Unknown";
    return timestamp.toDate().toLocaleString();
  };

  const uniquePages = useMemo(() => {
    const pages = chats.map((chat) => chat.page).filter(Boolean);

    return ["all", ...Array.from(new Set(pages))];
  }, [chats]);

  const filteredChats = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return chats.filter((chat) => {
      const matchesPage = selectedPage === "all" || chat.page === selectedPage;

      const searchableText = [
        chat.userMessage,
        chat.assistantReply,
        chat.page,
        ...(Array.isArray(chat.matchedProducts) ? chat.matchedProducts : []),
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch = !term || searchableText.includes(term);

      return matchesPage && matchesSearch;
    });
  }, [chats, selectedPage, searchTerm]);

  return (
    <div className="admin-card admin-ai-card">
      <div className="admin-ai-header">
        <div>
          <h2 className="admin-h2">AI Responses</h2>
          <p className="admin-muted">Review user prompts, assistant replies, matched products, and page activity.</p>
        </div>

        <div className="admin-ai-count">{filteredChats.length} shown</div>
      </div>

      <div className="admin-ai-filters">
        <select className="admin-input" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="recent">Most Recent</option>
          <option value="oldest">Oldest First</option>
        </select>

        <select className="admin-input" value={selectedPage} onChange={(e) => setSelectedPage(e.target.value)}>
          {uniquePages.map((page) => (
            <option key={page} value={page}>
              {page === "all" ? "All Pages" : page}
            </option>
          ))}
        </select>

        <input
          className="admin-input admin-searchInput"
          type="text"
          placeholder="Search messages, replies, pages, or products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="admin-muted">Loading AI responses...</p>
      ) : filteredChats.length === 0 ? (
        <p className="admin-muted">No AI responses found.</p>
      ) : (
        <div className="admin-tableWrap">
          <table className="admin-ai-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Page</th>
                <th>User Message</th>
                <th>Assistant Reply</th>
                <th>Matched Products</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredChats.map((chat) => (
                <tr key={chat.id}>
                  <td className="admin-dateCell">{formatDate(chat.createdAt)}</td>

                  <td className="admin-pageCell">
                    {chat.page ? (
                      <a href={chat.page} target="_blank" rel="noopener noreferrer">
                        {chat.page}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td>{chat.userMessage || "—"}</td>

                  <td>{chat.assistantReply || "—"}</td>

                  <td>
                    {Array.isArray(chat.matchedProducts) && chat.matchedProducts.length > 0 ? (
                      <div className="admin-productTags">
                        {chat.matchedProducts.map((product, index) => (
                          <span className="admin-productTag" key={`${chat.id}-${product}-${index}`}>
                            {product}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <button type="button" className="admin-junkButton" onClick={() => handleMarkJunk(chat.id)}>
                      Junk
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AnalyticsTab({ activeRange, onLoadingChange, activeAnalyticsTab, setActiveAnalyticsTab }) {
  const [productViewRows, setProductViewRows] = useState([]);

  const [sessionRows, setSessionRows] = useState([]);
  const [liveSessionRows, setLiveSessionRows] = useState([]);

  const [eventRows, setEventRows] = useState([]);

  const [productFamilies, setProductFamilies] = useState([]);

  const [analyticsProducts, setAnalyticsProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [analyticsLoadSeconds, setAnalyticsLoadSeconds] = useState(0);

  const [exportingAnalytics, setExportingAnalytics] = useState(false);

  const [emailAlerts, setEmailAlerts] = useState([]);

  const [savingAlert, setSavingAlert] = useState(false);

  const [selectedJourneyVisitor, setSelectedJourneyVisitor] = useState(null);
  const [plumaFocusVisitorId, setPlumaFocusVisitorId] = useState("");

  const [selectedAnalyticsPage, setSelectedAnalyticsPage] = useState(null);

  const [journeyCategoryFilter, setJourneyCategoryFilter] = useState("All");

  const [alertForm, setAlertForm] = useState({
    name: "",
    category: "productUpdates",
    frequency: "monthly",
    productFamily: "",
    email: "",
    active: true,
  });

  const [browserExclusions, setBrowserExclusions] = useState([]);

  const [browserLabel, setBrowserLabel] = useState("");

  const [renamingBrowserId, setRenamingBrowserId] = useState("");

  const [renamingBrowserLabel, setRenamingBrowserLabel] = useState("");

  const [savingBrowserExclusion, setSavingBrowserExclusion] = useState(false);

  const [legacyBrowserFlag, setLegacyBrowserFlag] = useState(
    () => localStorage.getItem(LEGACY_ANALYTICS_EXCLUDED_KEY) === "true"
  );

  const [selectedHeatmapPage, setSelectedHeatmapPage] = useState(PRODUCT_HEATMAP_KEY);

  const [heatmapDeviceFilter, setHeatmapDeviceFilter] = useState("all");

  const analyticsTabs = ANALYTICS_TAB_OPTIONS;

  const analyticsReportDataRef = useRef(null);

  const legacyBrowserMigrationAttempted = useRef(false);

  const currentAnalyticsBrowserId = useMemo(() => makePersistentBrowserId(), []);

  const currentBrowserExcluded = browserExclusions.some(
    (item) => item.browserId === currentAnalyticsBrowserId && item.active !== false
  );

  useEffect(() => {
    onLoadingChange?.(loading || eventsLoading || exportingAnalytics);
  }, [loading, eventsLoading, exportingAnalytics, onLoadingChange]);

  useEffect(() => {
    if (!loading && !eventsLoading) {
      setAnalyticsLoadSeconds(0);

      return undefined;
    }

    const startedAt = Date.now();

    setAnalyticsLoadSeconds(0);

    const timer = window.setInterval(() => {
      setAnalyticsLoadSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [loading, eventsLoading]);

  const getDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const selectedRange = ANALYTICS_RANGE_OPTIONS.find((item) => item.key === activeRange) || ANALYTICS_RANGE_OPTIONS[3];

  const periods = useMemo(() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);

    const start = new Date(end);
    start.setDate(start.getDate() - selectedRange.days + 1);

    const previousEnd = new Date(start);
    previousEnd.setDate(previousEnd.getDate() - 1);

    const previousStart = new Date(previousEnd);
    previousStart.setDate(previousStart.getDate() - selectedRange.days + 1);

    return {
      current: { start: getDateKey(start), end: getDateKey(end) },
      previous: { start: getDateKey(previousStart), end: getDateKey(previousEnd) },
    };
  }, [activeRange]);

  useEffect(() => {
    const handleAnalyticsExportRequest = async (event) => {
      if (exportingAnalytics) {
        return;
      }

      const format = event.detail?.format;

      const requestedTab = event.detail?.activeTab || activeAnalyticsTab;

      if (!format) {
        return;
      }

      const reportData = analyticsReportDataRef.current;

      if (!reportData) {
        alert("Analytics data is still loading. Try the export again in a moment.");

        return;
      }

      setExportingAnalytics(true);

      try {
        if (format === "slide") {
          await exportAnalyticsSlide({
            data: reportData,
            sectionKey: requestedTab,
          });

          return;
        }

        if (format === "pdf") {
          await exportAnalyticsPdf({
            data: reportData,
          });
        }
      } catch (error) {
        console.error("Analytics report export failed:", error);

        alert(error.message || "The analytics report could not be exported.");
      } finally {
        setExportingAnalytics(false);
      }
    };

    window.addEventListener(ANALYTICS_EXPORT_EVENT, handleAnalyticsExportRequest);

    return () => {
      window.removeEventListener(ANALYTICS_EXPORT_EVENT, handleAnalyticsExportRequest);
    };
  }, [activeAnalyticsTab, exportingAnalytics]);

  useEffect(() => {
    let cancelled = false;

    const loadAnalytics = async () => {
      setLoading(true);
      setEventsLoading(true);

      /*
        We need both the selected period
        and the immediately preceding
        comparison period.
      */
      const queryStart = periods.previous.start;

      const queryEnd = periods.current.end;

      const viewsQuery = query(
        collection(db, "productViews"),
        where("date", ">=", queryStart),
        where("date", "<=", queryEnd)
      );

      const sessionsQuery = query(
        collection(db, "analyticsSessions"),
        where("date", ">=", queryStart),
        where("date", "<=", queryEnd)
      );

      const eventsQuery = query(
        collection(db, "analyticsEvents"),
        where("date", ">=", queryStart),
        where("date", "<=", queryEnd)
      );

      try {
        /*
          CORE DATA FIRST.

          These are enough to show most
          of Overview immediately.
        */
        const [viewsSnap, productsSnap, alertsSnap, sessionsSnap] = await Promise.all([
          getDocs(viewsQuery),

          getDocs(collection(db, "products")),

          getDocs(collection(db, "analyticsEmailAlerts")),

          getDocs(sessionsQuery),
        ]);

        if (cancelled) {
          return;
        }

        setProductViewRows(
          viewsSnap.docs.map((docSnap) => ({
            id: docSnap.id,

            ...docSnap.data(),
          }))
        );

        setSessionRows(
          sessionsSnap.docs.map((docSnap) => ({
            id: docSnap.id,

            ...docSnap.data(),
          }))
        );

        setAnalyticsProducts(
          productsSnap.docs.map((docSnap) => ({
            id: docSnap.id,

            ...docSnap.data(),
          }))
        );

        const families = [
          ...new Set(productsSnap.docs.map((docSnap) => docSnap.data().productFamily).filter(Boolean)),
        ].sort();

        setProductFamilies(families);

        setEmailAlerts(
          alertsSnap.docs.map((docSnap) => ({
            id: docSnap.id,

            ...docSnap.data(),
          }))
        );

        /*
          The visible dashboard can now
          render even while the much
          larger analyticsEvents query
          continues in the background.
        */
        setLoading(false);

        /*
          HEAVY INTERACTION DATA SECOND.
        */
        const eventsSnap = await getDocs(eventsQuery);

        if (cancelled) {
          return;
        }

        setEventRows(
          eventsSnap.docs.map((docSnap) => ({
            id: docSnap.id,

            ...docSnap.data(),
          }))
        );

        /*
          Browser exclusions are small
          and do not need to block the
          dashboard.
        */
        getDocs(collection(db, "analyticsBrowserExclusions"))
          .then((snapshot) => {
            if (cancelled) {
              return;
            }

            setBrowserExclusions(
              snapshot.docs.map((docSnap) => ({
                id: docSnap.id,

                browserId: docSnap.data().browserId || docSnap.id,

                ...docSnap.data(),
              }))
            );
          })
          .catch((error) => {
            console.warn("Browser exclusions could not be loaded:", error);
          });
      } catch (err) {
        console.error("Error loading analytics:", err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setEventsLoading(false);
        }
      }
    };

    loadAnalytics();

    return () => {
      cancelled = true;
    };
  }, [periods.previous.start, periods.current.end]);

  useEffect(() => {
    if (loading || legacyBrowserMigrationAttempted.current) {
      return;
    }

    legacyBrowserMigrationAttempted.current = true;

    const migrateLegacyBrowserExclusion = async () => {
      const alreadyMigrated = localStorage.getItem(BROWSER_EXCLUSION_MIGRATION_KEY) === "true";

      if (!legacyBrowserFlag || alreadyMigrated) {
        return;
      }

      try {
        const browserName = getBrowserName();

        await setDoc(
          doc(db, "analyticsBrowserExclusions", currentAnalyticsBrowserId),
          {
            browserId: currentAnalyticsBrowserId,

            label: browserName,

            browserName,

            active: true,

            migratedFromLegacyFlag: true,

            createdAt: serverTimestamp(),

            updatedAt: serverTimestamp(),
          },
          {
            merge: true,
          }
        );

        setBrowserExclusions((previous) => [
          ...previous.filter((item) => item.browserId !== currentAnalyticsBrowserId),

          {
            id: currentAnalyticsBrowserId,

            browserId: currentAnalyticsBrowserId,

            label: browserName,

            browserName,

            active: true,

            migratedFromLegacyFlag: true,
          },
        ]);

        localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");

        window.dispatchEvent(new Event(ANALYTICS_EXCLUSION_EVENT));
      } catch (error) {
        console.error("Could not migrate the legacy browser exclusion:", error);
      }
    };

    migrateLegacyBrowserExclusion();
  }, [loading, legacyBrowserFlag, currentAnalyticsBrowserId]);

  useEffect(() => {
    const today = getDateKey(new Date());

    const liveQuery = query(collection(db, "analyticsSessions"), where("date", "==", today));

    const unsubscribe = onSnapshot(
      liveQuery,

      (snapshot) => {
        setLiveSessionRows(
          snapshot.docs.map((docSnap) => ({
            id: docSnap.id,

            ...docSnap.data(),
          }))
        );
      },

      (error) => {
        console.error("Live analytics session listener failed:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  const filterPeriod = (rows, period) =>
    rows.filter((row) => row.date && row.date >= period.start && row.date <= period.end);
  const aggregateProducts = (rows) =>
    Object.values(
      rows.reduce((acc, row) => {
        const key = row.productSlug || row.productName || "unknown";
        if (!acc[key])
          acc[key] = {
            productSlug: row.productSlug || "",
            productName: row.productName || key,
            productFamily: row.productFamily || "",
            views: 0,
            downloads: 0,
            timeOnPageSeconds: 0,
          };
        acc[key].views += Number(row.count || 0);
        acc[key].downloads += Number(row.downloads || 0);
        acc[key].timeOnPageSeconds += Number(row.timeOnPageSeconds || 0);
        return acc;
      }, {})
    );

  const currentProducts = useMemo(
    () => aggregateProducts(filterPeriod(productViewRows, periods.current)),
    [productViewRows, periods]
  );
  const previousProducts = useMemo(
    () => aggregateProducts(filterPeriod(productViewRows, periods.previous)),
    [productViewRows, periods]
  );

  const previousProductMap = useMemo(
    () => Object.fromEntries(previousProducts.map((item) => [item.productSlug || item.productName, item])),
    [previousProducts]
  );

  const productImageMap = useMemo(() => {
    const map = {};

    analyticsProducts.forEach((product) => {
      const slug = product.slug || product.id;
      if (slug) map[slug] = getProductImage(product);
    });

    return map;
  }, [analyticsProducts]);

  const productTotals = useMemo(
    () =>
      currentProducts
        .map((product) => {
          const previous = previousProductMap[product.productSlug || product.productName];
          return { ...product, previousViews: previous?.views || 0, previousDownloads: previous?.downloads || 0 };
        })
        .sort((a, b) => b.views - a.views),
    [currentProducts, previousProductMap]
  );

  const totalProductViews = productTotals.reduce((sum, row) => sum + row.views, 0);
  const totalDownloads = productTotals.reduce((sum, row) => sum + row.downloads, 0);
  const totalTime = productTotals.reduce((sum, row) => sum + row.timeOnPageSeconds, 0);
  const previousViews = previousProducts.reduce((sum, row) => sum + row.views, 0);
  const previousDownloads = previousProducts.reduce((sum, row) => sum + row.downloads, 0);
  const currentSessions = filterPeriod(sessionRows, periods.current);
  const previousSessions = filterPeriod(sessionRows, periods.previous);
  const currentEvents = filterPeriod(eventRows, periods.current);
  const previousEvents = filterPeriod(eventRows, periods.previous);
  const productSlugSet = new Set(analyticsProducts.map((product) => product.slug || product.id).filter(Boolean));

  const buildProductVisitorMap = (events) => {
    const grouped = {};

    events.forEach((event) => {
      if (!event.visitorId) return;

      let productSlug = "";

      if (event.type === "product_view" && event.productSlug) {
        productSlug = String(event.productSlug).replace(/^\/+/, "").split("?")[0];
      }

      if (!productSlug && event.type === "page_view") {
        const pageSlug = String(event.pagePath || "")
          .split("?")[0]
          .replace(/^\/+/, "")
          .replace(/\/+$/, "");

        if (productSlugSet.has(pageSlug)) {
          productSlug = pageSlug;
        }
      }

      if (!productSlug || !productSlugSet.has(productSlug)) {
        return;
      }

      if (!grouped[productSlug]) {
        grouped[productSlug] = new Set();
      }

      grouped[productSlug].add(event.visitorId);
    });

    return Object.fromEntries(
      Object.entries(grouped).map(([productSlug, visitorIds]) => [productSlug, visitorIds.size])
    );
  };

  const currentProductVisitors = buildProductVisitorMap(currentEvents);

  const previousProductVisitors = buildProductVisitorMap(previousEvents);

  const countUniqueVisitors = (rows) => new Set(rows.map((row) => row.visitorId).filter(Boolean)).size;

  const currentVisitors = countUniqueVisitors(currentSessions);
  const previousVisitors = countUniqueVisitors(previousSessions);

  const currentNewVisitors = countUniqueVisitors(currentSessions.filter((row) => row.isNewVisitor === true));
  const previousNewVisitors = countUniqueVisitors(previousSessions.filter((row) => row.isNewVisitor === true));

  const currentReturningVisitors = countUniqueVisitors(currentSessions.filter((row) => row.isNewVisitor === false));
  const previousReturningVisitors = countUniqueVisitors(previousSessions.filter((row) => row.isNewVisitor === false));

  const currentSessionPageViews = currentSessions.reduce((sum, session) => sum + Number(session.pageViewCount || 0), 0);

  const previousSessionPageViews = previousSessions.reduce(
    (sum, session) => sum + Number(session.pageViewCount || 0),
    0
  );

  const currentPageViews = eventsLoading
    ? currentSessionPageViews
    : currentEvents.filter((row) => row.type === "page_view").length;

  const previousPageViews = eventsLoading
    ? previousSessionPageViews
    : previousEvents.filter((row) => row.type === "page_view").length;

  const countBy = (rows, field) =>
    rows.reduce((acc, row) => {
      const key = row[field] || "unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

  const currentSources = countBy(currentSessions, "sourceType");
  const previousSources = countBy(previousSessions, "sourceType");
  const currentDevices = countBy(currentSessions, "deviceCategory");
  const previousDevices = countBy(previousSessions, "deviceCategory");
  const buildAcquisitionSourceRows = (rows) => {
    const grouped = {};

    rows.forEach((row) => {
      const sourceType = row.sourceType || "direct";
      const sourceName = row.sourceName || (sourceType === "direct" ? "Direct" : sourceType);
      const key = `${sourceType}|${sourceName}`;

      if (!grouped[key]) grouped[key] = { key, sourceType, sourceName, sessions: 0, visitorIds: new Set() };

      grouped[key].sessions += 1;
      if (row.visitorId) grouped[key].visitorIds.add(row.visitorId);
    });

    return Object.values(grouped)
      .map((row) => ({
        key: row.key,
        sourceType: row.sourceType,
        sourceName: row.sourceName,
        sessions: row.sessions,
        uniqueVisitors: row.visitorIds.size,
        share: rows.length ? (row.sessions / rows.length) * 100 : 0,
      }))
      .sort((a, b) => b.sessions - a.sessions);
  };

  const currentAcquisitionSources = buildAcquisitionSourceRows(currentSessions);
  const previousAcquisitionSources = buildAcquisitionSourceRows(previousSessions);

  const previousAcquisitionSourceMap = Object.fromEntries(
    previousAcquisitionSources.map((row) => [row.key, row.sessions])
  );

  const buildLandingPageRows = (rows) => {
    const grouped = {};

    rows.forEach((row) => {
      const pagePath = (row.landingPage || "/").split("?")[0] || "/";
      const sourceName = row.sourceName || "Direct";

      if (!grouped[pagePath]) grouped[pagePath] = { pagePath, sessions: 0, visitorIds: new Set(), sourceCounts: {} };

      grouped[pagePath].sessions += 1;
      if (row.visitorId) grouped[pagePath].visitorIds.add(row.visitorId);
      grouped[pagePath].sourceCounts[sourceName] = (grouped[pagePath].sourceCounts[sourceName] || 0) + 1;
    });

    return Object.values(grouped)
      .map((row) => ({
        pagePath: row.pagePath,
        sessions: row.sessions,
        uniqueVisitors: row.visitorIds.size,
        topSource: Object.entries(row.sourceCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "Direct",
      }))
      .sort((a, b) => b.sessions - a.sessions);
  };

  const currentLandingPages = buildLandingPageRows(currentSessions);
  const previousLandingPages = buildLandingPageRows(previousSessions);
  const previousLandingPageMap = Object.fromEntries(previousLandingPages.map((row) => [row.pagePath, row.sessions]));

  const sumActiveTime = (rows) =>
    rows.filter((row) => row.type === "active_time").reduce((sum, row) => sum + Number(row.seconds || 0), 0);

  const countEvents = (rows, type) => rows.filter((row) => row.type === type).length;

  const currentActiveSeconds = sumActiveTime(currentEvents);
  const previousActiveSeconds = sumActiveTime(previousEvents);

  const currentAvgActiveTime = currentSessions.length ? currentActiveSeconds / currentSessions.length : 0;
  const previousAvgActiveTime = previousSessions.length ? previousActiveSeconds / previousSessions.length : 0;

  const currentPagesPerSession = currentSessions.length ? currentPageViews / currentSessions.length : 0;
  const previousPagesPerSession = previousSessions.length ? previousPageViews / previousSessions.length : 0;

  const currentReturnFrequency = currentVisitors ? currentSessions.length / currentVisitors : 0;
  const previousReturnFrequency = previousVisitors ? previousSessions.length / previousVisitors : 0;

  const currentExitRate = currentPageViews ? Math.min(100, (currentSessions.length / currentPageViews) * 100) : 0;
  const previousExitRate = previousPageViews ? Math.min(100, (previousSessions.length / previousPageViews) * 100) : 0;

  const currentButtonClicks = countEvents(currentEvents, "button_click");
  const previousButtonClicks = countEvents(previousEvents, "button_click");

  const currentExternalClicks = countEvents(currentEvents, "external_link_click");
  const previousExternalClicks = countEvents(previousEvents, "external_link_click");

  const currentCopies = countEvents(currentEvents, "copy_to_clipboard");
  const previousCopies = countEvents(previousEvents, "copy_to_clipboard");
  const currentDevToolsSignals = currentEvents.filter(
    (event) => event.type === "developer_tools_detected" || event.type === "developer_tools_shortcut"
  ).length;

  const previousDevToolsSignals = previousEvents.filter(
    (event) => event.type === "developer_tools_detected" || event.type === "developer_tools_shortcut"
  ).length;

  const currentTabOpens = countEvents(currentEvents, "tab_open");
  const previousTabOpens = countEvents(previousEvents, "tab_open");

  const currentFormAbandonments = countEvents(currentEvents, "form_abandonment");
  const previousFormAbandonments = countEvents(previousEvents, "form_abandonment");

  const currentPlumaOpens = countEvents(currentEvents, "pluma_open");
  const previousPlumaOpens = countEvents(previousEvents, "pluma_open");

  const currentPlumaMessages = countEvents(currentEvents, "pluma_message");
  const previousPlumaMessages = countEvents(previousEvents, "pluma_message");

  const currentPlumaThumbsUp = currentEvents.filter(
    (event) => event.type === "pluma_feedback" && event.feedback === "up"
  ).length;
  const previousPlumaThumbsUp = previousEvents.filter(
    (event) => event.type === "pluma_feedback" && event.feedback === "up"
  ).length;

  const currentPlumaThumbsDown = currentEvents.filter(
    (event) => event.type === "pluma_feedback" && event.feedback === "down"
  ).length;
  const previousPlumaThumbsDown = previousEvents.filter(
    (event) => event.type === "pluma_feedback" && event.feedback === "down"
  ).length;

  const currentSearches = countEvents(currentEvents, "search");
  const previousSearches = countEvents(previousEvents, "search");

  const currentZeroSearches = countEvents(currentEvents, "search_zero_results");
  const previousZeroSearches = countEvents(previousEvents, "search_zero_results");

  const currentSearchOpens = countEvents(currentEvents, "search_open");

  const previousSearchOpens = countEvents(previousEvents, "search_open");

  const currentSearchResultClicks = countEvents(currentEvents, "search_result_click");

  const previousSearchResultClicks = countEvents(previousEvents, "search_result_click");

  const currentSearchViewAllClicks = countEvents(currentEvents, "search_view_all_click");

  const previousSearchViewAllClicks = countEvents(previousEvents, "search_view_all_click");

  const currentSearchCTR = currentSearches ? (currentSearchResultClicks / currentSearches) * 100 : 0;

  const previousSearchCTR = previousSearches ? (previousSearchResultClicks / previousSearches) * 100 : 0;

  const currentSearchOpenToSearchRate = currentSearchOpens ? (currentSearches / currentSearchOpens) * 100 : 0;

  const previousSearchOpenToSearchRate = previousSearchOpens ? (previousSearches / previousSearchOpens) * 100 : 0;

  const currentEmailClicks = countEvents(currentEvents, "email_click");
  const previousEmailClicks = countEvents(previousEvents, "email_click");

  const currentPhoneClicks = countEvents(currentEvents, "phone_click");
  const previousPhoneClicks = countEvents(previousEvents, "phone_click");

  const currentFormSubmissions = countEvents(currentEvents, "form_submission");
  const previousFormSubmissions = countEvents(previousEvents, "form_submission");

  const currentPlumaFeedbackTotal = currentPlumaThumbsUp + currentPlumaThumbsDown;

  const previousPlumaFeedbackTotal = previousPlumaThumbsUp + previousPlumaThumbsDown;

  const currentPlumaSatisfaction = currentPlumaFeedbackTotal
    ? (currentPlumaThumbsUp / currentPlumaFeedbackTotal) * 100
    : 0;

  const previousPlumaSatisfaction = previousPlumaFeedbackTotal
    ? (previousPlumaThumbsUp / previousPlumaFeedbackTotal) * 100
    : 0;

  const getSearchTerm = (event) => String(event.searchTerm || event.query || event.term || event.value || "").trim();

  const buildSearchTermRows = (rows) => {
    const grouped = {};

    rows
      .filter((event) => event.type === "search" || event.type === "search_zero_results")
      .forEach((event) => {
        const term = getSearchTerm(event);

        if (!term) return;

        const key = term.toLowerCase();

        if (!grouped[key]) {
          grouped[key] = {
            term,
            searches: 0,
            zeroResults: 0,
          };
        }

        if (event.type === "search") {
          grouped[key].searches += 1;
        }

        if (event.type === "search_zero_results") {
          grouped[key].zeroResults += 1;
        }
      });

    return Object.values(grouped)
      .sort((a, b) => b.searches + b.zeroResults - (a.searches + a.zeroResults))
      .slice(0, 15);
  };

  const currentSearchTermRows = buildSearchTermRows(currentEvents);

  const buildSearchResultClickRows = (rows) => {
    const grouped = {};

    rows
      .filter((event) => event.type === "search_result_click")
      .forEach((event) => {
        const productName = String(event.productName || event.productSlug || "Unknown Product").trim();

        const productSlug = String(event.productSlug || "").trim();

        const key = productSlug || productName.toLowerCase();

        if (!grouped[key]) {
          grouped[key] = {
            label: productName,
            productSlug,
            value: 0,
          };
        }

        grouped[key].value += 1;
      });

    return Object.values(grouped)
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  };

  const currentSearchResultClickRows = buildSearchResultClickRows(currentEvents);

  /* =====================================================
   TOOLS / PART NUMBER LOOKUP ANALYTICS
   ===================================================== */

  const getPartLookupEvents = (rows) => rows.filter((event) => String(event.type || "").startsWith("part_lookup_"));

  const currentPartLookupEvents = getPartLookupEvents(currentEvents);

  const previousPartLookupEvents = getPartLookupEvents(previousEvents);

  const countToolEvent = (rows, type) => rows.filter((event) => event.type === type).length;

  const currentPartLookupOpens = countToolEvent(currentPartLookupEvents, "part_lookup_open");

  const previousPartLookupOpens = countToolEvent(previousPartLookupEvents, "part_lookup_open");

  const currentPartLookupStarts = countToolEvent(currentPartLookupEvents, "part_lookup_start");

  const previousPartLookupStarts = countToolEvent(previousPartLookupEvents, "part_lookup_start");

  const currentPartLookupCompletions = countToolEvent(currentPartLookupEvents, "part_lookup_complete");

  const previousPartLookupCompletions = countToolEvent(previousPartLookupEvents, "part_lookup_complete");

  const currentPartLookupInvalid = countToolEvent(currentPartLookupEvents, "part_lookup_invalid");

  const previousPartLookupInvalid = countToolEvent(previousPartLookupEvents, "part_lookup_invalid");

  const currentPartLookupCopies = countToolEvent(currentPartLookupEvents, "part_lookup_copy");

  const previousPartLookupCopies = countToolEvent(previousPartLookupEvents, "part_lookup_copy");

  const currentPartLookupProductClicks = countToolEvent(currentPartLookupEvents, "part_lookup_product_click");

  const previousPartLookupProductClicks = countToolEvent(previousPartLookupEvents, "part_lookup_product_click");

  const currentPartLookupDatasheetClicks = countToolEvent(currentPartLookupEvents, "part_lookup_datasheet_click");

  const previousPartLookupDatasheetClicks = countToolEvent(previousPartLookupEvents, "part_lookup_datasheet_click");

  const currentPartLookupClears = countToolEvent(currentPartLookupEvents, "part_lookup_clear");

  const previousPartLookupClears = countToolEvent(previousPartLookupEvents, "part_lookup_clear");

  const currentPartLookupOptionSelections = countToolEvent(currentPartLookupEvents, "part_lookup_option_select");

  const previousPartLookupOptionSelections = countToolEvent(previousPartLookupEvents, "part_lookup_option_select");

  const currentPartLookupSchemaSelections = countToolEvent(currentPartLookupEvents, "part_lookup_schema_select");

  const previousPartLookupSchemaSelections = countToolEvent(previousPartLookupEvents, "part_lookup_schema_select");

  const currentPartLookupCompletionRate = currentPartLookupStarts
    ? (currentPartLookupCompletions / currentPartLookupStarts) * 100
    : 0;

  const previousPartLookupCompletionRate = previousPartLookupStarts
    ? (previousPartLookupCompletions / previousPartLookupStarts) * 100
    : 0;

  const currentPartLookupSuccessRate =
    currentPartLookupCompletions + currentPartLookupInvalid
      ? (currentPartLookupCompletions / (currentPartLookupCompletions + currentPartLookupInvalid)) * 100
      : 0;

  const previousPartLookupSuccessRate =
    previousPartLookupCompletions + previousPartLookupInvalid
      ? (previousPartLookupCompletions / (previousPartLookupCompletions + previousPartLookupInvalid)) * 100
      : 0;

  const currentPartLookupVisitors = new Set(currentPartLookupEvents.map((event) => event.visitorId).filter(Boolean))
    .size;

  const previousPartLookupVisitors = new Set(previousPartLookupEvents.map((event) => event.visitorId).filter(Boolean))
    .size;

  const currentPartLookupSessions = new Set(currentPartLookupEvents.map((event) => event.sessionId).filter(Boolean))
    .size;

  const previousPartLookupSessions = new Set(previousPartLookupEvents.map((event) => event.sessionId).filter(Boolean))
    .size;

  const completedPartLookupEvents = currentPartLookupEvents.filter((event) => event.type === "part_lookup_complete");

  const validLookupDurations = completedPartLookupEvents
    .map((event) => Number(event.durationMs || 0))
    .filter((value) => value > 0 && value < 3600000);

  const currentAvgPartLookupSeconds = validLookupDurations.length
    ? validLookupDurations.reduce((sum, value) => sum + value, 0) / validLookupDurations.length / 1000
    : 0;

  const previousValidLookupDurations = previousPartLookupEvents
    .filter((event) => event.type === "part_lookup_complete")
    .map((event) => Number(event.durationMs || 0))
    .filter((value) => value > 0 && value < 3600000);

  const previousAvgPartLookupSeconds = previousValidLookupDurations.length
    ? previousValidLookupDurations.reduce((sum, value) => sum + value, 0) / previousValidLookupDurations.length / 1000
    : 0;

  const buildToolBreakdownRows = (rows, getter) =>
    Object.entries(
      rows.reduce((acc, event) => {
        const key = String(getter(event) || "").trim();

        if (!key) return acc;

        acc[key] = (acc[key] || 0) + 1;

        return acc;
      }, {})
    )
      .map(([label, value]) => ({
        label,
        value,
      }))
      .sort((a, b) => b.value - a.value);

  const topPartLookupProducts = buildToolBreakdownRows(completedPartLookupEvents, (event) => event.productName).slice(
    0,
    12
  );

  const topPartLookupPartNumbers = buildToolBreakdownRows(completedPartLookupEvents, (event) => event.partNumber).slice(
    0,
    12
  );

  const partLookupEntryModes = buildToolBreakdownRows(
    currentPartLookupEvents.filter((event) => event.type === "part_lookup_start"),
    (event) => event.entryMode
  );

  const partLookupOptionTypes = buildToolBreakdownRows(
    currentPartLookupEvents.filter((event) => event.type === "part_lookup_option_select"),
    (event) => event.label || `Position ${event.positionIndex || "?"}`
  ).slice(0, 12);

  const currentPartLookupSessionIds = new Set(currentPartLookupEvents.map((event) => event.sessionId).filter(Boolean));

  const currentPartLookupSessionRows = currentSessions.filter((session) =>
    currentPartLookupSessionIds.has(session.sessionId)
  );

  const partLookupDeviceRows = buildToolBreakdownRows(
    currentPartLookupSessionRows,
    (session) => session.deviceCategory || "Unknown"
  );

  const partLookupSourceRows = buildToolBreakdownRows(
    currentPartLookupSessionRows,
    (session) => session.sourceName || session.sourceType || "Direct"
  ).slice(0, 10);

  const getAnalyticsTimestamp = (row) => {
    if (row?.createdAt?.toDate) {
      return row.createdAt.toDate().getTime();
    }

    return Number(row?.clientTimestamp || row?.startedClientTimestamp || row?.lastActivityClientTimestamp || 0);
  };

  const getCleanPagePath = (value = "") => String(value || "/").split("?")[0] || "/";

  const uniqueSessionCount = (rows) => new Set(rows.map((row) => row.sessionId).filter(Boolean)).size;

  const currentProductSlugs = new Set(analyticsProducts.map((product) => product.slug || product.id).filter(Boolean));

  const isProductPageView = (event) => {
    if (event.type === "product_view") return true;

    if (event.type !== "page_view") return false;

    const path = getCleanPagePath(event.pagePath).replace(/^\/+/, "");

    return currentProductSlugs.has(path);
  };

  /* =====================================================
   FUNNEL
   ===================================================== */

  const buildFunnelCounts = (sessions, events) => {
    const websiteSessionIds = new Set(sessions.map((row) => row.sessionId).filter(Boolean));

    const productSessionIds = new Set(
      events
        .filter(isProductPageView)
        .map((row) => row.sessionId)
        .filter(Boolean)
    );

    const downloadSessionIds = new Set(
      events
        .filter((row) => row.type === "document_download")
        .map((row) => row.sessionId)
        .filter(Boolean)
    );

    const contactSessionIds = new Set(
      events
        .filter((row) => ["email_click", "phone_click", "form_submission"].includes(row.type))
        .map((row) => row.sessionId)
        .filter(Boolean)
    );

    const formSessionIds = new Set(
      events
        .filter((row) => row.type === "form_submission")
        .map((row) => row.sessionId)
        .filter(Boolean)
    );

    return {
      websiteVisits: websiteSessionIds.size,
      productVisits: productSessionIds.size,
      downloads: downloadSessionIds.size,
      contactActions: contactSessionIds.size,
      formSubmissions: formSessionIds.size,
    };
  };

  const currentFunnel = buildFunnelCounts(currentSessions, currentEvents);

  const previousFunnel = buildFunnelCounts(previousSessions, previousEvents);

  const funnelRows = [
    {
      label: "Website Visits",
      current: currentFunnel.websiteVisits,
      previous: previousFunnel.websiteVisits,
    },
    {
      label: "Product Interest",
      current: currentFunnel.productVisits,
      previous: previousFunnel.productVisits,
    },
    {
      label: "Document Downloads",
      current: currentFunnel.downloads,
      previous: previousFunnel.downloads,
    },
    {
      label: "Contact Actions",
      current: currentFunnel.contactActions,
      previous: previousFunnel.contactActions,
    },
    {
      label: "Form Submissions",
      current: currentFunnel.formSubmissions,
      previous: previousFunnel.formSubmissions,
    },
  ].map((row, index, allRows) => {
    const previousStep = index === 0 ? row.current : allRows[index - 1].current;

    return {
      ...row,

      conversion: index === 0 ? 100 : previousStep ? Math.min(100, (row.current / previousStep) * 100) : 0,

      overallConversion: currentFunnel.websiteVisits
        ? Math.min(100, (row.current / currentFunnel.websiteVisits) * 100)
        : 0,
    };
  });

  /* =====================================================
   DAILY TREND DATA
   ===================================================== */

  const buildDateRangeKeys = (period) => {
    const rows = [];

    const cursor = new Date(`${period.start}T00:00:00`);

    const end = new Date(`${period.end}T00:00:00`);

    while (cursor <= end) {
      rows.push(getDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    return rows;
  };

  const currentDateKeys = buildDateRangeKeys(periods.current);

  const partLookupTrendRows = currentDateKeys.map((date) => ({
    date,

    started: currentPartLookupEvents.filter((event) => event.date === date && event.type === "part_lookup_start")
      .length,

    completed: currentPartLookupEvents.filter((event) => event.date === date && event.type === "part_lookup_complete")
      .length,
  }));

  const trafficTrendRows = currentDateKeys.map((date) => ({
    date,

    sessions: currentSessions.filter((row) => row.date === date).length,

    pageViews: currentEvents.filter((row) => row.date === date && row.type === "page_view").length,
  }));

  const ENGAGEMENT_EVENT_TYPES = new Set([
    "button_click",
    "external_link_click",
    "email_click",
    "phone_click",
    "document_download",
    "form_submission",
    "pluma_message",
    "search_result_click",
    "part_lookup_complete",
    "part_lookup_product_click",
    "part_lookup_datasheet_click",
  ]);

  const ENGAGEMENT_WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const buildEngagementByWeekdayRows = (rows) => {
    const counts = Object.fromEntries(ENGAGEMENT_WEEKDAYS.map((day) => [day, 0]));

    rows
      .filter((row) => ENGAGEMENT_EVENT_TYPES.has(row.type))
      .forEach((row) => {
        let date = null;

        if (row.date) {
          date = new Date(`${row.date}T12:00:00`);
        } else if (row.createdAt?.toDate) {
          date = row.createdAt.toDate();
        } else if (row.clientTimestamp) {
          date = new Date(Number(row.clientTimestamp));
        }

        if (!date || Number.isNaN(date.getTime())) {
          return;
        }

        const mondayIndex = (date.getDay() + 6) % 7;

        const label = ENGAGEMENT_WEEKDAYS[mondayIndex];

        counts[label] += 1;
      });

    return ENGAGEMENT_WEEKDAYS.map((label) => ({
      label,
      value: counts[label],
    }));
  };

  const engagementByWeekdayRows = buildEngagementByWeekdayRows(currentEvents);

  /* =====================================================
   GEOGRAPHY
   ===================================================== */

  const buildGeographyRows = (rows, getter) => {
    const grouped = rows.reduce((acc, row) => {
      const raw = getter(row);

      const name = String(typeof raw === "object" ? raw?.name || raw?.code || "" : raw || "").trim();

      const code = String(typeof raw === "object" ? raw?.code || "" : "")
        .trim()
        .toUpperCase();

      const key = (code || name).toLowerCase();

      if (!key || key === "unknown" || key === "zz" || key === "?") {
        return acc;
      }

      if (!acc[key]) {
        acc[key] = {
          name,
          code,
          sessions: 0,
        };
      }

      acc[key].sessions += 1;

      return acc;
    }, {});

    const values = Object.values(grouped);

    const knownTotal = values.reduce((sum, row) => sum + row.sessions, 0);

    return values
      .map((row) => ({
        ...row,

        share: knownTotal ? (row.sessions / knownTotal) * 100 : 0,
      }))
      .sort((a, b) => b.sessions - a.sessions);
  };

  const currentCountries = buildGeographyRows(currentSessions, (row) => {
    const code = String(row.countryCode || "")
      .trim()
      .toUpperCase();

    return {
      code,

      name: getCountryName(code, row.country || row.countryName || ""),
    };
  });

  const currentRegions = buildGeographyRows(currentSessions, (row) => row.regionCode || row.region);

  const currentCities = buildGeographyRows(currentSessions, (row) => row.city);

  const LANGUAGE_DISPLAY_NAMES =
    typeof Intl !== "undefined" && Intl.DisplayNames
      ? new Intl.DisplayNames(["en"], {
          type: "language",
        })
      : null;

  const getBaseLanguageCode = (value = "") =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace("_", "-")
      .split("-")[0];

  const formatLanguageName = (value = "") => {
    const code = getBaseLanguageCode(value);

    if (!code) {
      return "Unknown";
    }

    try {
      const name = LANGUAGE_DISPLAY_NAMES?.of(code);

      return name ? `${name} (${code})` : code;
    } catch {
      return code;
    }
  };

  const currentLanguageRows = Object.entries(
    currentSessions.reduce((acc, session) => {
      const code = getBaseLanguageCode(session.language);

      if (!code) {
        return acc;
      }

      acc[code] = (acc[code] || 0) + 1;

      return acc;
    }, {})
  )
    .map(([code, value]) => ({
      code,

      label: formatLanguageName(code),

      value,
    }))
    .sort((a, b) => b.value - a.value);

  const englishLanguageSessions = currentLanguageRows.find((row) => row.code === "en")?.value || 0;

  const nonEnglishLanguageRows = currentLanguageRows.filter((row) => row.code !== "en");

  const knownGeographySessions = currentSessions.filter((row) => row.countryCode && row.countryCode !== "ZZ").length;

  const geographyCoverage = currentSessions.length ? (knownGeographySessions / currentSessions.length) * 100 : 0;

  /* =====================================================
   TOP DOWNLOADS
   ===================================================== */

  const buildTopDownloadRows = (events) => {
    const grouped = {};

    events
      .filter((event) => event.type === "document_download")
      .forEach((event) => {
        const label = event.resourceLabel || event.documentName || event.fileName || event.productName || "Document";

        if (!grouped[label]) {
          grouped[label] = {
            label,
            downloads: 0,
            productName: event.productName || "",
            pagePath: getCleanPagePath(event.pagePath || "/"),
          };
        }

        grouped[label].downloads += 1;
      });

    return Object.values(grouped)
      .sort((a, b) => b.downloads - a.downloads)
      .slice(0, 15);
  };

  const topDownloadRows = buildTopDownloadRows(currentEvents);

  /* =====================================================
   EXIT ANALYTICS

   Meeting rule:
   Only sessions with 2+ page views count as exits.
   ===================================================== */

  const buildExitAnalytics = (sessions, events) => {
    const eventsBySession = {};

    events.forEach((event) => {
      if (!event.sessionId) return;

      if (!eventsBySession[event.sessionId]) {
        eventsBySession[event.sessionId] = [];
      }

      eventsBySession[event.sessionId].push(event);
    });

    const exitRows = [];

    sessions.forEach((session) => {
      if (!session.sessionId) return;

      const sessionEvents = [...(eventsBySession[session.sessionId] || [])].sort(
        (a, b) => getAnalyticsTimestamp(a) - getAnalyticsTimestamp(b)
      );

      const pageViews = sessionEvents.filter((event) => event.type === "page_view");

      /*
      Per meeting requirement:
      single-page sessions do not count in Exit Analytics.
    */
      if (pageViews.length < 2) return;

      const lastPageEvent = pageViews[pageViews.length - 1];

      const previousPageEvent = pageViews.length >= 2 ? pageViews[pageViews.length - 2] : null;

      const lastPage = getCleanPagePath(lastPageEvent?.pagePath || session.lastPage || "/");

      const previousPage = getCleanPagePath(previousPageEvent?.pagePath || "");

      const lastPageTimestamp = getAnalyticsTimestamp(lastPageEvent);

      const afterLastPage = sessionEvents.filter((event) => getAnalyticsTimestamp(event) >= lastPageTimestamp);

      const phoneEvent = [...afterLastPage].reverse().find((event) => event.type === "phone_click");

      const emailEvent = [...afterLastPage].reverse().find((event) => event.type === "email_click");

      const formEvent = [...afterLastPage].reverse().find((event) => event.type === "form_submission");

      const externalEvent = [...afterLastPage].reverse().find((event) => event.type === "external_link_click");

      const downloadEvent = [...afterLastPage].reverse().find((event) => event.type === "document_download");

      const plumaEvent = [...afterLastPage].reverse().find((event) => event.type === "pluma_message");

      let exitType = "Bounced / No Tracked Action";

      let destination = "";

      if (formEvent) {
        exitType = "Form Submission";

        destination = formEvent.formName || "Submitted form";
      } else if (phoneEvent) {
        exitType = "Phone Click";

        destination = phoneEvent.phoneNumber || phoneEvent.href || phoneEvent.destinationUrl || "Phone";
      } else if (emailEvent) {
        exitType = "Email Click";

        destination = emailEvent.email || emailEvent.href || emailEvent.destinationUrl || "Email";
      } else if (externalEvent) {
        exitType = "External Link";

        destination =
          externalEvent.destinationUrl ||
          externalEvent.href ||
          externalEvent.url ||
          externalEvent.linkUrl ||
          "External website";
      }

      const activeSeconds = sessionEvents
        .filter((event) => event.type === "active_time")
        .reduce((sum, event) => sum + Number(event.seconds || 0), 0);

      exitRows.push({
        sessionId: session.sessionId,

        visitorId: session.visitorId || "",

        lastPage,

        previousPage,

        pageCount: pageViews.length,

        activeSeconds,

        exitType,

        destination,

        deviceCategory: session.deviceCategory || "Unknown",

        isReturning: session.isNewVisitor === false,

        hadDownload: Boolean(downloadEvent),

        usedPluma: Boolean(plumaEvent),

        sourceName: session.sourceName || session.sourceType || "Direct",

        timestamp: getAnalyticsTimestamp(session),
      });
    });

    return exitRows;
  };

  const currentExitRows = buildExitAnalytics(currentSessions, currentEvents);

  const previousExitRows = buildExitAnalytics(previousSessions, previousEvents);

  const countExitType = (rows, type) => rows.filter((row) => row.exitType === type).length;

  const currentPhoneExits = countExitType(currentExitRows, "Phone Click");

  const currentEmailExits = countExitType(currentExitRows, "Email Click");

  const currentFormExits = countExitType(currentExitRows, "Form Submission");

  const currentExternalExits = countExitType(currentExitRows, "External Link");

  const currentPassiveExits = countExitType(currentExitRows, "Bounced / No Tracked Action");

  const previousPhoneExits = countExitType(previousExitRows, "Phone Click");

  const previousEmailExits = countExitType(previousExitRows, "Email Click");

  const previousFormExits = countExitType(previousExitRows, "Form Submission");

  const previousExternalExits = countExitType(previousExitRows, "External Link");

  const previousPassiveExits = countExitType(previousExitRows, "Bounced / No Tracked Action");

  const currentTrackedExits = currentExitRows.length - currentPassiveExits;

  const previousTrackedExits = previousExitRows.length - previousPassiveExits;

  const currentContactExits = currentPhoneExits + currentEmailExits + currentFormExits;

  const previousContactExits = previousPhoneExits + previousEmailExits + previousFormExits;

  const currentAvgPagesBeforeExit = currentExitRows.length
    ? currentExitRows.reduce((sum, row) => sum + row.pageCount, 0) / currentExitRows.length
    : 0;

  const previousAvgPagesBeforeExit = previousExitRows.length
    ? previousExitRows.reduce((sum, row) => sum + row.pageCount, 0) / previousExitRows.length
    : 0;

  const currentAvgExitActiveTime = currentExitRows.length
    ? currentExitRows.reduce((sum, row) => sum + row.activeSeconds, 0) / currentExitRows.length
    : 0;

  const previousAvgExitActiveTime = previousExitRows.length
    ? previousExitRows.reduce((sum, row) => sum + row.activeSeconds, 0) / previousExitRows.length
    : 0;

  const currentReturningExitCount = currentExitRows.filter((row) => row.isReturning).length;

  const previousReturningExitCount = previousExitRows.filter((row) => row.isReturning).length;

  const currentMobileExitCount = currentExitRows.filter((row) => row.deviceCategory === "mobile").length;

  const previousMobileExitCount = previousExitRows.filter((row) => row.deviceCategory === "mobile").length;

  const currentDownloadExitCount = currentExitRows.filter((row) => row.hadDownload).length;

  const previousDownloadExitCount = previousExitRows.filter((row) => row.hadDownload).length;

  const currentPlumaExitCount = currentExitRows.filter((row) => row.usedPluma).length;

  const previousPlumaExitCount = previousExitRows.filter((row) => row.usedPluma).length;

  const buildExitPageRows = (rows, field) => {
    const grouped = {};

    rows.forEach((row) => {
      const value = row[field];

      if (!value) return;

      grouped[value] = (grouped[value] || 0) + 1;
    });

    return Object.entries(grouped)
      .map(([page, exits]) => ({
        page,
        exits,
      }))
      .sort((a, b) => b.exits - a.exits);
  };

  const topExitPages = buildExitPageRows(currentExitRows, "lastPage");

  const topPreviousPages = buildExitPageRows(currentExitRows, "previousPage");

  const exitReasonRows = [
    {
      label: "Bounced / No Tracked Action",
      value: currentPassiveExits,
    },
    {
      label: "Phone Click",
      value: currentPhoneExits,
    },
    {
      label: "Email Click",
      value: currentEmailExits,
    },
    {
      label: "Form Submission",
      value: currentFormExits,
    },
    {
      label: "External Link",
      value: currentExternalExits,
    },
  ].sort((a, b) => b.value - a.value);

  /* =====================================================
   PAGE-SPECIFIC DETAIL
   ===================================================== */

  const getPageSpecificAnalytics = (pagePath) => {
    if (!pagePath) return null;

    const pageEvents = currentEvents.filter((event) => getCleanPagePath(event.pagePath) === pagePath);

    const pageViews = pageEvents.filter((event) => event.type === "page_view");

    const visitorIds = new Set(pageViews.map((event) => event.visitorId).filter(Boolean));

    const activeSeconds = pageEvents
      .filter((event) => event.type === "active_time")
      .reduce((sum, event) => sum + Number(event.seconds || 0), 0);

    const scrollRows = pageEvents
      .filter((event) => event.type === "scroll_depth")
      .map((event) => Number(event.depth || 0))
      .filter((value) => value > 0);

    const exitCount = currentExitRows.filter((row) => row.lastPage === pagePath).length;

    return {
      pagePath,

      views: pageViews.length,

      uniqueVisitors: visitorIds.size,

      avgActiveTime: pageViews.length ? activeSeconds / pageViews.length : 0,

      avgScroll: scrollRows.length ? scrollRows.reduce((sum, value) => sum + value, 0) / scrollRows.length : 0,

      downloads: countEvents(pageEvents, "document_download"),

      buttonClicks: countEvents(pageEvents, "button_click"),

      externalClicks: countEvents(pageEvents, "external_link_click"),

      emailClicks: countEvents(pageEvents, "email_click"),

      phoneClicks: countEvents(pageEvents, "phone_click"),

      formStarts: countEvents(pageEvents, "form_start"),

      formSubmissions: countEvents(pageEvents, "form_submission"),

      plumaMessages: countEvents(pageEvents, "pluma_message"),

      exits: exitCount,

      exitRate: pageViews.length ? Math.min(100, (exitCount / pageViews.length) * 100) : 0,
    };
  };

  const selectedPageAnalytics = selectedAnalyticsPage ? getPageSpecificAnalytics(selectedAnalyticsPage) : null;

  const getScrollRate = (rows, depth, pageViews) => {
    if (!pageViews) return 0;

    const reached = rows.filter((row) => row.type === "scroll_depth" && Number(row.depth) === depth).length;

    return Math.min(100, (reached / pageViews) * 100);
  };

  const selectedPageEvents = selectedAnalyticsPage
    ? currentEvents.filter((event) => getCleanPagePath(event.pagePath) === selectedAnalyticsPage)
    : [];

  const selectedPagePreviousEvents = selectedAnalyticsPage
    ? previousEvents.filter((event) => getCleanPagePath(event.pagePath) === selectedAnalyticsPage)
    : [];

  const selectedPageTrendRows = selectedAnalyticsPage
    ? currentDateKeys.map((date) => ({
        date,

        views: selectedPageEvents.filter((event) => event.date === date && event.type === "page_view").length,

        interactions: selectedPageEvents.filter(
          (event) =>
            event.date === date &&
            [
              "button_click",
              "external_link_click",
              "email_click",
              "phone_click",
              "document_download",
              "form_submission",
              "pluma_message",
            ].includes(event.type)
        ).length,
      }))
    : [];

  const selectedPageDownloads = selectedAnalyticsPage ? buildTopDownloadRows(selectedPageEvents) : [];

  const selectedPageScrollRows = selectedAnalyticsPage
    ? [25, 50, 75, 100].map((depth) => ({
        depth,

        current: getScrollRate(selectedPageEvents, depth, selectedPageAnalytics?.views || 0),

        previous: getScrollRate(
          selectedPagePreviousEvents,
          depth,
          selectedPagePreviousEvents.filter((event) => event.type === "page_view").length
        ),
      }))
    : [];

  const selectedPageSourceRows = selectedAnalyticsPage
    ? Object.entries(
        selectedPageEvents
          .filter((event) => event.type === "page_view")
          .reduce((acc, event) => {
            const key = event.sourceName || event.sourceType || "Direct";

            acc[key] = (acc[key] || 0) + 1;

            return acc;
          }, {})
      )
        .map(([label, value]) => ({
          label,
          value,
        }))
        .sort((a, b) => b.value - a.value)
    : [];

  const selectedPageDeviceRows = selectedAnalyticsPage
    ? Object.entries(
        selectedPageEvents
          .filter((event) => event.type === "page_view")
          .reduce((acc, event) => {
            const key = event.deviceCategory || "Unknown";

            acc[key] = (acc[key] || 0) + 1;

            return acc;
          }, {})
      )
        .map(([label, value]) => ({
          label,
          value,
        }))
        .sort((a, b) => b.value - a.value)
    : [];

  const groupPageExitRows = (rows, getter, formatter = (value) => value) => {
    const grouped = {};

    rows.forEach((row) => {
      const rawValue = getter(row);

      if (!rawValue) return;

      const label = formatter(rawValue);

      if (!label) return;

      grouped[label] = (grouped[label] || 0) + 1;
    });

    return Object.entries(grouped)
      .map(([label, value]) => ({
        label,
        value,
      }))
      .sort((a, b) => b.value - a.value);
  };

  const selectedPageExitRows = selectedAnalyticsPage
    ? currentExitRows.filter((row) => row.lastPage === selectedAnalyticsPage)
    : [];

  const selectedPageExitReasonRows = groupPageExitRows(
    selectedPageExitRows,
    (row) => row.exitType,
    (reason) => (reason === "Bounced / No Tracked Action" ? "No Tracked Final Action" : reason)
  );

  const selectedPagePreviousPageRows = groupPageExitRows(
    selectedPageExitRows,
    (row) => row.previousPage,
    (page) => (!page || page === "/" ? "Homepage" : page)
  ).slice(0, 10);

  const selectedPageExitDestinationRows = groupPageExitRows(
    selectedPageExitRows.filter((row) => row.destination),
    (row) => row.destination
  ).slice(0, 10);

  const scrollDepthRows = [25, 50, 75, 100].map((depth) => ({
    depth,
    current: getScrollRate(currentEvents, depth, currentPageViews),
    previous: getScrollRate(previousEvents, depth, previousPageViews),
  }));

  const classifyAnalyticsVisitor = (visitor) => {
    const events = visitor.events || [];

    const paths = events
      .filter((event) => event.type === "page_view")
      .map((event) => String(event.pagePath || "").toLowerCase());

    const categories = events.map((event) => String(event.category || "").toLowerCase()).filter(Boolean);

    const eventTypes = events.map((event) => event.type);

    /*
      "Hacker" means a DevTools/security signal
      was detected. It is not proof of malicious
      activity.
    */
    if (eventTypes.includes("developer_tools_detected") || eventTypes.includes("developer_tools_shortcut")) {
      return "DevTools";
    }

    if (
      paths.some((path) => path.includes("/partners")) ||
      categories.some(
        (value) => value.includes("partnership") || value.includes("reseller") || value.includes("distribution")
      )
    ) {
      return "Partner";
    }

    if (
      paths.some((path) => path.includes("/careers")) ||
      categories.some((value) => value.includes("career") || value.includes("job"))
    ) {
      return "Job Seeker";
    }

    if (
      categories.some(
        (value) =>
          value.includes("technical support") ||
          value.includes("troubleshooting") ||
          value.includes("order status") ||
          value.includes("billing") ||
          value.includes("return")
      )
    ) {
      return "Support";
    }

    if (
      categories.some(
        (value) =>
          value.includes("pricing") ||
          value.includes("quote") ||
          value.includes("estimation") ||
          value.includes("demo") ||
          value.includes("consultation") ||
          value.includes("appointment")
      ) ||
      eventTypes.includes("form_submission") ||
      eventTypes.includes("email_click") ||
      eventTypes.includes("phone_click")
    ) {
      return "Buyer";
    }

    if (
      paths.some((path) => path.includes("/part-lookup")) ||
      eventTypes.includes("document_download") ||
      events.filter((event) => event.type === "product_view").length >= 2
    ) {
      return "Technician";
    }

    return "Explorer";
  };

  const formatAnalyticsDateTime = (value) => {
    if (!value) return "Unknown";

    if (value?.toDate) {
      return value.toDate().toLocaleString();
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
  };

  const formatVisitorId = (visitorId = "") => {
    if (!visitorId) return "Unknown";
    return visitorId
      .replace(/^visitor_/, "")
      .slice(0, 10)
      .toUpperCase();
  };

  const formatJourneyPage = (pagePath = "") => {
    if (!pagePath || pagePath === "/") return "Homepage";
    return pagePath;
  };

  const LIVE_VISITOR_WINDOW_MS = 45 * 1000;

  const liveSessions = liveSessionRows
    .filter((session) => {
      const lastSeen = Number(session.liveUpdatedClientTimestamp || session.lastActivityClientTimestamp || 0);

      return session.liveVisible === true && lastSeen > 0 && Date.now() - lastSeen <= LIVE_VISITOR_WINDOW_MS;
    })
    .sort(
      (a, b) =>
        Number(b.liveUpdatedClientTimestamp || b.lastActivityClientTimestamp || 0) -
        Number(a.liveUpdatedClientTimestamp || a.lastActivityClientTimestamp || 0)
    );

  const liveVisitorCount = new Set(liveSessions.map((session) => session.visitorId).filter(Boolean)).size;

  const livePageRows = Object.values(
    liveSessions.reduce((acc, session) => {
      const page = getCleanPagePath(session.lastPage || "/");

      if (!acc[page]) {
        acc[page] = {
          label: formatJourneyPage(page),
          page,
          value: 0,
        };
      }

      acc[page].value += 1;

      return acc;
    }, {})
  ).sort((a, b) => b.value - a.value);

  const liveDeviceRows = Object.entries(
    liveSessions.reduce((acc, session) => {
      const device = session.deviceCategory || "Unknown";

      acc[device] = (acc[device] || 0) + 1;

      return acc;
    }, {})
  )
    .map(([label, value]) => ({
      label,
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const liveCountryRows = Object.entries(
    liveSessions.reduce((acc, session) => {
      const country = session.countryCode || session.country || "";

      if (!country) return acc;

      acc[country] = (acc[country] || 0) + 1;

      return acc;
    }, {})
  )
    .map(([name, sessions]) => ({
      name,
      sessions,
    }))
    .sort((a, b) => b.sessions - a.sessions);

  const liveCityRows = Object.entries(
    liveSessions.reduce((acc, session) => {
      const city = session.city || "";

      if (!city) return acc;

      acc[city] = (acc[city] || 0) + 1;

      return acc;
    }, {})
  )
    .map(([label, value]) => ({
      label,
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const formatLiveAgo = (session) => {
    const lastSeen = Number(session.liveUpdatedClientTimestamp || session.lastActivityClientTimestamp || 0);

    if (!lastSeen) return "Unknown";

    const seconds = Math.max(0, Math.round((Date.now() - lastSeen) / 1000));

    if (seconds < 5) return "Now";

    return `${seconds}s ago`;
  };

  const buildPagePerformance = (events) => {
    const pages = {};
    const scrollVisits = {};

    events.forEach((event) => {
      const pagePath = event.pagePath || "/";

      if (!pages[pagePath]) {
        pages[pagePath] = {
          pagePath,
          pageTitle: "",
          views: 0,
          visitorIds: new Set(),
          activeSeconds: 0,
          scrollDepthTotal: 0,
          scrollDepthCount: 0,
        };
      }

      if (event.type === "page_view") {
        pages[pagePath].views += 1;

        if (event.visitorId) pages[pagePath].visitorIds.add(event.visitorId);
        if (event.pageTitle) pages[pagePath].pageTitle = event.pageTitle;
      }

      if (event.type === "active_time") {
        pages[pagePath].activeSeconds += Number(event.seconds || 0);
      }

      if (event.type === "scroll_depth") {
        const depth = Number(event.depth || 0);

        if (depth > 0) {
          const visitKey = `${event.sessionId || event.visitorId || "unknown"}::${pagePath}`;
          scrollVisits[visitKey] = Math.max(scrollVisits[visitKey] || 0, depth);
        }
      }
    });

    Object.entries(scrollVisits).forEach(([visitKey, depth]) => {
      const pagePath = visitKey.substring(visitKey.indexOf("::") + 2);

      if (!pages[pagePath] || depth <= 0) return;

      pages[pagePath].scrollDepthTotal += depth;
      pages[pagePath].scrollDepthCount += 1;
    });

    return Object.values(pages)
      .filter((page) => page.views > 0)
      .map((page) => ({
        pagePath: page.pagePath,
        pageTitle: page.pageTitle,
        views: page.views,
        uniqueVisitors: page.visitorIds.size,
        activeSeconds: page.activeSeconds,
        avgActiveTime: page.views ? page.activeSeconds / page.views : 0,
        avgScrollDepth: page.scrollDepthCount ? page.scrollDepthTotal / page.scrollDepthCount : 0,
      }))
      .sort((a, b) => b.views - a.views);
  };

  const currentPagePerformance = buildPagePerformance(currentEvents);
  const previousPagePerformance = buildPagePerformance(previousEvents);

  const previousPagePerformanceMap = Object.fromEntries(previousPagePerformance.map((page) => [page.pagePath, page]));

  const pagePerformanceRows = currentPagePerformance.map((page) => {
    const previous = previousPagePerformanceMap[page.pagePath];

    return {
      ...page,
      previousViews: previous?.views || 0,
      previousUniqueVisitors: previous?.uniqueVisitors || 0,
      previousAvgActiveTime: previous?.avgActiveTime || 0,
      previousAvgScrollDepth: previous?.avgScrollDepth || 0,
    };
  });

  const getHeatmapPageKey = (pagePath = "") => {
    const cleanPath = getCleanPagePath(pagePath);

    const slug = cleanPath.replace(/^\/+/, "").replace(/\/+$/, "");

    return currentProductSlugs.has(slug) ? PRODUCT_HEATMAP_KEY : cleanPath;
  };

  const currentHeatmapEvents = currentEvents.filter((event) => {
    if (event.type !== "heatmap_click") {
      return false;
    }

    const x = Number(event.documentXPercent ?? event.xPercent);

    const y = Number(event.documentYPercent ?? event.yPercent);

    return Number.isFinite(x) && Number.isFinite(y);
  });

  const heatmapPageCounts = currentHeatmapEvents.reduce((accumulator, event) => {
    const key = getHeatmapPageKey(event.pagePath);

    accumulator[key] = (accumulator[key] || 0) + 1;

    return accumulator;
  }, {});

  const heatmapPageOptions = [
    {
      key: PRODUCT_HEATMAP_KEY,

      label: "All Product Pages",

      clicks: heatmapPageCounts[PRODUCT_HEATMAP_KEY] || 0,
    },

    ...Object.entries(heatmapPageCounts)
      .filter(([key]) => key !== PRODUCT_HEATMAP_KEY)
      .map(([key, clicks]) => ({
        key,

        label: formatJourneyPage(key),

        clicks,
      }))
      .sort((first, second) => second.clicks - first.clicks),
  ];

  useEffect(() => {
    if (heatmapPageOptions.some((item) => item.key === selectedHeatmapPage)) {
      return;
    }

    setSelectedHeatmapPage(heatmapPageOptions[0]?.key || PRODUCT_HEATMAP_KEY);
  }, [selectedHeatmapPage, heatmapPageOptions]);

  const selectedHeatmapEvents = currentHeatmapEvents.filter((event) => {
    const matchesPage = getHeatmapPageKey(event.pagePath) === selectedHeatmapPage;

    const matchesDevice = heatmapDeviceFilter === "all" || event.deviceCategory === heatmapDeviceFilter;

    return matchesPage && matchesDevice;
  });

  const selectedHeatmapTargetRows = Object.entries(
    selectedHeatmapEvents.reduce((accumulator, event) => {
      const label = String(
        event.targetLabel || event.analyticsLabel || event.analyticsId || event.elementType || "Unlabeled click"
      ).trim();

      const key = label || "Unlabeled click";

      accumulator[key] = (accumulator[key] || 0) + 1;

      return accumulator;
    }, {})
  )
    .map(([label, value]) => ({
      label,
      value,
    }))
    .sort((first, second) => second.value - first.value)
    .slice(0, 12);

  const selectedHeatmapLabel = heatmapPageOptions.find((item) => item.key === selectedHeatmapPage)?.label || "Page";

  const selectedHeatmapPreviewPath =
    selectedHeatmapPage === PRODUCT_HEATMAP_KEY
      ? Object.entries(
          selectedHeatmapEvents.reduce((accumulator, event) => {
            const pagePath = getCleanPagePath(event.pagePath || "");

            if (!pagePath) {
              return accumulator;
            }

            accumulator[pagePath] = (accumulator[pagePath] || 0) + 1;

            return accumulator;
          }, {})
        ).sort((first, second) => second[1] - first[1])[0]?.[0] ||
        (productTotals[0]?.productSlug ? `/${productTotals[0].productSlug}` : "")
      : selectedHeatmapPage;

  const journeyVisitors = useMemo(() => {
    const visitors = {};

    currentSessions.forEach((session) => {
      if (!session.visitorId) return;

      if (!visitors[session.visitorId]) {
        visitors[session.visitorId] = {
          visitorId: session.visitorId,
          sessions: [],
          events: [],
        };
      }

      visitors[session.visitorId].sessions.push(session);
    });

    currentEvents.forEach((event) => {
      if (!event.visitorId) return;

      if (!visitors[event.visitorId]) {
        visitors[event.visitorId] = {
          visitorId: event.visitorId,
          sessions: [],
          events: [],
        };
      }

      visitors[event.visitorId].events.push(event);
    });

    return Object.values(visitors)
      .map((visitor) => {
        const sessions = visitor.sessions;
        const events = visitor.events;

        const pageViews = events.filter((event) => event.type === "page_view");

        const activeSeconds = events
          .filter((event) => event.type === "active_time")
          .reduce((sum, event) => sum + Number(event.seconds || 0), 0);

        const timestamps = [
          ...sessions.map((session) => Number(session.startedClientTimestamp || 0)),
          ...events.map((event) => Number(event.clientTimestamp || 0)),
        ].filter(Boolean);

        const firstTimestamp = timestamps.length ? Math.min(...timestamps) : null;
        const lastTimestamp = timestamps.length ? Math.max(...timestamps) : null;

        const observedSeconds =
          firstTimestamp && lastTimestamp ? Math.max(0, Math.round((lastTimestamp - firstTimestamp) / 1000)) : 0;

        const passiveSeconds = Math.max(0, observedSeconds - activeSeconds);

        const latestSession = [...sessions].sort(
          (a, b) => Number(b.startedClientTimestamp || 0) - Number(a.startedClientTimestamp || 0)
        )[0];

        const importantEvents = events.filter((event) =>
          [
            "product_view",
            "document_download",
            "button_click",
            "external_link_click",
            "email_click",
            "phone_click",
            "copy_to_clipboard",
            "developer_tools_detected",
            "developer_tools_shortcut",
            "tab_open",
            "form_start",
            "form_abandonment",
            "form_submission",
            "pluma_open",
            "pluma_message",
            "search",
          ].includes(event.type)
        );

        return {
          ...visitor,
          category: classifyAnalyticsVisitor(visitor),
          sessionCount: sessions.length,
          pageCount: pageViews.length,
          activeSeconds,
          observedSeconds,
          passiveSeconds,
          latestSession,
          importantEvents,
          firstTimestamp,
          lastTimestamp,
        };
      })
      .sort((a, b) => Number(b.lastTimestamp || 0) - Number(a.lastTimestamp || 0));
  }, [currentSessions, currentEvents]);

  const filteredJourneyVisitors =
    journeyCategoryFilter === "All"
      ? journeyVisitors
      : journeyVisitors.filter((visitor) => visitor.category === journeyCategoryFilter);

  const openJourneyFromPluma = (visitorId) => {
    if (!visitorId) {
      return;
    }

    const visitor = journeyVisitors.find((item) => item.visitorId === visitorId);

    if (!visitor) {
      alert("No Customer Journey data was found for this Pluma visitor in the selected analytics period.");

      return;
    }

    setSelectedJourneyVisitor(visitor);
    setActiveAnalyticsTab("journeys");
  };

  const openPlumaFromJourney = (visitorId) => {
    if (!visitorId) {
      return;
    }

    setSelectedJourneyVisitor(null);

    setPlumaFocusVisitorId(visitorId);

    setActiveAnalyticsTab("search");
  };

  const JOURNEY_CATEGORY_ORDER = ["Explorer", "Technician", "Support", "Job Seeker", "Buyer", "Partner", "DevTools"];

  const journeyCategoryCounts = journeyVisitors.reduce((acc, visitor) => {
    const category = visitor.category || "Explorer";

    acc[category] = (acc[category] || 0) + 1;

    return acc;
  }, {});

  const journeyTotalVisitors = journeyVisitors.length;

  const journeyCategoryRows = JOURNEY_CATEGORY_ORDER.map((name) => ({
    name,
    sessions: journeyCategoryCounts[name] || 0,
    share: journeyTotalVisitors ? ((journeyCategoryCounts[name] || 0) / journeyTotalVisitors) * 100 : 0,
  })).filter((row) => row.sessions > 0);

  const journeyReturningVisitors = journeyVisitors.filter((visitor) => visitor.sessionCount > 1).length;

  const journeyReturningRate = journeyTotalVisitors ? (journeyReturningVisitors / journeyTotalVisitors) * 100 : 0;

  const journeyAverageSessions = journeyTotalVisitors
    ? journeyVisitors.reduce((sum, visitor) => sum + visitor.sessionCount, 0) / journeyTotalVisitors
    : 0;

  const journeyAveragePages = journeyTotalVisitors
    ? journeyVisitors.reduce((sum, visitor) => sum + visitor.pageCount, 0) / journeyTotalVisitors
    : 0;

  const journeyAverageActiveSeconds = journeyTotalVisitors
    ? journeyVisitors.reduce((sum, visitor) => sum + visitor.activeSeconds, 0) / journeyTotalVisitors
    : 0;

  const journeyBuyerVisitors = journeyCategoryCounts.Buyer || 0;

  const journeyBuyerRate = journeyTotalVisitors ? (journeyBuyerVisitors / journeyTotalVisitors) * 100 : 0;

  const longestJourneyRows = [...journeyVisitors]
    .filter((visitor) => visitor.pageCount > 0)
    .sort((first, second) => second.activeSeconds - first.activeSeconds)
    .slice(0, 3)
    .map((visitor) => ({
      visitor: formatVisitorId(visitor.visitorId),

      category: visitor.category,

      sessions: visitor.sessionCount,

      pages: visitor.pageCount,

      activeSeconds: visitor.activeSeconds,

      source: visitor.latestSession?.sourceName || visitor.latestSession?.sourceType || "Direct",

      device: visitor.latestSession?.deviceCategory || "Unknown",

      path: visitor.events
        .filter((event) => event.type === "page_view")
        .sort((first, second) => getAnalyticsTimestamp(first) - getAnalyticsTimestamp(second))
        .map((event) => formatJourneyPage(getCleanPagePath(event.pagePath)))
        .slice(0, 8),
    }));

  if (loading || eventsLoading) {
    analyticsReportDataRef.current = null;
  } else {
    analyticsReportDataRef.current = {
      brand: {
        name: "Go!Foton",

        logoUrl:
          "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FGoFoton%20Logo-k%20(R).png?alt=media&token=6fa4c257-8569-4f86-af93-320f2b64a527",
      },

      generatedAt: new Date().toISOString(),

      range: {
        label: selectedRange.label,

        start: periods.current.start,

        end: periods.current.end,

        previousStart: periods.previous.start,

        previousEnd: periods.previous.end,
      },

      overview: {
        visitors: {
          current: currentVisitors,

          previous: previousVisitors,
        },

        sessions: {
          current: currentSessions.length,

          previous: previousSessions.length,
        },

        pageViews: {
          current: currentPageViews,

          previous: previousPageViews,
        },

        newVisitors: {
          current: currentNewVisitors,

          previous: previousNewVisitors,
        },

        returningVisitors: {
          current: currentReturningVisitors,

          previous: previousReturningVisitors,
        },

        avgActiveSeconds: {
          current: currentAvgActiveTime,

          previous: previousAvgActiveTime,
        },

        pagesPerSession: {
          current: currentPagesPerSession,

          previous: previousPagesPerSession,
        },

        productViews: {
          current: totalProductViews,

          previous: previousViews,
        },

        downloads: {
          current: totalDownloads,

          previous: previousDownloads,
        },

        formSubmissions: {
          current: currentFormSubmissions,

          previous: previousFormSubmissions,
        },

        emailClicks: {
          current: currentEmailClicks,

          previous: previousEmailClicks,
        },

        phoneClicks: {
          current: currentPhoneClicks,

          previous: previousPhoneClicks,
        },
      },

      trends: {
        traffic: trafficTrendRows,
      },

      acquisition: {
        sourceTotals: {
          organic: currentSources.organic || 0,

          direct: currentSources.direct || 0,

          referral: currentSources.referral || 0,

          campaign: currentSources.campaign || 0,
        },

        sources: currentAcquisitionSources.slice(0, 10).map((row) => ({
          label: row.sourceName,

          value: row.sessions,

          share: row.share,
        })),

        devices: [
          {
            label: "Desktop",

            value: currentDevices.desktop || 0,
          },

          {
            label: "Mobile",

            value: currentDevices.mobile || 0,
          },

          {
            label: "Tablet",

            value: currentDevices.tablet || 0,
          },
        ],

        countries: currentCountries.slice(0, 10).map((row) => ({
          label: row.name,

          value: row.sessions,

          share: row.share,
        })),

        landingPages: currentLandingPages.slice(0, 10).map((row) => ({
          page: formatJourneyPage(row.pagePath),

          sessions: row.sessions,

          uniqueVisitors: row.uniqueVisitors,

          topSource: row.topSource,
        })),
      },

      pages: pagePerformanceRows.slice(0, 12).map((row) => ({
        page: formatJourneyPage(row.pagePath),

        title: row.pageTitle || "",

        uniqueVisitors: row.uniqueVisitors,

        views: row.views,

        avgActiveSeconds: row.avgActiveTime,

        avgScroll: row.avgScrollDepth,
      })),

      products: productTotals.slice(0, 12).map((row) => ({
        name: row.productName,

        family: row.productFamily || "",

        visitors: currentProductVisitors[row.productSlug] || 0,

        views: row.views,

        downloads: row.downloads,

        avgTimeSeconds: row.views ? row.timeOnPageSeconds / row.views : 0,
      })),

      engagement: {
        documentDownloads: countEvents(currentEvents, "document_download"),

        buttonClicks: currentButtonClicks,

        externalClicks: currentExternalClicks,

        emailClicks: currentEmailClicks,

        phoneClicks: currentPhoneClicks,

        formSubmissions: currentFormSubmissions,

        weekday: engagementByWeekdayRows,

        topDownloads: topDownloadRows.slice(0, 10).map((row) => ({
          label: row.label,

          context: row.productName || formatJourneyPage(row.pagePath),

          value: row.downloads,
        })),
      },

      funnel: funnelRows,

      exits: {
        eligible: currentExitRows.length,

        tracked: currentTrackedExits,

        contact: currentContactExits,

        passive: currentPassiveExits,

        avgPages: currentAvgPagesBeforeExit,

        avgActiveSeconds: currentAvgExitActiveTime,

        reasons: exitReasonRows,

        topPages: topExitPages.slice(0, 10).map((row) => ({
          label: formatJourneyPage(row.page),

          value: row.exits,
        })),
      },

      journeys: {
        totalVisitors: journeyTotalVisitors,

        returningVisitors: journeyReturningVisitors,

        returningRate: journeyReturningRate,

        avgSessions: journeyAverageSessions,

        avgPages: journeyAveragePages,

        avgActiveSeconds: journeyAverageActiveSeconds,

        buyerVisitors: journeyBuyerVisitors,

        buyerRate: journeyBuyerRate,

        categories: journeyCategoryRows.map((row) => ({
          label: row.name,

          value: row.sessions,

          share: row.share,
        })),

        longest: longestJourneyRows,
      },

      tools: {
        users: currentPartLookupVisitors,

        sessions: currentPartLookupSessions,

        starts: currentPartLookupStarts,

        completions: currentPartLookupCompletions,

        completionRate: currentPartLookupCompletionRate,

        successRate: currentPartLookupSuccessRate,

        invalid: currentPartLookupInvalid,

        avgSeconds: currentAvgPartLookupSeconds,

        copies: currentPartLookupCopies,

        productClicks: currentPartLookupProductClicks,

        datasheetClicks: currentPartLookupDatasheetClicks,

        trend: partLookupTrendRows,

        topProducts: topPartLookupProducts,

        topParts: topPartLookupPartNumbers,
      },

      search: {
        opens: currentSearchOpens,

        searches: currentSearches,

        zeroResults: currentZeroSearches,

        resultClicks: currentSearchResultClicks,

        ctr: currentSearchCTR,

        viewAllClicks: currentSearchViewAllClicks,

        topTerms: currentSearchTermRows.map((row) => ({
          label: row.term,

          value: row.searches + row.zeroResults,

          zeroResults: row.zeroResults,
        })),

        topResults: currentSearchResultClickRows,

        plumaMessages: currentPlumaMessages,

        plumaThumbsUp: currentPlumaThumbsUp,

        plumaThumbsDown: currentPlumaThumbsDown,

        plumaFeedbackTotal: currentPlumaFeedbackTotal,

        plumaSatisfaction: currentPlumaSatisfaction,
      },
    };
  }

  const AnalyticsLineChart = ({ rows, valueKey, secondaryValueKey = "", primaryLabel = "", secondaryLabel = "" }) => {
    const values = rows.flatMap((row) => [
      Number(row[valueKey] || 0),
      secondaryValueKey ? Number(row[secondaryValueKey] || 0) : 0,
    ]);

    const maxValue = Math.max(1, ...values);

    const width = 700;
    const height = 220;
    const padding = 24;

    const makePoints = (key) =>
      rows
        .map((row, index) => {
          const x = rows.length <= 1 ? width / 2 : padding + (index / (rows.length - 1)) * (width - padding * 2);

          const y = height - padding - (Number(row[key] || 0) / maxValue) * (height - padding * 2);

          return `${x},${y}`;
        })
        .join(" ");

    return (
      <div className="analytics-line-chart">
        <div className="analytics-chart-legend">
          <span>
            <i className="primary" />
            {primaryLabel}
          </span>

          {secondaryValueKey && (
            <span>
              <i className="secondary" />
              {secondaryLabel}
            </span>
          )}
        </div>

        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Analytics trend chart">
          <line
            x1={padding}
            y1={height - padding}
            x2={width - padding}
            y2={height - padding}
            className="analytics-chart-axis"
          />

          <polyline points={makePoints(valueKey)} className="analytics-chart-line primary" />

          {secondaryValueKey && (
            <polyline points={makePoints(secondaryValueKey)} className="analytics-chart-line secondary" />
          )}
        </svg>

        <div className="analytics-chart-labels">
          {rows.map((row) => (
            <span key={row.date}>{row.date.slice(5)}</span>
          ))}
        </div>
      </div>
    );
  };

  const AnalyticsDonutChart = ({ rows, centerLabel = "sessions", showCountryFlags = false }) => {
    const colors = ["#D94829", "#85246D", "#8CC540", "#23B0BA", "#F59E0B", "#64748B"];

    const cleanRows = rows
      .filter((row) => {
        const name = String(row.name || "")
          .trim()
          .toLowerCase();

        return name && name !== "unknown" && name !== "zz" && name !== "?";
      })
      .slice(0, 6);

    const total = cleanRows.reduce((sum, row) => sum + Number(row.sessions || 0), 0);

    if (!total) {
      return <div className="analytics-empty-chart">No known geographic data in this period.</div>;
    }

    let cursor = 0;

    const stops = cleanRows.map((row, index) => {
      const start = cursor;

      cursor += (Number(row.sessions || 0) / total) * 100;

      return `${colors[index % colors.length]} ${start}% ${cursor}%`;
    });

    return (
      <div className="analytics-geo-donut-layout">
        <div
          className="analytics-geo-donut"
          style={{
            background: `conic-gradient(${stops.join(",")})`,
          }}
        >
          <div>
            <strong>{total.toLocaleString()}</strong>
            <span>{centerLabel}</span>
          </div>
        </div>

        <div className="analytics-geo-legend">
          {cleanRows.map((row, index) => (
            <div key={row.name}>
              <i
                style={{
                  background: colors[index % colors.length],
                }}
              />

              <span className={showCountryFlags ? "analytics-country-label" : undefined}>
                {showCountryFlags && <b className="analytics-country-flag">{getCountryFlag(row.code || row.name)}</b>}

                {row.name}
              </span>

              <strong>{row.sessions.toLocaleString()}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const AnalyticsBarChart = ({ rows }) => {
    const maxValue = Math.max(1, ...rows.map((row) => Number(row.value || 0)));

    return (
      <div className="analytics-bar-chart">
        {rows.map((row) => (
          <div className="analytics-bar-row" key={row.label}>
            <span>{row.label}</span>

            <div>
              <i
                style={{
                  width: `${(Number(row.value || 0) / maxValue) * 100}%`,
                }}
              />
            </div>

            <strong>{row.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  const Change = ({ current, previous }) => {
    if (!previous && !current) return <span className="analytics-change neutral">0.0%</span>;
    if (!previous && current) return <span className="analytics-change positive">↑ New</span>;

    const change = ((current - previous) / previous) * 100;
    const positive = change >= 0;

    return (
      <span className={`analytics-change ${positive ? "positive" : "negative"}`}>
        {positive ? "↑" : "↓"} {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  const EmptyMetric = ({ label, description }) => (
    <div className="analytics-metric-card">
      <div className="analytics-metric-label">{label}</div>
      <div className="analytics-metric-value">—</div>
      <div className="analytics-metric-footer">
        <span className="analytics-change neutral">Tracking needed</span>
      </div>
      {description && <div className="analytics-metric-note">{description}</div>}
    </div>
  );

  const saveEmailAlert = async () => {
    const email = alertForm.email.trim();
    if (!email) return alert("Recipient email is required.");
    if (alertForm.category === "productUpdates" && !alertForm.productFamily) return alert("Select a product family.");

    setSavingAlert(true);

    try {
      const alertRef = doc(collection(db, "analyticsEmailAlerts"));

      const newAlert = {
        ...alertForm,
        name:
          alertForm.name.trim() ||
          `${alertForm.productFamily || "Website"} ${alertForm.frequency === "monthly" ? "Monthly" : "Quarterly"} Report`,
        email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(alertRef, newAlert);
      setEmailAlerts((prev) => [...prev, { id: alertRef.id, ...newAlert }]);
      setAlertForm({
        name: "",
        category: "productUpdates",
        frequency: "monthly",
        productFamily: "",
        email: "",
        active: true,
      });
    } catch (err) {
      console.error("Could not save analytics email alert:", err);
      alert("Could not save alert.");
    } finally {
      setSavingAlert(false);
    }
  };

  const toggleEmailAlert = async (item) => {
    const active = item.active === false;

    try {
      await updateDoc(doc(db, "analyticsEmailAlerts", item.id), { active, updatedAt: serverTimestamp() });
      setEmailAlerts((prev) =>
        prev.map((alertItem) => (alertItem.id === item.id ? { ...alertItem, active } : alertItem))
      );
    } catch (err) {
      console.error("Could not update analytics email alert:", err);
    }
  };

  const removeEmailAlert = async (id) => {
    if (!window.confirm("Delete this email alert?")) return;

    try {
      await deleteDoc(doc(db, "analyticsEmailAlerts", id));
      setEmailAlerts((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Could not delete analytics email alert:", err);
    }
  };

  const excludeCurrentBrowser = async () => {
    const label = browserLabel.trim() || getBrowserName();

    setSavingBrowserExclusion(true);

    try {
      const exclusionRef = doc(db, "analyticsBrowserExclusions", currentAnalyticsBrowserId);

      const existing = browserExclusions.find((item) => item.browserId === currentAnalyticsBrowserId);

      const record = {
        browserId: currentAnalyticsBrowserId,

        label,

        browserName: getBrowserName(),

        active: true,

        updatedAt: serverTimestamp(),

        ...(existing
          ? {}
          : {
              createdAt: serverTimestamp(),
            }),
      };

      await setDoc(exclusionRef, record, {
        merge: true,
      });

      setBrowserExclusions((previous) => [
        ...previous.filter((item) => item.browserId !== currentAnalyticsBrowserId),

        {
          id: currentAnalyticsBrowserId,

          ...existing,
          ...record,
        },
      ]);

      localStorage.setItem(LEGACY_ANALYTICS_EXCLUDED_KEY, "true");

      localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");

      setLegacyBrowserFlag(true);

      window.dispatchEvent(new Event(ANALYTICS_EXCLUSION_EVENT));

      setBrowserLabel("");
    } catch (error) {
      console.error("Could not exclude this browser:", error);

      alert("Could not exclude this browser. Check Firestore rules and try again.");
    } finally {
      setSavingBrowserExclusion(false);
    }
  };

  const includeCurrentBrowser = async () => {
    const confirmed = window.confirm("Include this browser in analytics again?");

    if (!confirmed) {
      return;
    }

    setSavingBrowserExclusion(true);

    try {
      await deleteDoc(doc(db, "analyticsBrowserExclusions", currentAnalyticsBrowserId));

      setBrowserExclusions((previous) => previous.filter((item) => item.browserId !== currentAnalyticsBrowserId));

      localStorage.removeItem(LEGACY_ANALYTICS_EXCLUDED_KEY);

      localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");

      setLegacyBrowserFlag(false);

      window.dispatchEvent(new Event(ANALYTICS_EXCLUSION_EVENT));
    } catch (error) {
      console.error("Could not include this browser:", error);

      alert("Could not include this browser. Try again.");
    } finally {
      setSavingBrowserExclusion(false);
    }
  };

  const beginBrowserRename = (item) => {
    setRenamingBrowserId(item.browserId || item.id);

    setRenamingBrowserLabel(item.label || item.browserName || "Excluded Browser");
  };

  const cancelBrowserRename = () => {
    setRenamingBrowserId("");

    setRenamingBrowserLabel("");
  };

  const saveBrowserRename = async (item) => {
    const browserId = item.browserId || item.id;

    const label = renamingBrowserLabel.trim();

    if (!label) {
      alert("Browser label is required.");

      return;
    }

    try {
      await updateDoc(doc(db, "analyticsBrowserExclusions", browserId), {
        label,

        updatedAt: serverTimestamp(),
      });

      setBrowserExclusions((previous) =>
        previous.map((row) =>
          (row.browserId || row.id) === browserId
            ? {
                ...row,
                label,
              }
            : row
        )
      );

      cancelBrowserRename();
    } catch (error) {
      console.error("Could not rename browser exclusion:", error);

      alert("Could not rename this browser. Try again.");
    }
  };

  const removeBrowserExclusion = async (item) => {
    const browserId = item.browserId || item.id;

    const confirmed = window.confirm(`Remove ${item.label || "this browser"} from the exclusion list?`);

    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(doc(db, "analyticsBrowserExclusions", browserId));

      setBrowserExclusions((previous) => previous.filter((row) => (row.browserId || row.id) !== browserId));

      if (browserId === currentAnalyticsBrowserId) {
        localStorage.removeItem(LEGACY_ANALYTICS_EXCLUDED_KEY);

        localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");

        setLegacyBrowserFlag(false);

        window.dispatchEvent(new Event(ANALYTICS_EXCLUSION_EVENT));
      }

      if (renamingBrowserId === browserId) {
        cancelBrowserRename();
      }
    } catch (error) {
      console.error("Could not remove browser exclusion:", error);

      alert("Could not remove this browser. Try again.");
    }
  };

  return (
    <div className="analytics-dashboard">
      <div className="analytics-tabs">
        {analyticsTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"

            data-analytics-tab={tab.key}

            className={`analytics-tab ${activeAnalyticsTab === tab.key ? "active" : ""}`}

            onClick={() => setActiveAnalyticsTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {eventsLoading && (
        <div className="analytics-detail-loading">
          <span className="analytics-export-spinner" />

          <div>
            <strong>Loading detailed interaction data...</strong>

            <small>
              {analyticsLoadSeconds < 4
                ? "Loading clicks, page activity, journeys, search, Pluma, and heatmap data."
                : `${analyticsLoadSeconds}s elapsed. The main dashboard is already usable.`}
            </small>
          </div>
        </div>
      )}

      {exportingAnalytics && (
        <div className="analytics-export-overlay" role="status">
          <span className="analytics-export-spinner" />
          Generating analytics report…
        </div>
      )}

      <div className="analytics-report-capture-root">
        {activeAnalyticsTab === "overview" && (
          <div className="analytics-tab-content">
            <div className="analytics-metric-grid">
              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Visitors</div>
                <div className="analytics-metric-value">{currentVisitors.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentVisitors} previous={previousVisitors} />
                </div>
                <div className="analytics-metric-note">Unique website visitors</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">New Visitors</div>
                <div className="analytics-metric-value">{currentNewVisitors.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentNewVisitors} previous={previousNewVisitors} />
                </div>
                <div className="analytics-metric-note">First-time visitors</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Returning Visitors</div>
                <div className="analytics-metric-value">{currentReturningVisitors.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentReturningVisitors} previous={previousReturningVisitors} />
                </div>
                <div className="analytics-metric-note">Visitors seen previously</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Page Views</div>
                <div className="analytics-metric-value">{currentPageViews.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPageViews} previous={previousPageViews} />
                </div>
                <div className="analytics-metric-note">All tracked website pages</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Product Page Views</div>
                <div className="analytics-metric-value">{totalProductViews.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={totalProductViews} previous={previousViews} />
                </div>
                <div className="analytics-metric-note">Product activity</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Document Downloads</div>
                <div className="analytics-metric-value">{totalDownloads.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={totalDownloads} previous={previousDownloads} />
                </div>
                <div className="analytics-metric-note">Product resources</div>
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Traffic Sources</h3>
                </div>
                <div className="analytics-stat-list">
                  <div>
                    <span>Organic Search</span>
                    <span>
                      <strong>{currentSources.organic || 0}</strong>{" "}
                      <Change current={currentSources.organic || 0} previous={previousSources.organic || 0} />
                    </span>
                  </div>
                  <div>
                    <span>Direct</span>
                    <span>
                      <strong>{currentSources.direct || 0}</strong>{" "}
                      <Change current={currentSources.direct || 0} previous={previousSources.direct || 0} />
                    </span>
                  </div>
                  <div>
                    <span>Referral</span>
                    <span>
                      <strong>{currentSources.referral || 0}</strong>{" "}
                      <Change current={currentSources.referral || 0} previous={previousSources.referral || 0} />
                    </span>
                  </div>
                  <div>
                    <span>Campaign</span>
                    <span>
                      <strong>{currentSources.campaign || 0}</strong>{" "}
                      <Change current={currentSources.campaign || 0} previous={previousSources.campaign || 0} />
                    </span>
                  </div>
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Device Categories</h3>
                </div>

                <div className="analytics-device-row">
                  <span>
                    <IoDesktopOutline />
                    Desktop
                  </span>

                  <span>
                    <strong>{currentDevices.desktop || 0}</strong>
                    <Change current={currentDevices.desktop || 0} previous={previousDevices.desktop || 0} />
                  </span>
                </div>

                <div className="analytics-device-row">
                  <span>
                    <IoPhonePortraitOutline />
                    Mobile
                  </span>

                  <span>
                    <strong>{currentDevices.mobile || 0}</strong>
                    <Change current={currentDevices.mobile || 0} previous={previousDevices.mobile || 0} />
                  </span>
                </div>

                <div className="analytics-device-row">
                  <span>
                    <IoTabletPortraitOutline />
                    Tablet
                  </span>

                  <span>
                    <strong>{currentDevices.tablet || 0}</strong>
                    <Change current={currentDevices.tablet || 0} previous={previousDevices.tablet || 0} />
                  </span>
                </div>
              </div>
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <h3>Top Products</h3>
                <button
                  type="button"
                  className="analytics-text-button"
                  onClick={() => setActiveAnalyticsTab("products")}
                >
                  View All
                </button>
              </div>
              <div className="analytics-mini-product-list">
                {productTotals.slice(0, 5).map((row) => (
                  <a
                    key={row.productSlug || row.productName}
                    href={`/${row.productSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="analytics-overview-product-row"
                  >
                    <div className="analytics-overview-product-name">
                      {productImageMap[row.productSlug] && <img src={productImageMap[row.productSlug]} alt="" />}

                      <span>{row.productName}</span>
                    </div>

                    <strong>{row.views.toLocaleString()} views</strong>

                    <Change current={row.views} previous={row.previousViews} />
                  </a>
                ))}
                {!loading && productTotals.length === 0 && (
                  <div className="analytics-empty">No product activity in this period.</div>
                )}
              </div>
            </div>

            <div className="analytics-live-panel">
              <div className="analytics-live-header">
                <div>
                  <div className="analytics-live-title-row">
                    <span className="analytics-live-pulse" />

                    <h3>Live View</h3>
                  </div>

                  <p>Visitors with Go!Foton currently open and visible.</p>
                </div>

                <div className="analytics-live-count">
                  <strong>{liveVisitorCount}</strong>

                  <span>
                    visitor
                    {liveVisitorCount === 1 ? "" : "s"} now
                  </span>
                </div>
              </div>

              {liveSessions.length ? (
                <>
                  <div className="analytics-three-column">
                    <div className="analytics-panel">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Pages Open Now</h3>
                          <p>Current page for each live session.</p>
                        </div>
                      </div>

                      <AnalyticsBarChart rows={livePageRows.slice(0, 10)} />
                    </div>

                    <div className="analytics-panel">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Devices</h3>
                          <p>Device mix for visitors online now.</p>
                        </div>
                      </div>

                      <AnalyticsBarChart rows={liveDeviceRows} />
                    </div>

                    <div className="analytics-panel">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Live Geography</h3>
                          <p>Known countries for current visitors.</p>
                        </div>
                      </div>

                      <AnalyticsDonutChart rows={liveCountryRows} centerLabel="live sessions" />
                    </div>
                  </div>

                  <div className="analytics-two-column">
                    <div className="analytics-panel">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Live Visitors</h3>

                          <p>Anonymous session-level activity.</p>
                        </div>
                      </div>

                      <div className="analytics-live-list">
                        {liveSessions.slice(0, 20).map((session) => (
                          <div className="analytics-live-row" key={session.sessionId || session.id}>
                            <div className="analytics-live-row-main">
                              <span className="analytics-live-row-dot" />

                              <div>
                                <strong title={session.lastPage || "/"}>
                                  {formatJourneyPage(getCleanPagePath(session.lastPage || "/"))}
                                </strong>

                                <small>
                                  {session.city
                                    ? `${session.city}${session.regionCode ? `, ${session.regionCode}` : ""}`
                                    : session.countryCode || "Location unknown"}
                                </small>
                              </div>
                            </div>

                            <div className="analytics-live-row-meta">
                              <span>{session.deviceCategory || "Unknown"}</span>

                              <strong>{formatLiveAgo(session)}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="analytics-panel">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Live Cities</h3>

                          <p>Known city-level activity right now.</p>
                        </div>
                      </div>

                      {liveCityRows.length ? (
                        <AnalyticsBarChart rows={liveCityRows.slice(0, 10)} />
                      ) : (
                        <div className="analytics-empty">
                          City data will appear as new live sessions receive geography.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="analytics-live-empty">
                  <span className="analytics-live-pulse" />
                  Nobody is currently active on the site.
                </div>
              )}
            </div>
          </div>
        )}

        {activeAnalyticsTab === "pages" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Page Performance</h3>
                <p>Traffic and engagement across all tracked website pages.</p>
              </div>
            </div>

            <div className="analytics-panel analytics-heatmap-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Page Click Heatmap</h3>

                  <p>
                    Normalized click positions for the selected page group. Product URLs are combined into one Product
                    Pages view.
                  </p>
                </div>

                <div className="analytics-heatmap-controls">
                  <select
                    className="admin-input"

                    value={selectedHeatmapPage}

                    onChange={(event) => setSelectedHeatmapPage(event.target.value)}
                  >
                    {heatmapPageOptions.map((item) => (
                      <option
                        key={item.key}

                        value={item.key}
                      >
                        {item.label} ({item.clicks})
                      </option>
                    ))}
                  </select>

                  <select
                    className="admin-input"

                    value={heatmapDeviceFilter}

                    onChange={(event) => setHeatmapDeviceFilter(event.target.value)}
                  >
                    <option value="all">All Devices</option>

                    <option value="desktop">Desktop</option>

                    <option value="tablet">Tablet</option>

                    <option value="mobile">Mobile</option>
                  </select>
                </div>
              </div>

              <div className="analytics-heatmap-summary">
                <strong>{selectedHeatmapLabel}</strong>

                <span>
                  {selectedHeatmapEvents.length.toLocaleString()} recorded clicks
                  {heatmapDeviceFilter === "all" ? "" : ` on ${heatmapDeviceFilter}`}
                </span>
              </div>

              <div className="analytics-heatmap-layout">
                <AnalyticsHeatmap
                  events={selectedHeatmapEvents}

                  previewPath={selectedHeatmapPreviewPath}
                />

                <div className="analytics-panel analytics-heatmap-targets">
                  <div className="analytics-panel-header">
                    <div>
                      <h3>Most Clicked Elements</h3>

                      <p>Interactive labels recorded with heatmap clicks.</p>
                    </div>
                  </div>

                  {selectedHeatmapTargetRows.length ? (
                    <AnalyticsBarChart rows={selectedHeatmapTargetRows} />
                  ) : (
                    <div className="analytics-empty-chart">
                      No labeled heatmap clicks are available for this selection yet.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="analytics-panel analytics-table-panel">
              <div className="analytics-table-scroll">
                <table className="analytics-table analytics-pages-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Unique Visitors</th>
                      <th>Views</th>
                      <th>Avg. Active Time</th>
                      <th>Avg. Scroll</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagePerformanceRows.map((row) => (
                      <tr key={row.pagePath}>
                        <td className="analytics-pages-page-cell">
                          <button
                            type="button"
                            className="analytics-page-detail-button"
                            title={row.pagePath}
                            onClick={() => setSelectedAnalyticsPage(row.pagePath)}
                          >
                            {formatJourneyPage(row.pagePath)}
                          </button>

                          {row.pageTitle && <small title={row.pageTitle}>{row.pageTitle}</small>}
                        </td>

                        <td>
                          <div className="analytics-productMetric">
                            <strong>{row.uniqueVisitors.toLocaleString()}</strong>

                            <Change current={row.uniqueVisitors} previous={row.previousUniqueVisitors} />
                          </div>
                        </td>

                        <td>
                          <div className="analytics-productMetric">
                            <strong>{row.views.toLocaleString()}</strong>

                            <Change current={row.views} previous={row.previousViews} />
                          </div>
                        </td>

                        <td>
                          <div className="analytics-productMetric">
                            <strong>{row.avgActiveTime ? formatSeconds(row.avgActiveTime) : "0s"}</strong>

                            <Change current={row.avgActiveTime} previous={row.previousAvgActiveTime} />
                          </div>
                        </td>

                        <td>
                          <div className="analytics-productMetric">
                            <strong>{row.avgScrollDepth ? `${row.avgScrollDepth.toFixed(0)}%` : "—"}</strong>

                            {row.avgScrollDepth > 0 && (
                              <Change current={row.avgScrollDepth} previous={row.previousAvgScrollDepth} />
                            )}
                          </div>
                        </td>

                        <td className="analytics-pages-action-cell">
                          <button
                            type="button"
                            className="analytics-pages-view-button"
                            onClick={() => setSelectedAnalyticsPage(row.pagePath)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {!loading && pagePerformanceRows.length === 0 && (
                  <div className="analytics-empty">No page activity in this period.</div>
                )}
              </div>
            </div>

            {selectedAnalyticsPage && selectedPageAnalytics && (
              <div className="analytics-page-modal-backdrop" onClick={() => setSelectedAnalyticsPage(null)}>
                <div className="analytics-page-modal" onClick={(event) => event.stopPropagation()}>
                  <div className="analytics-page-detail-header">
                    <div>
                      <span className="analytics-page-detail-eyebrow">Page Analytics</span>

                      <h3 title={selectedAnalyticsPage}>{formatJourneyPage(selectedAnalyticsPage)}</h3>
                    </div>

                    <div className="analytics-page-detail-actions">
                      <a href={selectedAnalyticsPage} target="_blank" rel="noopener noreferrer">
                        Open Page
                      </a>

                      <button type="button" onClick={() => setSelectedAnalyticsPage(null)}>
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="analytics-page-modal-body">
                    <div className="analytics-page-detail-grid">
                      <div>
                        <span>Views</span>
                        <strong>{selectedPageAnalytics.views.toLocaleString()}</strong>
                      </div>

                      <div>
                        <span>Unique Visitors</span>
                        <strong>{selectedPageAnalytics.uniqueVisitors.toLocaleString()}</strong>
                      </div>

                      <div>
                        <span>Avg Active Time</span>
                        <strong>{formatSeconds(selectedPageAnalytics.avgActiveTime)}</strong>
                      </div>

                      <div>
                        <span>Avg Scroll</span>
                        <strong>
                          {selectedPageAnalytics.avgScroll ? `${selectedPageAnalytics.avgScroll.toFixed(0)}%` : "—"}
                        </strong>
                      </div>

                      <div>
                        <span>Downloads</span>
                        <strong>{selectedPageAnalytics.downloads}</strong>
                      </div>

                      <div>
                        <span>Button Clicks</span>
                        <strong>{selectedPageAnalytics.buttonClicks}</strong>
                      </div>

                      <div>
                        <span>External Clicks</span>
                        <strong>{selectedPageAnalytics.externalClicks}</strong>
                      </div>

                      <div>
                        <span>Email Clicks</span>
                        <strong>{selectedPageAnalytics.emailClicks}</strong>
                      </div>

                      <div>
                        <span>Phone Clicks</span>
                        <strong>{selectedPageAnalytics.phoneClicks}</strong>
                      </div>

                      <div>
                        <span>Form Starts</span>
                        <strong>{selectedPageAnalytics.formStarts}</strong>
                      </div>

                      <div>
                        <span>Form Submissions</span>
                        <strong>{selectedPageAnalytics.formSubmissions}</strong>
                      </div>

                      <div>
                        <span>Pluma Messages</span>
                        <strong>{selectedPageAnalytics.plumaMessages}</strong>
                      </div>

                      <div>
                        <span>Eligible Exits</span>
                        <strong>{selectedPageAnalytics.exits}</strong>
                      </div>

                      <div>
                        <span>Page Exit Rate</span>
                        <strong>{selectedPageAnalytics.exitRate.toFixed(1)}%</strong>
                      </div>
                    </div>

                    <div className="analytics-panel analytics-page-modal-section">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Page Engagement Trend</h3>
                          <p>Views and high-value interactions for this page only.</p>
                        </div>
                      </div>

                      <AnalyticsLineChart
                        rows={selectedPageTrendRows}
                        valueKey="views"
                        secondaryValueKey="interactions"
                        primaryLabel="Views"
                        secondaryLabel="Interactions"
                      />
                    </div>

                    <div className="analytics-two-column">
                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <h3>Scroll Depth</h3>
                        </div>

                        <div className="analytics-progress-list">
                          {selectedPageScrollRows.map((row) => (
                            <div key={row.depth}>
                              <span>{row.depth}%</span>

                              <div>
                                <i
                                  style={{
                                    width: `${row.current}%`,
                                  }}
                                />
                              </div>

                              <span className="analytics-scroll-value">
                                <strong>{row.current.toFixed(0)}%</strong>

                                <Change current={row.current} previous={row.previous} />
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <h3>Interaction Summary</h3>
                        </div>

                        <div className="analytics-stat-list">
                          <div>
                            <span>Button clicks</span>
                            <strong>{selectedPageAnalytics.buttonClicks}</strong>
                          </div>

                          <div>
                            <span>External link clicks</span>
                            <strong>{selectedPageAnalytics.externalClicks}</strong>
                          </div>

                          <div>
                            <span>Email clicks</span>
                            <strong>{selectedPageAnalytics.emailClicks}</strong>
                          </div>

                          <div>
                            <span>Phone clicks</span>
                            <strong>{selectedPageAnalytics.phoneClicks}</strong>
                          </div>

                          <div>
                            <span>Form starts</span>
                            <strong>{selectedPageAnalytics.formStarts}</strong>
                          </div>

                          <div>
                            <span>Form submissions</span>
                            <strong>{selectedPageAnalytics.formSubmissions}</strong>
                          </div>

                          <div>
                            <span>Pluma messages</span>
                            <strong>{selectedPageAnalytics.plumaMessages}</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="analytics-two-column">
                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <div>
                            <h3>Traffic Sources</h3>
                            <p>Page-view source data for this page.</p>
                          </div>
                        </div>

                        <AnalyticsBarChart rows={selectedPageSourceRows} />
                      </div>

                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <div>
                            <h3>Devices</h3>
                            <p>Devices used to view this page.</p>
                          </div>
                        </div>

                        <AnalyticsBarChart rows={selectedPageDeviceRows} />
                      </div>
                    </div>

                    <div className="analytics-three-column analytics-page-modal-section">
                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <div>
                            <h3>Exit Reasons</h3>

                            <p>Best available classification of how eligible sessions ended on this page.</p>
                          </div>
                        </div>

                        {selectedPageExitReasonRows.length ? (
                          <AnalyticsBarChart rows={selectedPageExitReasonRows} />
                        ) : (
                          <div className="analytics-empty-chart">
                            No eligible exits from this page in the selected period.
                          </div>
                        )}
                      </div>

                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <div>
                            <h3>Pages Before Exit</h3>

                            <p>The page viewed immediately before visitors reached this exit page.</p>
                          </div>
                        </div>

                        {selectedPagePreviousPageRows.length ? (
                          <AnalyticsBarChart rows={selectedPagePreviousPageRows} />
                        ) : (
                          <div className="analytics-empty-chart">No previous-page data for this page.</div>
                        )}
                      </div>

                      <div className="analytics-panel">
                        <div className="analytics-panel-header">
                          <div>
                            <h3>Exit Destinations</h3>

                            <p>Phone numbers, emails, forms, or external destinations used before leaving.</p>
                          </div>
                        </div>

                        {selectedPageExitDestinationRows.length ? (
                          <AnalyticsBarChart rows={selectedPageExitDestinationRows} />
                        ) : (
                          <div className="analytics-empty-chart">No tracked destination was recorded.</div>
                        )}
                      </div>
                    </div>

                    <div className="analytics-panel analytics-page-modal-section">
                      <div className="analytics-panel-header">
                        <div>
                          <h3>Top Downloads From This Page</h3>
                          <p>Documents and resources downloaded while visitors were on this page.</p>
                        </div>
                      </div>

                      {selectedPageDownloads.length ? (
                        <div className="analytics-download-grid">
                          {selectedPageDownloads.map((row, index) => (
                            <div className="analytics-download-row" key={row.label}>
                              <span className="analytics-download-rank">{index + 1}</span>

                              <div>
                                <strong>{row.label}</strong>

                                <small>{row.productName || formatJourneyPage(row.pagePath)}</small>
                              </div>

                              <b>{row.downloads.toLocaleString()}</b>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="analytics-empty">No downloads recorded from this page in this period.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeAnalyticsTab === "products" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Product Performance</h3>
                <p>
                  Monthly and period performance by product, including unique visitors, views, downloads, and
                  engagement.
                </p>
              </div>
            </div>
            <div className="analytics-panel analytics-table-panel">
              {loading ? (
                <div className="analytics-empty">Loading product analytics...</div>
              ) : (
                <div className="analytics-table-scroll">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Family</th>
                        <th>Unique Visitors</th>
                        <th>Views</th>
                        <th>Downloads</th>
                        <th>Avg. Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productTotals.map((row) => (
                        <tr key={row.productSlug || row.productName}>
                          <td>
                            <div className="analytics-productNameWrap">
                              {productImageMap[row.productSlug] && (
                                <img src={productImageMap[row.productSlug]} alt="" className="analytics-productThumb" />
                              )}
                              <a href={`/${row.productSlug}`} target="_blank" rel="noopener noreferrer">
                                {row.productName}
                              </a>
                            </div>
                          </td>
                          <td>{row.productFamily || "—"}</td>
                          <td>
                            <div className="analytics-productMetric">
                              <strong>{(currentProductVisitors[row.productSlug] || 0).toLocaleString()}</strong>

                              <Change
                                current={currentProductVisitors[row.productSlug] || 0}
                                previous={previousProductVisitors[row.productSlug] || 0}
                              />
                            </div>
                          </td>
                          <td className="analytics-productViewsCell">
                            <div className="analytics-productMetric">
                              <strong>{row.views.toLocaleString()}</strong>
                              <Change current={row.views} previous={row.previousViews} />
                            </div>
                          </td>
                          <td className="analytics-productDownloadsCell">
                            <div className="analytics-productMetric">
                              <strong>{row.downloads.toLocaleString()}</strong>
                              <Change current={row.downloads} previous={row.previousDownloads} />
                            </div>
                          </td>
                          <td>{row.views ? formatSeconds(row.timeOnPageSeconds / row.views) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!productTotals.length && <div className="analytics-empty">No product activity in this period.</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {activeAnalyticsTab === "tools" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Website Tools</h3>
                <p>Usage and conversion analytics for the Go!Foton Part Number Lookup and configurator.</p>
              </div>
            </div>

            <div className="analytics-metric-grid">
              <div
                className="analytics-metric-card"
                title="Unique visitors who interacted with the Part Number Lookup."
              >
                <div className="analytics-metric-label">Tool Users</div>
                <div className="analytics-metric-value">{currentPartLookupVisitors.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupVisitors} previous={previousPartLookupVisitors} />
                </div>
                <div className="analytics-metric-note">Unique Part Lookup users</div>
              </div>

              <div className="analytics-metric-card" title="Website sessions containing Part Number Lookup activity.">
                <div className="analytics-metric-label">Tool Sessions</div>
                <div className="analytics-metric-value">{currentPartLookupSessions.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupSessions} previous={previousPartLookupSessions} />
                </div>
                <div className="analytics-metric-note">Sessions using the tool</div>
              </div>

              <div className="analytics-metric-card" title="Times the Part Number Lookup interface was opened.">
                <div className="analytics-metric-label">Tool Opens</div>
                <div className="analytics-metric-value">{currentPartLookupOpens.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupOpens} previous={previousPartLookupOpens} />
                </div>
                <div className="analytics-metric-note">Part Lookup opens</div>
              </div>

              <div className="analytics-metric-card" title="Users who began entering or configuring a part number.">
                <div className="analytics-metric-label">Lookups Started</div>
                <div className="analytics-metric-value">{currentPartLookupStarts.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupStarts} previous={previousPartLookupStarts} />
                </div>
                <div className="analytics-metric-note">Actual lookup attempts</div>
              </div>

              <div
                className="analytics-metric-card"
                title="Valid full part numbers successfully resolved by the builder."
              >
                <div className="analytics-metric-label">Valid Completions</div>
                <div className="analytics-metric-value">{currentPartLookupCompletions.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupCompletions} previous={previousPartLookupCompletions} />
                </div>
                <div className="analytics-metric-note">Valid completed configurations</div>
              </div>

              <div
                className="analytics-metric-card"
                title="Percentage of started lookups that reached a valid complete configuration."
              >
                <div className="analytics-metric-label">Completion Rate</div>
                <div className="analytics-metric-value">{currentPartLookupCompletionRate.toFixed(1)}%</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupCompletionRate} previous={previousPartLookupCompletionRate} />
                </div>
                <div className="analytics-metric-note">Completed / started</div>
              </div>

              <div
                className="analytics-metric-card"
                title="Full-length attempts that did not resolve as a valid configuration."
              >
                <div className="analytics-metric-label">Invalid Attempts</div>
                <div className="analytics-metric-value">{currentPartLookupInvalid.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupInvalid} previous={previousPartLookupInvalid} />
                </div>
                <div className="analytics-metric-note">Potential lookup friction</div>
              </div>

              <div
                className="analytics-metric-card"
                title="Valid completions divided by all full valid and invalid attempts."
              >
                <div className="analytics-metric-label">Lookup Success Rate</div>
                <div className="analytics-metric-value">{currentPartLookupSuccessRate.toFixed(1)}%</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupSuccessRate} previous={previousPartLookupSuccessRate} />
                </div>
                <div className="analytics-metric-note">Valid vs full attempts</div>
              </div>

              <div
                className="analytics-metric-card"
                title="Average time from beginning a lookup until reaching a valid configuration."
              >
                <div className="analytics-metric-label">Avg. Time to Valid</div>
                <div className="analytics-metric-value">{formatSeconds(currentAvgPartLookupSeconds)}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentAvgPartLookupSeconds} previous={previousAvgPartLookupSeconds} />
                </div>
                <div className="analytics-metric-note">Lookup completion speed</div>
              </div>

              <div className="analytics-metric-card" title="Times visitors copied a part number from the tool.">
                <div className="analytics-metric-label">Part Numbers Copied</div>
                <div className="analytics-metric-value">{currentPartLookupCopies.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupCopies} previous={previousPartLookupCopies} />
                </div>
                <div className="analytics-metric-note">Strong completion signal</div>
              </div>

              <div className="analytics-metric-card" title="Clicks from the tool to the associated product page.">
                <div className="analytics-metric-label">Product Page Clicks</div>
                <div className="analytics-metric-value">{currentPartLookupProductClicks.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupProductClicks} previous={previousPartLookupProductClicks} />
                </div>
                <div className="analytics-metric-note">Tool to product interest</div>
              </div>

              <div className="analytics-metric-card" title="Datasheet opens from the Part Number Lookup.">
                <div className="analytics-metric-label">Datasheet Clicks</div>
                <div className="analytics-metric-value">{currentPartLookupDatasheetClicks.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPartLookupDatasheetClicks} previous={previousPartLookupDatasheetClicks} />
                </div>
                <div className="analytics-metric-note">Technical document interest</div>
              </div>
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Part Lookup Usage Trend</h3>
                  <p>Started lookups compared with successful valid completions each day.</p>
                </div>
              </div>

              <AnalyticsLineChart
                rows={partLookupTrendRows}
                valueKey="started"
                secondaryValueKey="completed"
                primaryLabel="Started"
                secondaryLabel="Completed"
              />
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Most Configured Products</h3>
                    <p>Product formats producing the most valid part numbers.</p>
                  </div>
                </div>

                <AnalyticsBarChart rows={topPartLookupProducts} />
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Most Completed Part Numbers</h3>
                    <p>Part numbers most frequently completed by visitors.</p>
                  </div>
                </div>

                <AnalyticsBarChart rows={topPartLookupPartNumbers} />
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>How Users Enter the Tool</h3>
                    <p>Manual usage versus product-page or prefilled part-number entry.</p>
                  </div>
                </div>

                <AnalyticsBarChart rows={partLookupEntryModes} />
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Most Used Configuration Fields</h3>
                    <p>Options visitors interact with most while building configurations.</p>
                  </div>
                </div>

                <AnalyticsBarChart rows={partLookupOptionTypes} />
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Tool Devices</h3>
                </div>

                <AnalyticsBarChart rows={partLookupDeviceRows} />
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Traffic Sources Using Part Lookup</h3>
                </div>

                <AnalyticsBarChart rows={partLookupSourceRows} />
              </div>
            </div>

            <div className="analytics-three-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Builder Interaction</h3>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Configuration option selections</span>
                    <strong>{currentPartLookupOptionSelections}</strong>
                  </div>
                  <div>
                    <span>Product format selections</span>
                    <strong>{currentPartLookupSchemaSelections}</strong>
                  </div>
                  <div>
                    <span>Clear / restart clicks</span>
                    <strong>{currentPartLookupClears}</strong>
                  </div>
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Post-Lookup Actions</h3>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Copied part number</span>
                    <strong>{currentPartLookupCopies}</strong>
                  </div>
                  <div>
                    <span>Opened product page</span>
                    <strong>{currentPartLookupProductClicks}</strong>
                  </div>
                  <div>
                    <span>Opened datasheet</span>
                    <strong>{currentPartLookupDatasheetClicks}</strong>
                  </div>
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Lookup Health</h3>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Completion rate</span>
                    <strong>{currentPartLookupCompletionRate.toFixed(1)}%</strong>
                  </div>
                  <div>
                    <span>Valid attempt rate</span>
                    <strong>{currentPartLookupSuccessRate.toFixed(1)}%</strong>
                  </div>
                  <div>
                    <span>Invalid full attempts</span>
                    <strong>{currentPartLookupInvalid}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeAnalyticsTab === "acquisition" && (
          <div className="analytics-tab-content">
            <div className="analytics-metric-grid">
              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Organic Search</div>
                <div className="analytics-metric-value">{(currentSources.organic || 0).toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentSources.organic || 0} previous={previousSources.organic || 0} />
                </div>
                <div className="analytics-metric-note">Search engine sessions</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Direct</div>
                <div className="analytics-metric-value">{(currentSources.direct || 0).toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentSources.direct || 0} previous={previousSources.direct || 0} />
                </div>
                <div className="analytics-metric-note">Direct website sessions</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Referral</div>
                <div className="analytics-metric-value">{(currentSources.referral || 0).toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentSources.referral || 0} previous={previousSources.referral || 0} />
                </div>
                <div className="analytics-metric-note">Other websites</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Campaign Traffic</div>
                <div className="analytics-metric-value">{(currentSources.campaign || 0).toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentSources.campaign || 0} previous={previousSources.campaign || 0} />
                </div>
                <div className="analytics-metric-note">UTM-tagged campaigns</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Known Geography</div>
                <div className="analytics-metric-value">{knownGeographySessions.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <span className="analytics-change neutral">{geographyCoverage.toFixed(1)}% coverage</span>
                </div>
                <div className="analytics-metric-note">Sessions with country data</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Countries</div>
                <div className="analytics-metric-value">{currentCountries.length.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <span className="analytics-change neutral">Known countries only</span>
                </div>
                <div className="analytics-metric-note">Unknown historical locations excluded</div>
              </div>
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Traffic Trend</h3>
                  <p>Website sessions and page views by day for the selected period.</p>
                </div>
              </div>

              <AnalyticsLineChart
                rows={trafficTrendRows}
                valueKey="sessions"
                secondaryValueKey="pageViews"
                primaryLabel="Sessions"
                secondaryLabel="Page Views"
              />
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Where Visitors Came From</h3>
                </div>

                <div className="analytics-stat-list">
                  {currentAcquisitionSources.slice(0, 10).map((row) => (
                    <div key={row.key}>
                      <span>
                        <strong>{row.sourceName}</strong>
                        <small
                          style={{
                            display: "block",
                            color: "#9ca3af",
                            marginTop: 3,
                          }}
                        >
                          {row.sourceType} · {row.share.toFixed(1)}% of sessions
                        </small>
                      </span>

                      <span>
                        <strong>{row.sessions.toLocaleString()}</strong>{" "}
                        <Change current={row.sessions} previous={previousAcquisitionSourceMap[row.key] || 0} />
                      </span>
                    </div>
                  ))}

                  {!currentAcquisitionSources.length && <div className="analytics-empty">No acquisition data yet.</div>}
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Landing Pages</h3>
                </div>

                <div className="analytics-stat-list">
                  {currentLandingPages.slice(0, 10).map((row) => (
                    <div key={row.pagePath}>
                      <span>
                        <button
                          type="button"
                          className="analytics-inline-page-button"
                          onClick={() => {
                            setSelectedAnalyticsPage(row.pagePath);
                            setActiveAnalyticsTab("pages");
                          }}
                        >
                          {formatJourneyPage(row.pagePath)}
                        </button>

                        <small
                          style={{
                            display: "block",
                            color: "#9ca3af",
                            marginTop: 3,
                          }}
                        >
                          Top source: {row.topSource}
                        </small>
                      </span>

                      <span>
                        <strong>{row.sessions.toLocaleString()}</strong>{" "}
                        <Change current={row.sessions} previous={previousLandingPageMap[row.pagePath] || 0} />
                      </span>
                    </div>
                  ))}

                  {!currentLandingPages.length && <div className="analytics-empty">No landing-page data yet.</div>}
                </div>
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Visitors by Country</h3>
                    <p>Known geographic sessions only. Historical unknown sessions are excluded.</p>
                  </div>
                </div>

                <AnalyticsDonutChart rows={currentCountries} centerLabel="known sessions" showCountryFlags />
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Visitors by City</h3>
                    <p>City data from sessions where geographic identity is available.</p>
                  </div>
                </div>

                <AnalyticsDonutChart rows={currentCities} centerLabel="known sessions" />
              </div>
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Visitor Browser Languages</h3>

                  <p>
                    English browser sessions: {englishLanguageSessions.toLocaleString()}. The bars below compare only
                    non-English browser preferences; browser language is not visitor location.
                  </p>
                </div>
              </div>

              {nonEnglishLanguageRows.length ? (
                <AnalyticsBarChart rows={nonEnglishLanguageRows.slice(0, 15)} />
              ) : (
                <div className="analytics-empty-chart">
                  No non-English browser-language sessions were recorded in this period.
                </div>
              )}
            </div>
          </div>
        )}

        {activeAnalyticsTab === "engagement" && (
          <div className="analytics-tab-content">
            <div className="analytics-metric-grid">
              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Avg. Active Time</div>
                <div className="analytics-metric-value">{formatSeconds(currentAvgActiveTime)}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentAvgActiveTime} previous={previousAvgActiveTime} />
                </div>
                <div className="analytics-metric-note">Active time per session</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Pages / Session</div>
                <div className="analytics-metric-value">{currentPagesPerSession.toFixed(1)}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPagesPerSession} previous={previousPagesPerSession} />
                </div>
                <div className="analytics-metric-note">Average pages per visit</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Return Frequency</div>
                <div className="analytics-metric-value">{currentReturnFrequency.toFixed(1)}x</div>
                <div className="analytics-metric-footer">
                  <Change current={currentReturnFrequency} previous={previousReturnFrequency} />
                </div>
                <div className="analytics-metric-note">Sessions per visitor</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Document Downloads</div>
                <div className="analytics-metric-value">
                  {countEvents(currentEvents, "document_download").toLocaleString()}
                </div>
                <div className="analytics-metric-footer">
                  <Change
                    current={countEvents(currentEvents, "document_download")}
                    previous={countEvents(previousEvents, "document_download")}
                  />
                </div>
                <div className="analytics-metric-note">Tracked document/resource downloads</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Phone Clicks</div>
                <div className="analytics-metric-value">{currentPhoneClicks.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPhoneClicks} previous={previousPhoneClicks} />
                </div>
                <div className="analytics-metric-note">Clicks on tracked phone links</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Email Clicks</div>
                <div className="analytics-metric-value">{currentEmailClicks.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentEmailClicks} previous={previousEmailClicks} />
                </div>
                <div className="analytics-metric-note">Clicks on tracked email links</div>
              </div>
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Engagement by Day of Week</h3>

                  <p>High-value website interactions grouped by weekday.</p>
                </div>
              </div>

              <AnalyticsBarChart rows={engagementByWeekdayRows} />
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Scroll Depth</h3>
                </div>

                <div className="analytics-progress-list">
                  {scrollDepthRows.map((row) => (
                    <div key={row.depth}>
                      <span>{row.depth}%</span>

                      <div>
                        <i
                          style={{
                            width: `${row.current}%`,
                          }}
                        />
                      </div>

                      <span className="analytics-scroll-value">
                        <strong>{row.current.toFixed(0)}%</strong>

                        <Change current={row.current} previous={row.previous} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Interactions</h3>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Button clicks</span>
                    <span>
                      <strong>{currentButtonClicks}</strong>{" "}
                      <Change current={currentButtonClicks} previous={previousButtonClicks} />
                    </span>
                  </div>

                  <div>
                    <span>External link clicks</span>
                    <span>
                      <strong>{currentExternalClicks}</strong>{" "}
                      <Change current={currentExternalClicks} previous={previousExternalClicks} />
                    </span>
                  </div>

                  <div>
                    <span>Email clicks</span>
                    <span>
                      <strong>{currentEmailClicks}</strong>{" "}
                      <Change current={currentEmailClicks} previous={previousEmailClicks} />
                    </span>
                  </div>

                  <div>
                    <span>Phone clicks</span>
                    <span>
                      <strong>{currentPhoneClicks}</strong>{" "}
                      <Change current={currentPhoneClicks} previous={previousPhoneClicks} />
                    </span>
                  </div>

                  <div>
                    <span>Form submissions</span>
                    <span>
                      <strong>{currentFormSubmissions}</strong>{" "}
                      <Change current={currentFormSubmissions} previous={previousFormSubmissions} />
                    </span>
                  </div>

                  <div>
                    <span>Copy to clipboard</span>
                    <span>
                      <strong>{currentCopies}</strong> <Change current={currentCopies} previous={previousCopies} />
                    </span>
                  </div>

                  <div>
                    <span>DevTools signals</span>
                    <span>
                      <strong>{currentDevToolsSignals}</strong>{" "}
                      <Change current={currentDevToolsSignals} previous={previousDevToolsSignals} />
                    </span>
                  </div>

                  <div>
                    <span>Tab opens</span>
                    <span>
                      <strong>{currentTabOpens}</strong>{" "}
                      <Change current={currentTabOpens} previous={previousTabOpens} />
                    </span>
                  </div>

                  <div>
                    <span>Form abandonments</span>
                    <span>
                      <strong>{currentFormAbandonments}</strong>{" "}
                      <Change current={currentFormAbandonments} previous={previousFormAbandonments} />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Top Downloads</h3>
                  <p>Most frequently downloaded documents and resources in the selected period.</p>
                </div>
              </div>

              {topDownloadRows.length ? (
                <div className="analytics-download-grid">
                  {topDownloadRows.map((row, index) => (
                    <div className="analytics-download-row" key={row.label}>
                      <span className="analytics-download-rank">{index + 1}</span>

                      <div>
                        <strong>{row.label}</strong>

                        <small>{row.productName || formatJourneyPage(row.pagePath)}</small>
                      </div>

                      <b>{row.downloads.toLocaleString()}</b>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="analytics-empty">No document downloads in this period.</div>
              )}
            </div>
          </div>
        )}
        {activeAnalyticsTab === "exits" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Exit Analytics</h3>
                <p>
                  Analyzes how multi-page visitors ended their sessions, the last page they viewed, and tracked actions
                  that occurred before leaving.
                </p>
                <p>Per the analytics rule, sessions with fewer than 2 page views are excluded from this report.</p>
              </div>
            </div>

            <div className="analytics-metric-grid">
              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Eligible Exits</div>
                <div className="analytics-metric-value">{currentExitRows.length.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentExitRows.length} previous={previousExitRows.length} />
                </div>
                <div className="analytics-metric-note">Sessions with 2+ viewed pages</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Tracked Exit Actions</div>
                <div className="analytics-metric-value">{currentTrackedExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentTrackedExits} previous={previousTrackedExits} />
                </div>
                <div className="analytics-metric-note">Exit associated with a measurable action</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Passive / Unknown Exit</div>
                <div className="analytics-metric-value">{currentPassiveExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPassiveExits} previous={previousPassiveExits} />
                </div>
                <div className="analytics-metric-note">No tracked destination action</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Contact Exits</div>
                <div className="analytics-metric-value">{currentContactExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentContactExits} previous={previousContactExits} />
                </div>
                <div className="analytics-metric-note">Phone, email, or submitted form</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Phone Exits</div>
                <div className="analytics-metric-value">{currentPhoneExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPhoneExits} previous={previousPhoneExits} />
                </div>
                <div className="analytics-metric-note">Last tracked action was phone</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Email Exits</div>
                <div className="analytics-metric-value">{currentEmailExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentEmailExits} previous={previousEmailExits} />
                </div>
                <div className="analytics-metric-note">Last tracked action was email</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Form Conversion Exits</div>
                <div className="analytics-metric-value">{currentFormExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentFormExits} previous={previousFormExits} />
                </div>
                <div className="analytics-metric-note">Session ended after form submission</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">External-Link Exits</div>
                <div className="analytics-metric-value">{currentExternalExits.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentExternalExits} previous={previousExternalExits} />
                </div>
                <div className="analytics-metric-note">Last tracked action opened another site</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Avg Pages Before Exit</div>
                <div className="analytics-metric-value">{currentAvgPagesBeforeExit.toFixed(1)}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentAvgPagesBeforeExit} previous={previousAvgPagesBeforeExit} />
                </div>
                <div className="analytics-metric-note">Eligible multi-page sessions</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Avg Active Time Before Exit</div>
                <div className="analytics-metric-value">{formatSeconds(currentAvgExitActiveTime)}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentAvgExitActiveTime} previous={previousAvgExitActiveTime} />
                </div>
                <div className="analytics-metric-note">Active time in eligible exit sessions</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Returning Visitor Exits</div>
                <div className="analytics-metric-value">{currentReturningExitCount.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentReturningExitCount} previous={previousReturningExitCount} />
                </div>
                <div className="analytics-metric-note">Exits from returning visitors</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Mobile Exits</div>
                <div className="analytics-metric-value">{currentMobileExitCount.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentMobileExitCount} previous={previousMobileExitCount} />
                </div>
                <div className="analytics-metric-note">Eligible sessions on mobile</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Download Before Exit</div>
                <div className="analytics-metric-value">{currentDownloadExitCount.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentDownloadExitCount} previous={previousDownloadExitCount} />
                </div>
                <div className="analytics-metric-note">Sessions that downloaded before leaving</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Pluma Before Exit</div>
                <div className="analytics-metric-value">{currentPlumaExitCount.toLocaleString()}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPlumaExitCount} previous={previousPlumaExitCount} />
                </div>
                <div className="analytics-metric-note">Sessions that used Pluma before leaving</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Tracked Exit Rate</div>
                <div className="analytics-metric-value">
                  {currentExitRows.length
                    ? `${((currentTrackedExits / currentExitRows.length) * 100).toFixed(1)}%`
                    : "0.0%"}
                </div>
                <div className="analytics-metric-footer">
                  <span className="analytics-change neutral">Action identified</span>
                </div>
                <div className="analytics-metric-note">Exits with known final behavior</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Contact Exit Rate</div>
                <div className="analytics-metric-value">
                  {currentExitRows.length
                    ? `${((currentContactExits / currentExitRows.length) * 100).toFixed(1)}%`
                    : "0.0%"}
                </div>
                <div className="analytics-metric-footer">
                  <span className="analytics-change neutral">Positive exit</span>
                </div>
                <div className="analytics-metric-note">Exits following phone, email, or form</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Download Exit Rate</div>
                <div className="analytics-metric-value">
                  {currentExitRows.length
                    ? `${((currentDownloadExitCount / currentExitRows.length) * 100).toFixed(1)}%`
                    : "0.0%"}
                </div>
                <div className="analytics-metric-footer">
                  <span className="analytics-change neutral">Resource interest</span>
                </div>
                <div className="analytics-metric-note">Download occurred before session ended</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Pluma Exit Rate</div>
                <div className="analytics-metric-value">
                  {currentExitRows.length
                    ? `${((currentPlumaExitCount / currentExitRows.length) * 100).toFixed(1)}%`
                    : "0.0%"}
                </div>
                <div className="analytics-metric-footer">
                  <span className="analytics-change neutral">AI-assisted sessions</span>
                </div>
                <div className="analytics-metric-note">Pluma used before session ended</div>
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Why Sessions Ended</h3>

                    <p>Best available classification from tracked activity at the end of the visit.</p>
                  </div>
                </div>

                <AnalyticsBarChart rows={exitReasonRows} />
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Top Exit Pages</h3>

                    <p>The final page viewed before an eligible multi-page session ended.</p>
                  </div>
                </div>

                <AnalyticsBarChart
                  rows={topExitPages.slice(0, 15).map((row) => ({
                    label: formatJourneyPage(row.page),
                    value: row.exits,
                  }))}
                />
              </div>
            </div>
          </div>
        )}

        {activeAnalyticsTab === "funnel" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Website Conversion Funnel</h3>
                <p>
                  Real session-level progression from website visits through product interest, downloads, contact
                  activity, and form submissions.
                </p>
              </div>
            </div>

            <div className="analytics-metric-grid">
              {funnelRows.map((row) => (
                <div className="analytics-metric-card" key={row.label}>
                  <div className="analytics-metric-label">{row.label}</div>

                  <div className="analytics-metric-value">{row.current.toLocaleString()}</div>

                  <div className="analytics-metric-footer">
                    <Change current={row.current} previous={row.previous} />
                  </div>

                  <div className="analytics-metric-note">{row.overallConversion.toFixed(1)}% of website sessions</div>
                </div>
              ))}
            </div>

            <div className="analytics-panel">
              <div className="analytics-panel-header">
                <div>
                  <h3>Conversion Funnel</h3>
                  <p>Width represents the percentage of website sessions reaching each stage.</p>
                </div>
              </div>

              <div className="analytics-funnel">
                {funnelRows.map((row, index) => (
                  <div
                    key={row.label}
                    className="analytics-funnel-stage"
                    style={{
                      width: index === 0 ? "100%" : `${Math.max(36, 82 - index * 14)}%`,
                    }}
                  >
                    <span>{index + 1}</span>

                    <div className="analytics-funnel-stage-copy">
                      <strong>{row.label}</strong>

                      <small>
                        {row.overallConversion.toFixed(1)}% overall
                        {index > 0 ? ` · ${row.conversion.toFixed(1)}% from prior step` : ""}
                      </small>
                    </div>

                    <b>{row.current.toLocaleString()}</b>
                  </div>
                ))}
              </div>
            </div>

            <div className="analytics-two-column analytics-funnel-summary-grid">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Stage Conversion</h3>
                </div>

                <div className="analytics-stat-list analytics-funnel-summary-list">
                  {funnelRows.map((row, index) => (
                    <div key={row.label}>
                      <span>{row.label}</span>

                      <span className="analytics-funnel-summary-value">
                        <strong>{row.current.toLocaleString()}</strong>

                        <small className="analytics-funnel-stat-note">
                          {index === 0 ? "Starting stage" : `${row.conversion.toFixed(1)}% from previous stage`}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <h3>Drop-Off</h3>
                </div>

                <div className="analytics-stat-list analytics-funnel-summary-list">
                  {funnelRows.slice(1).map((row, index) => {
                    const previousStage = funnelRows[index];
                    const lost = Math.max(0, previousStage.current - row.current);
                    const rate = previousStage.current ? (lost / previousStage.current) * 100 : 0;

                    return (
                      <div key={row.label}>
                        <span>
                          {previousStage.label} → {row.label}
                        </span>

                        <span className="analytics-funnel-summary-value">
                          <strong>{lost.toLocaleString()}</strong>

                          <small className="analytics-funnel-stat-note">{rate.toFixed(1)}% drop-off</small>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeAnalyticsTab === "journeys" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Customer Journeys</h3>
                <p>Anonymous visitors and their activity across Go!Foton.com.</p>
              </div>
            </div>

            <div className="analytics-journey-overview-row">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Journey Types</h3>
                    <p>Current visitor classification for the selected analytics period.</p>
                  </div>
                </div>

                {journeyCategoryRows.length ? (
                  <AnalyticsDonutChart rows={journeyCategoryRows} centerLabel="classified visitors" />
                ) : (
                  <div className="analytics-empty-chart">No classified journeys in this period.</div>
                )}
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Journey Summary</h3>
                    <p>Aggregate behavior across all visitors in the selected period.</p>
                  </div>
                </div>

                <div className="analytics-journey-kpi-grid">
                  <div className="analytics-journey-kpi">
                    <span>Visitors</span>
                    <strong>{journeyTotalVisitors.toLocaleString()}</strong>
                    <small>Unique anonymous visitors</small>
                  </div>

                  <div className="analytics-journey-kpi">
                    <span>Returning Visitors</span>
                    <strong>{journeyReturningVisitors.toLocaleString()}</strong>
                    <small>{journeyReturningRate.toFixed(1)}% of visitors</small>
                  </div>

                  <div className="analytics-journey-kpi">
                    <span>Avg. Sessions</span>
                    <strong>{journeyAverageSessions.toFixed(1)}</strong>
                    <small>Sessions per visitor</small>
                  </div>

                  <div className="analytics-journey-kpi">
                    <span>Avg. Pages</span>
                    <strong>{journeyAveragePages.toFixed(1)}</strong>
                    <small>Viewed pages per visitor</small>
                  </div>

                  <div className="analytics-journey-kpi">
                    <span>Avg. Active Time</span>
                    <strong>{formatSeconds(journeyAverageActiveSeconds)}</strong>
                    <small>Active time per visitor</small>
                  </div>

                  <div className="analytics-journey-kpi">
                    <span>Buyer Journeys</span>
                    <strong>{journeyBuyerVisitors.toLocaleString()}</strong>
                    <small>{journeyBuyerRate.toFixed(1)}% of visitors</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-journey-filter-row">
              <span>Filter visitor type</span>

              <div className="analytics-filter-chips">
                {["All", "Explorer", "Technician", "Support", "Job Seeker", "Buyer", "Partner", "DevTools"].map(
                  (category) => (
                    <button
                      key={category}
                      type="button"
                      className={journeyCategoryFilter === category ? "active" : ""}
                      onClick={() => setJourneyCategoryFilter(category)}
                    >
                      {category}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="analytics-panel analytics-table-panel">
              <div className="analytics-table-scroll">
                <table className="analytics-table analytics-journey-table">
                  <thead>
                    <tr>
                      <th>Visitor</th>
                      <th>Type</th>
                      <th>Sessions</th>
                      <th>Pages</th>
                      <th>Source</th>
                      <th>Device</th>
                      <th>Active</th>
                      <th>Idle / Passive</th>
                      <th>Last Seen</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredJourneyVisitors.map((visitor) => (
                      <tr
                        key={visitor.visitorId}
                        className="analytics-journey-row"
                        onClick={() => setSelectedJourneyVisitor(visitor)}
                      >
                        <td>
                          <strong>{formatVisitorId(visitor.visitorId)}</strong>
                        </td>
                        <td>
                          <span className="analytics-change neutral">{visitor.category}</span>
                        </td>
                        <td>{visitor.sessionCount}</td>
                        <td>{visitor.pageCount}</td>
                        <td>{visitor.latestSession?.sourceName || visitor.latestSession?.sourceType || "Direct"}</td>
                        <td>{visitor.latestSession?.deviceCategory || "Unknown"}</td>
                        <td>{formatSeconds(visitor.activeSeconds)}</td>
                        <td>{formatSeconds(visitor.passiveSeconds)}</td>
                        <td>{visitor.lastTimestamp ? new Date(visitor.lastTimestamp).toLocaleString() : "Unknown"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {!loading && journeyVisitors.length === 0 && (
                  <div className="analytics-journey-empty">
                    <div className="analytics-journey-line">
                      <span></span>
                      <i></i>
                      <span></span>
                      <i></i>
                      <span></span>
                    </div>
                    <strong>No customer journeys yet</strong>
                    <p>Visitor journeys will appear after public website tracking begins.</p>
                  </div>
                )}
              </div>
            </div>

            {selectedJourneyVisitor && (
              <div className="analytics-journey-modal-backdrop" onClick={() => setSelectedJourneyVisitor(null)}>
                <div className="analytics-journey-modal" onClick={(event) => event.stopPropagation()}>
                  <div className="analytics-journey-detail-header">
                    <div>
                      <span className="analytics-journey-eyebrow">Anonymous Visitor</span>

                      <h3>{formatVisitorId(selectedJourneyVisitor.visitorId)}</h3>
                    </div>

                    <button type="button" onClick={() => setSelectedJourneyVisitor(null)}>
                      ×
                    </button>
                  </div>

                  <div className="analytics-journey-modal-body">
                    {selectedJourneyVisitor.events?.some((event) => event.type === "pluma_message") && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          paddingTop: 16,
                          marginBottom: 2,
                        }}
                      >
                        <button
                          type="button"
                          className="analytics-pages-view-button"
                          onClick={() => openPlumaFromJourney(selectedJourneyVisitor.visitorId)}
                        >
                          View Pluma Conversation
                        </button>
                      </div>
                    )}

                    <div className="analytics-journey-summary">
                      <div>
                        <span>Type</span>
                        <strong>{selectedJourneyVisitor.category}</strong>
                      </div>

                      <div>
                        <span>Sessions</span>
                        <strong>{selectedJourneyVisitor.sessionCount}</strong>
                      </div>

                      <div>
                        <span>Pages</span>
                        <strong>{selectedJourneyVisitor.pageCount}</strong>
                      </div>

                      <div>
                        <span>Active Time</span>
                        <strong>{formatSeconds(selectedJourneyVisitor.activeSeconds)}</strong>
                      </div>

                      <div>
                        <span>Idle / Passive</span>
                        <strong>{formatSeconds(selectedJourneyVisitor.passiveSeconds)}</strong>
                      </div>

                      <div>
                        <span>Device</span>
                        <strong>{selectedJourneyVisitor.latestSession?.deviceCategory || "Unknown"}</strong>
                      </div>

                      <div>
                        <span>Source</span>
                        <strong>
                          {selectedJourneyVisitor.latestSession?.sourceName ||
                            selectedJourneyVisitor.latestSession?.sourceType ||
                            "Direct"}
                        </strong>
                      </div>

                      <div>
                        <span>Language</span>
                        <strong>{selectedJourneyVisitor.latestSession?.language || "Unknown"}</strong>
                      </div>

                      <div>
                        <span>Timezone</span>
                        <strong>{selectedJourneyVisitor.latestSession?.timezone || "Unknown"}</strong>
                      </div>

                      <div>
                        <span>Country</span>
                        <strong>
                          {selectedJourneyVisitor.latestSession?.countryCode ||
                            selectedJourneyVisitor.latestSession?.country ||
                            "Unknown"}
                        </strong>
                      </div>

                      <div>
                        <span>Region</span>
                        <strong>
                          {selectedJourneyVisitor.latestSession?.regionCode ||
                            selectedJourneyVisitor.latestSession?.region ||
                            "Unknown"}
                        </strong>
                      </div>

                      <div>
                        <span>City</span>
                        <strong>{selectedJourneyVisitor.latestSession?.city || "Unknown"}</strong>
                      </div>
                    </div>

                    <div className="analytics-journey-timeline">
                      <h4>Journey Timeline</h4>

                      {selectedJourneyVisitor.events
                        .filter((event) => !JOURNEY_HIDDEN_EVENT_TYPES.has(event.type))
                        .sort((a, b) => Number(a.clientTimestamp || 0) - Number(b.clientTimestamp || 0))
                        .map((event) => (
                          <div className="analytics-journey-event" key={event.id}>
                            <div className="analytics-journey-event-dot" />

                            <div className="analytics-journey-event-content">
                              <strong>
                                {event.type === "page_view" && `Viewed ${formatJourneyPage(event.pagePath)}`}

                                {event.type === "product_view" &&
                                  `Viewed product: ${event.productName || event.productSlug || "Product"}`}

                                {event.type === "document_download" &&
                                  `Downloaded ${event.resourceLabel || "document"}`}

                                {event.type === "button_click" &&
                                  `Clicked ${event.analyticsLabel || event.analyticsId || "button"}`}

                                {event.type === "external_link_click" && "Opened external link"}

                                {event.type === "email_click" && "Clicked email link"}

                                {event.type === "phone_click" && "Clicked phone number"}

                                {event.type === "copy_to_clipboard" && "Copied website content"}

                                {event.type === "developer_tools_detected" && "Technical browser tools detected"}

                                {event.type === "developer_tools_shortcut" &&
                                  `Technical browser shortcut used${event.shortcut ? `: ${event.shortcut}` : ""}`}

                                {event.type === "tab_open" && `Opened ${event.tabName || "tab"}`}

                                {event.type === "form_start" && `Started ${event.formName || "form"}`}

                                {event.type === "form_abandonment" && `Left ${event.formName || "form"} incomplete`}

                                {event.type === "form_submission" && `Submitted ${event.formName || "form"}`}

                                {event.type === "pluma_open" && "Opened Pluma"}

                                {event.type === "pluma_message" && `Asked Pluma: ${event.userMessage || ""}`}

                                {event.type === "pluma_feedback" &&
                                  `Rated Pluma response: ${event.feedback === "up" ? "Helpful" : "Not Helpful"}`}

                                {event.type === "pluma_product_click" &&
                                  `Opened Pluma recommendation: ${event.productName || event.productSlug || "Product"}`}

                                {![
                                  "page_view",
                                  "product_view",
                                  "document_download",
                                  "button_click",
                                  "external_link_click",
                                  "email_click",
                                  "phone_click",
                                  "copy_to_clipboard",
                                  "developer_tools_detected",
                                  "developer_tools_shortcut",
                                  "tab_open",
                                  "form_start",
                                  "form_abandonment",
                                  "form_submission",
                                  "pluma_open",
                                  "pluma_message",
                                  "pluma_feedback",
                                  "pluma_product_click",
                                ].includes(event.type) && event.type.replaceAll("_", " ")}
                              </strong>

                              <span>{formatAnalyticsDateTime(event.createdAt || event.clientTimestamp)}</span>

                              {event.type === "pluma_message" && event.assistantReply && (
                                <small>Pluma replied: {event.assistantReply}</small>
                              )}

                              {event.pagePath && event.type !== "page_view" && (
                                <small>{formatJourneyPage(event.pagePath)}</small>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeAnalyticsTab === "search" && (
          <div className="analytics-tab-content">
            <div className="analytics-metric-grid">
              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Search Opens</div>

                <div className="analytics-metric-value">{currentSearchOpens.toLocaleString()}</div>

                <div className="analytics-metric-footer">
                  <Change current={currentSearchOpens} previous={previousSearchOpens} />
                </div>

                <div className="analytics-metric-note">Top-bar search opened</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Site Searches</div>
                <div className="analytics-metric-value">{currentSearches}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentSearches} previous={previousSearches} />
                </div>
                <div className="analytics-metric-note">Searches performed on the website</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Zero-Result Searches</div>
                <div className="analytics-metric-value">{currentZeroSearches}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentZeroSearches} previous={previousZeroSearches} />
                </div>
                <div className="analytics-metric-note">Searches that returned no results</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Result Clicks</div>

                <div className="analytics-metric-value">{currentSearchResultClicks.toLocaleString()}</div>

                <div className="analytics-metric-footer">
                  <Change current={currentSearchResultClicks} previous={previousSearchResultClicks} />
                </div>

                <div className="analytics-metric-note">Product results opened from search</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Search CTR</div>

                <div className="analytics-metric-value">{currentSearchCTR.toFixed(1)}%</div>

                <div className="analytics-metric-footer">
                  <Change current={currentSearchCTR} previous={previousSearchCTR} />
                </div>

                <div className="analytics-metric-note">Result clicks / searches</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Open → Search Rate</div>

                <div className="analytics-metric-value">{currentSearchOpenToSearchRate.toFixed(1)}%</div>

                <div className="analytics-metric-footer">
                  <Change current={currentSearchOpenToSearchRate} previous={previousSearchOpenToSearchRate} />
                </div>

                <div className="analytics-metric-note">Search opens that became searches</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">View All Clicks</div>

                <div className="analytics-metric-value">{currentSearchViewAllClicks.toLocaleString()}</div>

                <div className="analytics-metric-footer">
                  <Change current={currentSearchViewAllClicks} previous={previousSearchViewAllClicks} />
                </div>

                <div className="analytics-metric-note">Zero-result users opening Products</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-label">Pluma Messages</div>
                <div className="analytics-metric-value">{currentPlumaMessages}</div>
                <div className="analytics-metric-footer">
                  <Change current={currentPlumaMessages} previous={previousPlumaMessages} />
                </div>
                <div className="analytics-metric-note">Questions submitted to Pluma</div>
              </div>

              <div className="analytics-metric-card">
                <div className="analytics-metric-footer">
                  {currentPlumaFeedbackTotal && previousPlumaFeedbackTotal ? (
                    <Change current={currentPlumaSatisfaction} previous={previousPlumaSatisfaction} />
                  ) : (
                    <span>Thumbs up / down</span>
                  )}
                </div>
                <div className="analytics-metric-note">
                  {currentPlumaThumbsUp} helpful, {currentPlumaThumbsDown} unhelpful
                </div>
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Top Search Terms</h3>
                    <p>Most common website searches and zero-result searches.</p>
                  </div>
                </div>

                {currentSearchTermRows.length ? (
                  <div className="analytics-stat-list">
                    {currentSearchTermRows.map((row) => (
                      <div key={row.term}>
                        <span>
                          {row.term}
                          {row.zeroResults > 0 && (
                            <small style={{ marginLeft: "8px", color: "#d94829" }}>{row.zeroResults} zero-result</small>
                          )}
                        </span>

                        <strong>{row.searches + row.zeroResults}</strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="analytics-empty-chart">No recorded search terms in this date range.</div>
                )}
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>High-Intent Actions</h3>
                    <p>Actions that may indicate stronger customer interest.</p>
                  </div>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Pluma interactions</span>
                    <strong>{currentPlumaMessages}</strong>
                  </div>

                  <div>
                    <span>Email clicks</span>
                    <strong>{currentEmailClicks}</strong>
                  </div>

                  <div>
                    <span>Phone clicks</span>
                    <strong>{currentPhoneClicks}</strong>
                  </div>

                  <div>
                    <span>Form submissions</span>
                    <strong>{currentFormSubmissions}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Most Clicked Search Results</h3>

                    <p>Products visitors selected from the top-bar search.</p>
                  </div>
                </div>

                {currentSearchResultClickRows.length ? (
                  <AnalyticsBarChart rows={currentSearchResultClickRows} />
                ) : (
                  <div className="analytics-empty-chart">No search-result clicks recorded in this date range.</div>
                )}
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Search Conversion</h3>

                    <p>How visitors move from opening search to finding and selecting a product.</p>
                  </div>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Search opens</span>
                    <strong>{currentSearchOpens}</strong>
                  </div>

                  <div>
                    <span>Searches performed</span>
                    <strong>{currentSearches}</strong>
                  </div>

                  <div>
                    <span>Result clicks</span>
                    <strong>{currentSearchResultClicks}</strong>
                  </div>

                  <div>
                    <span>View-all clicks</span>
                    <strong>{currentSearchViewAllClicks}</strong>
                  </div>

                  <div>
                    <span>Search CTR</span>
                    <strong>{currentSearchCTR.toFixed(1)}%</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-two-column">
              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Pluma Feedback</h3>
                    <p>Direct visitor ratings of AI responses.</p>
                  </div>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Helpful responses</span>
                    <strong>{currentPlumaThumbsUp}</strong>
                  </div>

                  <div>
                    <span>Unhelpful responses</span>
                    <strong>{currentPlumaThumbsDown}</strong>
                  </div>

                  <div>
                    <span>Total rated responses</span>
                    <strong>{currentPlumaFeedbackTotal}</strong>
                  </div>

                  <div>
                    <span>Helpful rate</span>
                    <strong>{currentPlumaFeedbackTotal ? `${currentPlumaSatisfaction.toFixed(1)}%` : "—"}</strong>
                  </div>
                </div>
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Search Health</h3>
                    <p>How effectively visitors are finding results.</p>
                  </div>
                </div>

                <div className="analytics-stat-list">
                  <div>
                    <span>Total searches</span>
                    <strong>{currentSearches}</strong>
                  </div>

                  <div>
                    <span>Successful searches</span>
                    <strong>{Math.max(0, currentSearches - currentZeroSearches)}</strong>
                  </div>

                  <div>
                    <span>Zero-result searches</span>
                    <strong>{currentZeroSearches}</strong>
                  </div>

                  <div>
                    <span>Search result clicks</span>
                    <strong>{currentSearchResultClicks}</strong>
                  </div>

                  <div>
                    <span>Search click-through rate</span>
                    <strong>{currentSearchCTR.toFixed(1)}%</strong>
                  </div>

                  <div>
                    <span>Zero-result rate</span>
                    <strong>
                      {currentSearches
                        ? `${Math.min(100, (currentZeroSearches / currentSearches) * 100).toFixed(1)}%`
                        : "0.0%"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeAnalyticsTab === "search" && (
          <div className="analytics-search-pluma-detail">
            <PlumaAnalyticsPanel
              activeRange={activeRange}
              focusVisitorId={plumaFocusVisitorId}
              onFocusConsumed={setPlumaFocusVisitorId}
              onOpenJourney={openJourneyFromPluma}
            />
          </div>
        )}

        {activeAnalyticsTab === "insights" && <AnalyticsInsightsChat activeRange={activeRange} />}

        {activeAnalyticsTab === "alerts" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Recurring Email Reports</h3>
                <p>Automatically deliver analytics summaries to product managers and other website stakeholders.</p>
              </div>
            </div>

            <div className="analytics-alert-layout">
              <div className="analytics-panel analytics-alert-builder">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Add Email Alert</h3>
                    <p>Create a recurring analytics report.</p>
                  </div>
                </div>

                <label className="analytics-field-label">Alert Name</label>
                <input
                  className="admin-input analytics-full-input"
                  value={alertForm.name}
                  onChange={(e) => setAlertForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Optional, e.g. Fiber Panels Monthly Report"
                />

                <div className="analytics-alert-fields">
                  <div>
                    <label className="analytics-field-label">Report Category</label>
                    <select
                      className="admin-input analytics-full-input"
                      value={alertForm.category}
                      onChange={(e) => setAlertForm((prev) => ({ ...prev, category: e.target.value }))}
                    >
                      <option value="productUpdates">Product Updates</option>
                      <option value="websiteOverview">Website Overview</option>
                      <option value="searchPluma">Search & Pluma</option>
                      <option value="funnel">Conversion Funnel</option>
                    </select>
                  </div>

                  <div>
                    <label className="analytics-field-label">Frequency</label>
                    <select
                      className="admin-input analytics-full-input"
                      value={alertForm.frequency}
                      onChange={(e) => setAlertForm((prev) => ({ ...prev, frequency: e.target.value }))}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                </div>

                {alertForm.category === "productUpdates" && (
                  <div className="analytics-alert-product-family">
                    <label className="analytics-field-label">Product Family</label>
                    <select
                      className="admin-input analytics-full-input"
                      value={alertForm.productFamily}
                      onChange={(e) => setAlertForm((prev) => ({ ...prev, productFamily: e.target.value }))}
                    >
                      <option value="">Select product family...</option>
                      {productFamilies.map((family) => (
                        <option key={family} value={family}>
                          {family}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <label className="analytics-field-label">Recipient Email</label>
                <input
                  className="admin-input analytics-full-input"
                  type="email"
                  value={alertForm.email}
                  onChange={(e) => setAlertForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="plm@gofoton.com"
                />

                <button type="button" className="admin-primaryButton" onClick={saveEmailAlert} disabled={savingAlert}>
                  {savingAlert ? "Saving..." : "Add Email Alert"}
                </button>
              </div>

              <div className="analytics-alert-list">
                {emailAlerts.length === 0 && (
                  <div className="analytics-panel analytics-empty">No recurring reports configured yet.</div>
                )}

                {emailAlerts.map((item) => (
                  <div className="analytics-alert-card" key={item.id}>
                    <div className="analytics-alert-card-main">
                      <div className="analytics-alert-icon">✉</div>
                      <div>
                        <strong>{item.name}</strong>
                        <span>{item.category === "productUpdates" ? item.productFamily : item.category}</span>
                        <small>{item.email}</small>
                      </div>
                    </div>

                    <div className="analytics-alert-card-actions">
                      <span className="analytics-alert-frequency">
                        {item.frequency === "monthly" ? "Monthly" : "Quarterly"}
                      </span>
                      <button
                        type="button"
                        className={`analytics-alert-status ${item.active === false ? "inactive" : ""}`}
                        onClick={() => toggleEmailAlert(item)}
                      >
                        {item.active === false ? "Paused" : "Active"}
                      </button>
                      <button
                        type="button"
                        className="analytics-alert-delete"
                        onClick={() => removeEmailAlert(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeAnalyticsTab === "exclusions" && (
          <div className="analytics-tab-content">
            <div className="analytics-section-heading">
              <div>
                <h3>Browser Exclusions</h3>

                <p>Exclude specific staff browsers without relying on a changing public IP address.</p>
              </div>
            </div>

            <div className="analytics-exclusion-status-grid">
              <div className={`analytics-exclusion-status ${currentBrowserExcluded ? "excluded" : "not-excluded"}`}>
                <span>THIS BROWSER</span>

                <strong>{currentBrowserExcluded ? "Excluded from analytics" : "Included in analytics"}</strong>

                <p>
                  {getBrowserName()}
                  {" · ID "}
                  {formatBrowserId(currentAnalyticsBrowserId)}
                </p>

                {currentBrowserExcluded ? (
                  <button
                    type="button"
                    className="admin-navItem"

                    onClick={includeCurrentBrowser}

                    disabled={savingBrowserExclusion}
                  >
                    {savingBrowserExclusion ? "Updating..." : "Include This Browser"}
                  </button>
                ) : (
                  <>
                    <label className="analytics-field-label">Browser Label</label>

                    <input
                      className="admin-input analytics-full-input"

                      value={browserLabel}

                      onChange={(event) => setBrowserLabel(event.target.value)}

                      placeholder={getBrowserName()}
                    />

                    <button
                      type="button"
                      className="admin-primaryButton"

                      onClick={excludeCurrentBrowser}

                      disabled={savingBrowserExclusion}
                    >
                      {savingBrowserExclusion ? "Excluding..." : "Exclude This Browser"}
                    </button>
                  </>
                )}
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel-header">
                  <div>
                    <h3>Excluded Browsers</h3>

                    <p>Open this admin page in another browser and choose “Exclude This Browser” to add it here.</p>
                  </div>
                </div>

                {!browserExclusions.length ? (
                  <div className="analytics-empty">No browsers are currently excluded.</div>
                ) : (
                  <div className="analytics-browser-list">
                    {browserExclusions
                      .slice()
                      .sort((first, second) => String(first.label || "").localeCompare(String(second.label || "")))
                      .map((item) => {
                        const browserId = item.browserId || item.id;

                        const isCurrent = browserId === currentAnalyticsBrowserId;

                        const isRenaming = renamingBrowserId === browserId;

                        return (
                          <div className="analytics-browser-row" key={browserId}>
                            <div className="analytics-browser-row-main">
                              {isRenaming ? (
                                <div className="analytics-browser-rename">
                                  <input
                                    className="admin-input"

                                    value={renamingBrowserLabel}

                                    onChange={(event) => setRenamingBrowserLabel(event.target.value)}

                                    autoFocus
                                  />

                                  <button
                                    type="button"
                                    className="admin-navItem"

                                    onClick={() => saveBrowserRename(item)}
                                  >
                                    Save
                                  </button>

                                  <button
                                    type="button"
                                    className="admin-navItem"

                                    onClick={cancelBrowserRename}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <strong>
                                    {item.label || item.browserName || "Excluded Browser"}

                                    {isCurrent ? " (This Browser)" : ""}
                                  </strong>

                                  <small>
                                    {item.browserName || "Browser"}
                                    {" · ID "}
                                    {formatBrowserId(browserId)}
                                  </small>
                                </>
                              )}
                            </div>

                            {!isRenaming && (
                              <div className="analytics-browser-row-actions">
                                <button
                                  type="button"
                                  className="analytics-text-button"

                                  onClick={() => beginBrowserRename(item)}
                                >
                                  Rename
                                </button>

                                <button
                                  type="button"
                                  className="analytics-alert-delete"

                                  onClick={() => removeBrowserExclusion(item)}
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BlogsTab() {
  const [activeContentTab, setActiveContentTab] = useState("articles");

  const emptyForm = {
    logo: "",
    title: "",
    date: "",
    description: "",
    link: "",
  };

  const [articles, setArticles] = useState([]);
  const [articleForm, setArticleForm] = useState(emptyForm);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);

  const loadArticles = async () => {
    setLoadingArticles(true);

    try {
      const q = query(collection(db, "documentMetadata"));
      const snap = await getDocs(q);

      const rows = snap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((item) => item.type === "article")
        .sort((a, b) => {
          return new Date(b.date || "1900-01-01") - new Date(a.date || "1900-01-01");
        });

      setArticles(rows);
    } catch (err) {
      console.error("Error loading articles:", err);
      alert("Could not load articles.");
    } finally {
      setLoadingArticles(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const resetForm = () => {
    setArticleForm(emptyForm);
    setEditingArticleId(null);
  };

  const handleFormChange = (field, value) => {
    setArticleForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveArticle = async () => {
    const title = articleForm.title.trim();
    const description = articleForm.description.trim();
    const link = articleForm.link.trim();
    const logo = articleForm.logo.trim();
    const date = articleForm.date;

    if (!title) {
      alert("Article title is required.");
      return;
    }

    if (!date) {
      alert("Date is required.");
      return;
    }

    if (!description) {
      alert("Description is required.");
      return;
    }

    if (!link) {
      alert("Article link is required.");
      return;
    }

    if (title.length > 150) {
      alert("Article title must be 150 characters or less.");
      return;
    }

    if (description.length > 150) {
      alert("Description must be 150 characters or less.");
      return;
    }

    setSavingArticle(true);

    try {
      const articleId = editingArticleId || `article-${Date.now()}`;

      await setDoc(
        doc(db, "documentMetadata", articleId),
        {
          type: "article",
          folder: "articles",
          logo,
          title,
          date,
          description,
          link,
          updatedAt: serverTimestamp(),
          createdAt: editingArticleId ? articleForm.createdAt || serverTimestamp() : serverTimestamp(),
        },
        { merge: true }
      );

      resetForm();
      loadArticles();
    } catch (err) {
      console.error("Error saving article:", err);
      alert("Could not save article.");
    } finally {
      setSavingArticle(false);
    }
  };

  const editArticle = (article) => {
    setEditingArticleId(article.id);

    setArticleForm({
      logo: article.logo || "",
      title: article.title || "",
      date: article.date || "",
      description: article.description || "",
      link: article.link || "",
      createdAt: article.createdAt || null,
    });

    setActiveContentTab("articles");
  };

  const removeArticle = async (articleId) => {
    const confirmed = window.confirm("Delete this article?");
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "documentMetadata", articleId));
      loadArticles();

      if (editingArticleId === articleId) {
        resetForm();
      }
    } catch (err) {
      console.error("Error deleting article:", err);
      alert("Could not delete article.");
    }
  };

  return (
    <div className="admin-card">
      <div
        style={{
          display: "flex",
          gap: 10,
          borderBottom: "1px solid #eee",
          marginBottom: 22,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className={`admin-navItem ${activeContentTab === "articles" ? "is-active" : ""}`}
          onClick={() => setActiveContentTab("articles")}
          style={{
            borderRadius: "8px 8px 0 0",
            borderBottom: activeContentTab === "articles" ? "2px solid #D54F28" : "none",
          }}
        >
          Articles
        </button>

        <button
          type="button"
          className={`admin-navItem ${activeContentTab === "blogs" ? "is-active" : ""}`}
          onClick={() => setActiveContentTab("blogs")}
          style={{
            borderRadius: "8px 8px 0 0",
            borderBottom: activeContentTab === "blogs" ? "2px solid #D54F28" : "none",
          }}
        >
          Blogs
        </button>

        <button
          type="button"
          className={`admin-navItem ${activeContentTab === "featuredContent" ? "is-active" : ""}`}
          onClick={() => setActiveContentTab("featuredContent")}
          style={{
            borderRadius: "8px 8px 0 0",
            borderBottom: activeContentTab === "featuredContent" ? "2px solid #D54F28" : "none",
          }}
        >
          Featured Content
        </button>
      </div>

      {activeContentTab === "articles" && (
        <>
          <p className="admin-muted" style={{ marginBottom: 18 }}>
            Add external articles here.
          </p>

          <div
            style={{
              border: "1px solid #eee",
              padding: 16,
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            <h3 style={{ marginTop: 0 }}>{editingArticleId ? "Edit Article" : "Add Article"}</h3>

            <label className="admin-muted">
              Logo URL (Hint: drop this in the document manager under "Stock Images" and grab the link)
            </label>
            <input
              className="admin-input"
              type="text"
              value={articleForm.logo}
              onChange={(e) => handleFormChange("logo", e.target.value)}
              placeholder="/images/newsLogos/Forbes.png or https://..."
              style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="admin-muted">Article Title</label>
            <input
              className="admin-input"
              type="text"
              maxLength={150}
              value={articleForm.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
              placeholder="Article title"
              style={{ width: "100%", marginBottom: 6 }}
            />
            <div className="admin-muted" style={{ fontSize: 12, marginBottom: 12 }}>
              {articleForm.title.length}/150
            </div>

            <label className="admin-muted">Date</label>
            <input
              className="admin-input"
              type="date"
              value={articleForm.date}
              onChange={(e) => handleFormChange("date", e.target.value)}
              style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="admin-muted">Description</label>
            <textarea
              className="admin-input"
              maxLength={150}
              value={articleForm.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              placeholder="Short description"
              rows={4}
              style={{ width: "100%", marginBottom: 6 }}
            />
            <div className="admin-muted" style={{ fontSize: 12, marginBottom: 12 }}>
              {articleForm.description.length}/150
            </div>

            <label className="admin-muted">Article Link</label>
            <input
              className="admin-input"
              type="text"
              value={articleForm.link}
              onChange={(e) => handleFormChange("link", e.target.value)}
              placeholder="https://..."
              style={{ width: "100%", marginBottom: 16 }}
            />

            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="admin-primaryButton" onClick={saveArticle} disabled={savingArticle}>
                {savingArticle ? "Saving..." : editingArticleId ? "Save Changes" : "Add Article"}
              </button>

              {editingArticleId && (
                <button type="button" className="admin-navItem" onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          <h3>Existing Articles</h3>

          {loadingArticles ? (
            <p className="admin-muted">Loading articles...</p>
          ) : articles.length === 0 ? (
            <p className="admin-muted">No articles have been added yet.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                  <th style={{ padding: 10 }}>Logo</th>
                  <th style={{ padding: 10 }}>Title</th>
                  <th style={{ padding: 10 }}>Date</th>
                  <th style={{ padding: 10 }}>Description</th>
                  <th style={{ padding: 10 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {articles.map((article) => (
                  <tr key={article.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: 10 }}>
                      {article.logo ? (
                        <img
                          src={article.logo}
                          alt=""
                          style={{
                            width: 70,
                            maxHeight: 40,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    <td style={{ padding: 10 }}>
                      <strong>{article.title}</strong>
                      <br />
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#0072b1", fontSize: 13 }}
                      >
                        View article
                      </a>
                    </td>

                    <td style={{ padding: 10 }}>{article.date || "—"}</td>

                    <td style={{ padding: 10 }}>{article.description || "—"}</td>

                    <td style={{ padding: 10 }}>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button
                          type="button"
                          onClick={() => editArticle(article)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#333",
                            cursor: "pointer",
                            fontWeight: "bold",
                            padding: 0,
                          }}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => removeArticle(article.id)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#D54F28",
                            cursor: "pointer",
                            fontWeight: "bold",
                            padding: 0,
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {activeContentTab === "blogs" && <BlogManager />}

      {activeContentTab === "featuredContent" && <FeaturedCardsManager />}

      {activeContentTab === "featuredContent" && (
        <div
          style={{
            border: "1px solid #eee",
            padding: 16,
            borderRadius: 8,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Featured Content</h3>
          <p className="admin-muted">This section is intentionally blank for now.</p>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ onLogout, loggingOut }) {
  return (
    <div className="admin-card">
      <h2 className="admin-h2">Settings</h2>
      <p className="admin-muted">Account + admin settings will live here.</p>

      <div style={{ marginTop: 14 }}>
        <button className="admin-logout" onClick={onLogout} disabled={loggingOut}>
          {loggingOut ? "Signing out..." : "Sign out"}
        </button>
      </div>
    </div>
  );
}

async function uploadProductTaxonomyFromExcel(file) {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

  const newSlugs = new Set();
  const productDocs = [];

  for (const row of rows) {
    if (!row["Product (L3)"]) continue;

    const includeFlag = (row["Include Y or N"] || "").trim().toUpperCase();
    if (includeFlag === "N") continue;

    const slug = slugify(row["Product (L3)"] || "");
    newSlugs.add(slug);

    const docData = {
      productFamily: row["Product Family (L1)"],
      subFamily: row["Sub Family (L2)"] || "",
      product: row["Product (L3)"] || "",
      application: splitMulti(row["Application (use case)"] || ""),
      industry: splitMulti(row['Industry \n(refer to "Solution" choices)'] || ""),
      size: row["Size/length/Width/Height"] || "",
      density: row["density/capacity"] || "",
      cableTypes: splitMulti(row["Cable types"] || ""),
      connectorTypes: splitMulti(row["Connector Types"] || ""),
      color: splitMulti(row["Color"] || ""),
      certifications: splitMulti(row["Certifications"] || ""),
      beadBaba: (row["BEAD/BABA (Y/N)"] || "").trim().toUpperCase() === "Y",
      access: row["Trunk Cable Access (front/rear)"] || "",
      environment: row["Environment"] || "",
      integration: row["Optical Component Integration"] || "",
      mounting: row["Mounting Options"] || "",
      portDensity: row["Port/Fiber Density"] || "",
      fiberType: splitMulti(row["Fiber Type (Fiber Mode)"] || ""),
      spacing: row["Spacing"] || "",
      numChannels: row["Number of Channels"] || "",
      fiberCount: splitMulti(row["Fiber Count"] || ""),
      frequentlyUsedWith: splitMulti(row["Frequently Used With"] || ""),
      relatedProducts: splitMulti(row["Related Products"] || ""),
      headline: row["Headline"] || "",
      subhead: row["Subhead"] || "",
      text: row["Text (character limit)"] || "",
      section1Heading: row["Section 1 Heading"] || "",
      section1Description: row["Section 1 Description"] || "",
      section2Heading: row["Section 2 Heading"] || "",
      section2Description: row["Section 2 Description"] || "",
      section3Heading: row["Section 3 Heading"] || "",
      section3Description: row["Section 3 Description"] || "",
      image: row["Image"] || "",
      image2: row["Image2"] || "",
      image3: row["Image3"] || "",
      image4: row["Image4"] || "",
      image5: row["Image5"] || "",
      image6: row["Image6"] || "",
      image7: row["Image7"] || "",
      image8: row["Image8"] || "",
      image9: row["Image9"] || "",
      features: splitMultiSemiColon(row["Features"] || ""),
      features2: splitMultiSemiColon(row["Features2"] || ""),
      features3: splitMultiSemiColon(row["Features3"] || ""),
      featuresHeading: row["FeaturesHeading"] || "",
      features2Heading: row["Features2Heading"] || "",
      features3Heading: row["Features3Heading"] || "",
      category1Heading: row["Category 1 Heading"] || "",
      category1Description: row["Category 1 Description"] || "",
      category2Heading: row["Category 2 Heading"] || "",
      category2Description: row["Category 2 Description"] || "",
      category3Heading: row["Category 3 Heading"] || "",
      category3Description: row["Category 3 Description"] || "",
      category4Heading: row["Category 4 Heading"] || "",
      category4Description: row["Category 4 Description"] || "",
      category5Heading: row["Category 5 Heading"] || "",
      category5Description: row["Category 5 Description"] || "",
      category6Heading: row["Category 6 Heading"] || "",
      category6Description: row["Category 6 Description"] || "",
      category7Heading: row["Category 7 Heading"] || "",
      category7Description: row["Category 7 Description"] || "",
      category8Heading: row["Category 8 Heading"] || "",
      category8Description: row["Category 8 Description"] || "",
      category9Heading: row["Category 9 Heading"] || "",
      category9Description: row["Category 9 Description"] || "",
      category10Heading: row["Category 10 Heading"] || "",
      category10Description: row["Category 10 Description"] || "",
      faq: splitMultiSemiColon(row["FAQs"] || ""),
      dataSheet: row["Data sheet"] || "",
      installationGuide: row["Installation Guide"] || "",
      brochure: row["Brochure"] || "",
      techNote: row["Tech Note"] || "",
      featuredContentVideo: row["FeaturedContentVideo"] || "",
      installationVideo: row["InstallationVideo"] || "",
      installationVideos: row["InstallationVideos"] || "",
      introVideo: row["IntroVideo"] || "",
      productSheet: row["Product Sheet"] || "",
      whitePaper: row["White Paper"] || "",
      quickInstallationGuide: row["Quick Installation Guide"] || "",
      specialVideo1: row["SpecialVideo1"] || "",
      specialVideo1Title: row["SpecialVideo1Title"] || "",
      specialVideo2: row["SpecialVideo2"] || "",
      specialVideo2Title: row["SpecialVideo2Title"] || "",
      industry: splitMulti(row["Industry (refer to Solution choices)"] || ""),
      rank: row["Rank"] || "",
      slug,
    };

    productDocs.push({ slug, docData });
  }

  // Delete old products not in Excel
  const existingSnap = await getDocs(collection(db, "products"));
  for (const docSnap of existingSnap.docs) {
    const existingSlug = docSnap.id;
    if (!newSlugs.has(existingSlug)) {
      await deleteDoc(docSnap.ref);
    }
  }

  // Write new/updated products
  for (const { slug, docData } of productDocs) {
    const docRef = doc(db, "products", slug);
    await setDoc(docRef, docData);
  }
}
